import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, LineChart, Line, Cell, AreaChart, Area, PieChart, Pie } from 'recharts';
import { RiFilePdf2Fill } from 'react-icons/ri';
import { FiChevronLeft, FiCheck, FiBarChart2, FiAlertTriangle, FiHelpCircle, FiUsers, FiX, FiArrowLeft, FiSearch, FiDownload, FiUser, FiAward, FiTrendingUp, FiTarget, FiAlertCircle, FiChevronRight, FiBarChart, FiChevronUp, FiChevronDown, FiActivity, FiBookOpen, FiCheckCircle, FiTrendingDown, FiInfo, FiList, FiMousePointer } from 'react-icons/fi';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { createPortal } from 'react-dom';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { resolveAssessmentImageBaseUrl } from '../assessmentApiConfig';
import assessmentApi from '../assessmentApi';
import { compareAnalyticsQuestions } from './assessmentQuestionUtils';
import { ExportExcelIcon, ExportPdfIcon, getEnovaLogoDataUrl, applyAssessmentPdfBranding, createAssessmentPdfPreview, downloadAssessmentPdfPreview, revokeAssessmentPdfPreview, AssessmentPdfFullscreenViewer, buildAssessmentExportFilename } from '../dashboard/downloadQuestionPaperPdf';
import toast from 'react-hot-toast';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';




const loadScript = (src) => {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve();
    script.onerror = (err) => reject(err);
    document.body.appendChild(script);
  });
};

const getResultAnalysisStatus = (pct, passPercentage = 50) => {
  const score = Number(pct) || 0;
  const pass = Number(passPercentage) || 50;
  if (score >= 75) return 'High';
  if (score >= pass) return 'Average';
  return 'Low';
};

const RESULT_STATUS_BADGE_CLASS = {
  High: 'bg-emerald-50 text-emerald-600 border border-emerald-100',
  Average: 'bg-amber-50 text-amber-600 border border-amber-100',
  Low: 'bg-rose-50 text-rose-600 border border-rose-100',
};

const AnalyticsDashboard = ({ onBack }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [allCandidatesData, setAllCandidatesData] = useState([]);
  const [amChartsLoaded, setAmChartsLoaded] = useState(false);

  // Drilldown states
  const [selectedMetric, setSelectedMetric] = useState('Topper');
  const [selectedRole, setSelectedRole] = useState(null);
  const [activeRoleChart, setActiveRoleChart] = useState(null);
  const [selectedTestFilter, setSelectedTestFilter] = useState(() => {
    return sessionStorage.getItem('analytics_selected_test') || '';
  });
  const [selectedGroup, setSelectedGroup] = useState('All Groups');
  const [overviewFilter, setOverviewFilter] = useState('All');
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [tableSortColumn, setTableSortColumn] = useState('name');
  const [tableSortDirection, setTableSortDirection] = useState('asc');

  // Overall candidate details states
  const [selectedOverallMetric, setSelectedOverallMetric] = useState(null);
  const [overallSearch, setOverallSearch] = useState('');
  const [overallPage, setOverallPage] = useState(1);
  const [showOverallExportModal, setShowOverallExportModal] = useState(false);

  // Role candidate details states
  const [showRoleExportModal, setShowRoleExportModal] = useState(false);
  const [expandedCandidateId, setExpandedCandidateId] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Suppress ResizeObserver loop errors locally when this dashboard is mounted
  useEffect(() => {
    const handleResizeError = (e) => {
      const _suppressedErrors = [
        'ResizeObserver loop completed with undelivered notifications',
        'ResizeObserver loop limit exceeded'
      ];
      if (_suppressedErrors.some(msg => e.message?.includes(msg))) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };

    window.addEventListener('error', handleResizeError);
    return () => {
      window.removeEventListener('error', handleResizeError);
    };
  }, []);

  useEffect(() => {
    if (!selectedRole) {
      setActiveRoleChart(null);
    }
  }, [selectedRole]);

  const [optionTableSortColumn, setOptionTableSortColumn] = useState('name');
  const [optionTableSortDirection, setOptionTableSortDirection] = useState('asc');
  
  const [questionTableSortColumn, setQuestionTableSortColumn] = useState('wrong_count');
  const [questionTableSortDirection, setQuestionTableSortDirection] = useState('desc');
  const [groupTableSortColumn, setGroupTableSortColumn] = useState('group');
  const [groupTableSortDirection, setGroupTableSortDirection] = useState('asc');
  // Question Performance states
  const [showGroupCompare, setShowGroupCompare] = useState(() => {
    return sessionStorage.getItem('analytics_show_group_compare') === 'true';
  });
  const [showQuestionPerf, setShowQuestionPerf] = useState(() => {
    return sessionStorage.getItem('analytics_show_question_perf') === 'true';
  });
  const [selectedCompareAssessments, setSelectedCompareAssessments] = useState([]);
  const [compareAssessmentDropdownOpen, setCompareAssessmentDropdownOpen] = useState(false);
  const [compareAssessmentSearch, setCompareAssessmentSearch] = useState('');
  const compareAssessmentDropdownRef = useRef(null);
  const [selectedCompareGroups, setSelectedCompareGroups] = useState([]);
  const [compareGroupDropdownOpen, setCompareGroupDropdownOpen] = useState(false);
  const [compareGroupSearch, setCompareGroupSearch] = useState('');
  const compareGroupDropdownRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (compareGroupDropdownRef.current && !compareGroupDropdownRef.current.contains(event.target)) {
        setCompareGroupDropdownOpen(false);
      }
      if (compareAssessmentDropdownRef.current && !compareAssessmentDropdownRef.current.contains(event.target)) {
        setCompareAssessmentDropdownOpen(false);
      }
      if (indAssessmentDropdownRef.current && !indAssessmentDropdownRef.current.contains(event.target)) {
        setIndAssessmentDropdownOpen(false);
      }
      if (indGroupDropdownRef.current && !indGroupDropdownRef.current.contains(event.target)) {
        setIndGroupDropdownOpen(false);
      }
      if (individualDropdownRef.current && !individualDropdownRef.current.contains(event.target)) {
        setIndividualDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Persist selected test and active tabs to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('analytics_selected_test', selectedTestFilter);
  }, [selectedTestFilter]);

  useEffect(() => {
    sessionStorage.setItem('analytics_show_question_perf', String(showQuestionPerf));
  }, [showQuestionPerf]);

  useEffect(() => {
    sessionStorage.setItem('analytics_show_group_compare', String(showGroupCompare));
  }, [showGroupCompare]);

  // Helper to accurately extract missed (unanswered) questions for a candidate
  const getCandidateMissedQs = useCallback((c, allQuestionsData) => {
    const attended = (c.overall_pct !== null && c.overall_pct !== undefined) || (c.accuracy !== null && c.accuracy !== undefined);
    // Only use missed_list if it is a non-empty array. An empty missed_list []
    // means the backend couldn't resolve template/pack questions for this submission,
    // so we fall through to the allQuestionsData distribution-based fallback.
    if (c.missed_list && Array.isArray(c.missed_list)) {
      return c.missed_list.filter(q => {
        const ans = String(q.selected_answer || '').trim();
        return ans === 'Unanswered' || ans === '-' || ans === '';
      });
    }
    
    if (!attended) return [];
    
    return allQuestionsData 
      ? allQuestionsData.filter(q => {
          const distSrc = q.distribution || q.options;
          return distSrc?.some(opt => 
            opt.option === 'Unanswered' && 
            opt.user_ids?.some(id => String(id) === String(c.user_id))
          );
        })
      : [];
  }, []);

  // Build available assessments from raw data
  const compareAvailableAssessments = useMemo(() => {
    const tests = new Set();
    allCandidatesData.forEach(c => { if (c.assessment_name) tests.add(c.assessment_name); });
    return Array.from(tests).sort();
  }, [allCandidatesData]);

  // Build groups organized under their assessments (for the multi-select dropdown)
  const compareGroupsByAssessment = useMemo(() => {
    const assessmentMap = {};
    allCandidatesData.forEach(c => {
      const test = c.assessment_name || 'Unknown';
      const group = c.pack_name || 'General';
      if (!assessmentMap[test]) assessmentMap[test] = {};
      if (!assessmentMap[test][group]) assessmentMap[test][group] = { total: 0, count: 0 };
      assessmentMap[test][group].total += (c.overall_pct ?? c.accuracy ?? 0);
      assessmentMap[test][group].count += 1;
    });
    // Convert to structured array
    return Object.entries(assessmentMap).sort(([a], [b]) => a.localeCompare(b)).map(([testName, groups]) => {
      const groupList = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b)).map(([groupName, stats]) => ({
        name: groupName,
        candidateCount: stats.count,
        avgScore: stats.count ? Math.round(stats.total / stats.count) : 0,
        key: `${testName}::${groupName}`,
      }));

      // Calculate overall test average
      const totalTestCandidates = groupList.reduce((sum, g) => sum + g.candidateCount, 0);
      const totalTestScoreSum = Object.values(groups).reduce((sum, g) => sum + g.total, 0);
      const overallAvg = totalTestCandidates ? Math.round(totalTestScoreSum / totalTestCandidates) : 0;

      // Add overall test mean option at the beginning
      /*
      groupList.unshift({
        name: 'Overall Test Mean / Avg',
        candidateCount: totalTestCandidates,
        avgScore: overallAvg,
        key: `${testName}::OVERALL_TEST_MEAN`,
      });
      */

      return {
        assessment: testName,
        groups: groupList,
      };
    });
  }, [allCandidatesData]);

  const comparisonData = useMemo(() => {
    if (selectedCompareGroups.length === 0) return [];
    return selectedCompareGroups.map(groupKey => {
      const [testName, groupName] = groupKey.split('::');
      if (groupName === 'OVERALL_TEST_MEAN') {
        const testCandidates = allCandidatesData.filter(c => (c.assessment_name || 'Unknown') === testName);
        const total = testCandidates.reduce((s, c) => s + (c.overall_pct ?? c.accuracy ?? 0), 0);
        const avg = testCandidates.length ? Math.round(total / testCandidates.length) : 0;
        const passPercentage = testCandidates.length > 0 ? (testCandidates[0].pass_percentage ?? 50) : 50;
        return {
          group: 'Overall Test Mean',
          assessment: testName,
          label: `Overall Mean (${testName})`,
          avgScore: avg,
          candidateCount: testCandidates.length,
          pass_percentage: passPercentage,
        };
      }
      const groupCandidates = allCandidatesData.filter(c =>
        (c.assessment_name || 'Unknown') === testName && (c.pack_name || 'General') === groupName
      );
      const total = groupCandidates.reduce((s, c) => s + (c.overall_pct ?? c.accuracy ?? 0), 0);
      const avg = groupCandidates.length ? Math.round(total / groupCandidates.length) : 0;
      const passPercentage = groupCandidates.length > 0 ? (groupCandidates[0].pass_percentage ?? 50) : 50;
      return {
        group: groupName,
        assessment: testName,
        label: `${groupName} (${testName})`,
        avgScore: avg,
        candidateCount: groupCandidates.length,
        pass_percentage: passPercentage,
      };
    });
  }, [selectedCompareGroups, allCandidatesData]);

  // Individual Compare states and filters
  const [compareMode, setCompareMode] = useState('group'); // 'group' or 'individual'
  const [indTableSortColumn, setIndTableSortColumn] = useState('assessment');
  const [indTableSortDirection, setIndTableSortDirection] = useState('asc');
  const [indSelectedAssessments, setIndSelectedAssessments] = useState([]);
  const [indAssessmentDropdownOpen, setIndAssessmentDropdownOpen] = useState(false);
  const [indAssessmentSearch, setIndAssessmentSearch] = useState('');
  const indAssessmentDropdownRef = useRef(null);

  const [indSelectedGroups, setIndSelectedGroups] = useState([]);
  const [indGroupDropdownOpen, setIndGroupDropdownOpen] = useState(false);
  const [indGroupSearch, setIndGroupSearch] = useState('');
  const indGroupDropdownRef = useRef(null);

  const [selectedIndividual, setSelectedIndividual] = useState(null);
  const [individualDropdownOpen, setIndividualDropdownOpen] = useState(false);
  const [individualSearch, setIndividualSearch] = useState('');
  const individualDropdownRef = useRef(null);

  // Sync individual comparison selected assessments with the global header test filter
  useEffect(() => {
    if (selectedTestFilter) {
      setIndSelectedAssessments([selectedTestFilter]);
    } else {
      setIndSelectedAssessments([]);
    }
    setSelectedIndividual(null);
  }, [selectedTestFilter]);

  // Filter all candidates raw data based on selected assessments and groups under Individual Comparison
  const filteredIndividualCandidatesData = useMemo(() => {
    return allCandidatesData.filter(c => {
      const testName = c.assessment_name || c.test_name;
      if (!testName) return false;
      const groupName = c.pack_name || 'General';
      const groupKey = `${testName}::${groupName}`;

      // Check assessment filter
      const matchesAssessment = indSelectedAssessments.length === 0 || indSelectedAssessments.includes(testName);

      // Check group filter
      const matchesGroup = indSelectedGroups.length === 0 || indSelectedGroups.includes(groupKey);

      return matchesAssessment && matchesGroup;
    });
  }, [allCandidatesData, indSelectedAssessments, indSelectedGroups]);

  // Build a unique candidates list for the dropdown based on filteredIndividualCandidatesData
  const compareUniqueCandidates = useMemo(() => {
    const map = new Map();
    filteredIndividualCandidatesData.forEach(c => {
      const uid = c.user_id || c.emp_id || c.name;
      if (!uid) return;
      if (!map.has(uid)) {
        map.set(uid, {
          user_id: uid,
          name: c.name || 'Unknown',
          emp_id: c.emp_id || '',
          role: c.role || '',
          assessmentCount: 0
        });
      }
      const entry = map.get(uid);
      entry.assessmentCount = (entry.assessmentCount || 0) + 1;
    });
    return Array.from(map.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [filteredIndividualCandidatesData]);

  // Build individual comparison data for the selected candidate (filtered by selected assessments & groups)
  const individualComparisonData = useMemo(() => {
    if (!selectedIndividual) return [];
    const uid = selectedIndividual.user_id || selectedIndividual.emp_id || selectedIndividual.name;
    const records = filteredIndividualCandidatesData
      .filter(c => {
        const cuid = c.user_id || c.emp_id || c.name;
        return String(cuid) === String(uid);
      })
      .map(c => ({
        label: `${c.assessment_name || c.test_name || 'Unknown'} (${c.pack_name || 'General'})`,
        assessment: c.assessment_name || c.test_name || 'Unknown',
        group: c.pack_name || 'General',
        mcq_score: c.mcq_score ?? 0,
        total_marks: c.total_marks ?? 100,
        score: Math.round(c.overall_pct ?? c.accuracy ?? 0),
        pass_percentage: c.pass_percentage ?? 50,
        status: getResultAnalysisStatus(c.overall_pct ?? c.accuracy ?? 0, c.pass_percentage ?? 50)
      }));
    return records;
  }, [selectedIndividual, filteredIndividualCandidatesData]);

  // Reset selected candidate if they no longer match the active filters
  useEffect(() => {
    if (selectedIndividual) {
      const isStillAvailable = compareUniqueCandidates.some(
        c => String(c.user_id) === String(selectedIndividual.user_id)
      );
      if (!isStillAvailable) {
        setSelectedIndividual(null);
      }
    }
  }, [compareUniqueCandidates, selectedIndividual]);

  // Compute top and bottom candidates for individual compare view when no candidate is selected
  const allCandidatesStats = useMemo(() => {
    const candidateMap = {};
    filteredIndividualCandidatesData.forEach(c => {
      const uid = c.user_id || c.emp_id || c.name;
      if (!candidateMap[uid]) {
        candidateMap[uid] = {
          name: c.name || 'Unknown',
          scores: [],
        };
      }
      candidateMap[uid].scores.push(c.overall_pct ?? c.accuracy ?? 0);
    });

    const list = Object.values(candidateMap).map(item => {
      const avg = item.scores.length ? Math.round(item.scores.reduce((s, v) => s + v, 0) / item.scores.length) : 0;
      return {
        name: item.name,
        score: avg,
      };
    });

    return {
      topCandidates: [...list].sort((a, b) => b.score - a.score).slice(0, 5),
      bottomCandidates: [...list].sort((a, b) => a.score - b.score).slice(0, 5),
    };
  }, [filteredIndividualCandidatesData]);

  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);

  // Candidate Missed Questions Modal
  const [showCandidateMissedModal, setShowCandidateMissedModal] = useState(false);
  const [candidateMissedData, setCandidateMissedData] = useState({ name: '', questions: [] });
  const [activeMissedTab, setActiveMissedTab] = useState('MCQ');
  const [showMissedColumn, setShowMissedColumn] = useState(false);

  // Search/Filters
  const [roleQuestionSearch, setRoleQuestionSearch] = useState('');
  const [questionSearch, setQuestionSearch] = useState('');
  const [questionSort, setQuestionSort] = useState('z_to_a');
  const [employeeSearch, setEmployeeSearch] = useState('');

  // Pagination
  const [roleQuestionPage, setRoleQuestionPage] = useState(1);
  const [questionPage, setQuestionPage] = useState(1);
  const [employeePage, setEmployeePage] = useState(1);
  
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [questionItemsPerPage, setQuestionItemsPerPage] = useState(6);
  const [optionEmployeesPerPage, setOptionEmployeesPerPage] = useState(10);

  // Chart Root References
  const overallRootRef = useRef(null);
  const missedRootRef = useRef(null);
  const roleAnalysisRootRef = useRef(null);
  const optionRootRef = useRef(null);
  const individualRootRef = useRef(null);
  
  // Pedagogy State & Refs
  const [selectedPedagogy, setSelectedPedagogy] = useState(null);
  const [selectedQuestionPedagogy, setSelectedQuestionPedagogy] = useState(null);
  const [activeDrilldownTab, setActiveDrilldownTab] = useState('pedagogy');
  const [pedagogySearch, setPedagogySearch] = useState('');
  const [pedagogyPage, setPedagogyPage] = useState(1);
  const [pedagogyItemsPerPage, setPedagogyItemsPerPage] = useState(5);
  const [pedagogyTableSortColumn, setPedagogyTableSortColumn] = useState('score');
  const [pedagogyTableSortDirection, setPedagogyTableSortDirection] = useState('desc');
  const pedagogyRootRef = useRef(null);

  const pedagogyData = useMemo(() => {
    if (!data || !data.candidates_data) return [];
    
    const questionCounts = {};
    if (data.questions_data) {
      data.questions_data.forEach(q => {
        const pedName = q.pedagogy || 'General';
        questionCounts[pedName] = (questionCounts[pedName] || 0) + 1;
      });
    }

    const pedagogyMap = {};
    
    // Initialize map with ALL pedagogies present in the test's questions
    Object.keys(questionCounts).forEach(pedName => {
      pedagogyMap[pedName] = { total: 0, correct: 0, candidates: [] };
    });
    
    // Apply group filter to pedagogy data
    const filteredCandidates = selectedGroup === 'All Groups'
      ? data.candidates_data
      : data.candidates_data.filter(c => c.pack_name && c.pack_name.trim() === selectedGroup);

    filteredCandidates.forEach(c => {
      if (c.pedagogy_stats) {
        Object.entries(c.pedagogy_stats).forEach(([pedName, stats]) => {
          if (!pedagogyMap[pedName]) {
            pedagogyMap[pedName] = { total: 0, correct: 0, candidates: [] };
          }
          pedagogyMap[pedName].total += stats.total || 0;
          pedagogyMap[pedName].correct += stats.correct || 0;
          
          if (stats.total > 0) {
            pedagogyMap[pedName].candidates.push(c);
          }
        });
      }
    });

    const result = Object.entries(pedagogyMap).map(([pedName, stats]) => {
      const score = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;
      return {
        pedagogy: pedName,
        score: score,
        candidateCount: stats.candidates.length,
        candidates: stats.candidates,
        questionCount: questionCounts[pedName] || 0
      };
    });

    return result.sort((a, b) => b.score - a.score);
  }, [data, selectedGroup]);

  // Sync or set default selected pedagogy
  useEffect(() => {
    setSelectedPedagogy(prev => {
      if (!pedagogyData || pedagogyData.length === 0) return null;
      if (prev) {
        const matched = pedagogyData.find(p => p.pedagogy === prev.pedagogy);
        if (matched) return matched;
      }
      return pedagogyData[0];
    });
  }, [pedagogyData]);
  const topCandidatesRootRef = useRef(null);
  const bottomCandidatesRootRef = useRef(null);

  const [isSortOpen, setIsSortOpen] = useState(false);
  
  const sortDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Derive available groups (pack_name) for the group filter dropdown
  const availableGroups = useMemo(() => {
    const rawList = data?.candidates_data || [];
    const groups = new Set();
    rawList.forEach(c => {
      if (c.pack_name && c.pack_name.trim() !== '') {
        groups.add(c.pack_name.trim());
      }
    });
    return Array.from(groups).sort();
  }, [data?.candidates_data]);

  const uniqueCandidatesList = useMemo(() => {
    const rawList = data?.candidates_data || [];
    // Apply group filter
    const groupFiltered = selectedGroup === 'All Groups'
      ? rawList
      : rawList.filter(c => c.pack_name && c.pack_name.trim() === selectedGroup);
    const map = new Map();
    groupFiltered.forEach(c => {
      const key = (c.user_id || c.name) + '_' + (c.pack_name || ''); // fallback to name if user_id is missing, group-aware deduplication
      if (!key) return;
      if (!map.has(key)) {
        map.set(key, c);
      } else {
        const existing = map.get(key);
        const currPct = c.overall_pct || 0;
        const existPct = existing.overall_pct || 0;
        if (currPct > existPct) {
          map.set(key, c);
        }
      }
    });
    return Array.from(map.values());
  }, [data?.candidates_data, selectedGroup]);

  const missedChartData = useMemo(() => {
    if (!data || !uniqueCandidatesList) return [];
    const roleMissedQuestions = {};
    uniqueCandidatesList.forEach(c => {
      const r = c.role || 'Unknown';
      if (!roleMissedQuestions[r]) {
        roleMissedQuestions[r] = 0;
      }
      const missedQs = getCandidateMissedQs(c, data?.questions_data);
      roleMissedQuestions[r] += missedQs.length;
    });

    return Object.keys(roleMissedQuestions).map(role => ({
      role: role,
      total_missed: roleMissedQuestions[role],
    })).filter(r => r.total_missed > 0)
      .sort((a, b) => b.total_missed - a.total_missed);
  }, [data, uniqueCandidatesList, getCandidateMissedQs]);

  const hasMissedQuestions = missedChartData.length > 0;

  const computedSelectedRole = useMemo(() => {
    if (!selectedRole || !data || !uniqueCandidatesList) return null;

    const roleCandidates = uniqueCandidatesList.filter(c => c.role === selectedRole.role);

    const missedQuestionsMap = {};
    let totalAttempts = 0;
    let totalCorrectAttempts = 0;

    roleCandidates.forEach(c => {
      const questionsCount = data.questions_data?.length || 0;
      totalAttempts += questionsCount;
      const pct = c.overall_pct ?? c.accuracy ?? 0;
      totalCorrectAttempts += Math.round((pct / 100) * questionsCount);

      const missedQs = getCandidateMissedQs(c, data?.questions_data);
      missedQs.forEach(q => {
        const qId = q.question_id;
        if (!qId) return;
        if (!missedQuestionsMap[qId]) {
          missedQuestionsMap[qId] = {
            question_id: qId,
            question_text: q.question_text || q.question,
            miss_count: 0,
            attempts: 0,
            question_number: q.question_number ?? q.question_id
          };
        }
        missedQuestionsMap[qId].miss_count++;
      });
    });

    const missedQuestionsList = Object.values(missedQuestionsMap).map(mq => {
      const attempts = roleCandidates.length;
      const acc = attempts > 0 ? Math.round(((attempts - mq.miss_count) / attempts) * 100) : 100;
      return {
        ...mq,
        attempts,
        accuracy: acc
      };
    }).sort((a, b) => b.miss_count - a.miss_count);

    const matchedBackendRole = (data?.roles_data || []).find(r => r.role === selectedRole.role);
    const roleAcc = matchedBackendRole ? matchedBackendRole.accuracy : (totalAttempts > 0 ? Math.round((totalCorrectAttempts / totalAttempts) * 100) : 100);

    const totalMissedSum = missedQuestionsList.reduce((sum, mq) => sum + mq.miss_count, 0);

    return {
      role: selectedRole.role,
      total_missed: totalMissedSum,
      total_attempts: totalAttempts,
      accuracy: roleAcc,
      missed_questions: missedQuestionsList
    };
  }, [selectedRole, data, uniqueCandidatesList, getCandidateMissedQs]);

  // Load AM Charts scripts from CDN dynamically
  useEffect(() => {
    let isMounted = true;
    const loadLibraries = async () => {
      try {
        await loadScript('https://cdn.amcharts.com/lib/5/index.js');
        await loadScript('https://cdn.amcharts.com/lib/5/xy.js');
        await loadScript('https://cdn.amcharts.com/lib/5/percent.js');
        await loadScript('https://cdn.amcharts.com/lib/5/themes/Animated.js');
        if (isMounted) {
          setAmChartsLoaded(true);
        }
      } catch (err) {
        console.error('Failed to load amcharts scripts', err);
      }
    };
    loadLibraries();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch API Dashboard Data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await assessmentApi.get('/assessment/analytics/dashboard', {
          params: { test_name: selectedTestFilter }
        });
        if (response.data?.status === 'success') {
          const newData = response.data;

          let activeTestFilter = selectedTestFilter;
          if (isInitialLoad && !selectedTestFilter && newData.all_tests?.length > 0) {
            activeTestFilter = newData.all_tests[0];
            setSelectedTestFilter(activeTestFilter);
          }
          setIsInitialLoad(false);
          setShowMissedColumn(false);
          setSelectedGroup('All Groups');
          setData(newData);

          setSelectedRole(prevRole => {
            if (prevRole && newData.roles_data?.length > 0) {
              const matchedRole = newData.roles_data.find(r => r.role === prevRole.role);
              if (matchedRole) return matchedRole;
            }
            return null;
          });

          // Dynamically update/preserve the selected question based on the new data
          setSelectedQuestion(prevQuestion => {
            if (prevQuestion && newData.questions_data?.length > 0) {
              const matchedQuestion = newData.questions_data.find(q => q.question_id === prevQuestion.question_id);
              if (matchedQuestion) return matchedQuestion;
            }
            return null;
          });
        }
      } catch (err) {
        console.error('Error fetching analytics dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedTestFilter]);

  // Ensure selectedTestFilter always has a valid test selected (allow '' for All Tests)
  useEffect(() => {
    if (data?.all_tests?.length > 0 && selectedTestFilter !== '' && !data.all_tests.includes(selectedTestFilter)) {
      setSelectedTestFilter(data.all_tests[0]);
    }
  }, [data, selectedTestFilter]);

  // Fetch ALL candidates (unfiltered) once for the comparison feature
  useEffect(() => {
    const fetchAllCandidates = async () => {
      try {
        const response = await assessmentApi.get('/assessment/analytics/dashboard', {
          params: { test_name: '' }
        });
        if (response.data?.status === 'success') {
          setAllCandidatesData(response.data.candidates_data || []);
        }
      } catch (err) {
        console.error('Error fetching all candidates for comparison:', err);
      }
    };
    fetchAllCandidates();
  }, []);

  // Set default option to null whenever selectedQuestion changes to show all candidates initially
  useEffect(() => {
    setSelectedOption(null);
  }, [selectedQuestion]);

  // Keep track of previous sort/search/role/performance state to auto-select the first record on change
  const prevSortColumnRef = useRef(questionTableSortColumn);
  const prevSortDirectionRef = useRef(questionTableSortDirection);
  const prevSearchRef = useRef(questionSearch);
  const prevRoleRef = useRef(selectedRole);
  const prevShowQuestionPerfRef = useRef(showQuestionPerf);

  useEffect(() => {
    if (!data || !data.questions_data) return;

    const hasChanged = 
      prevSortColumnRef.current !== questionTableSortColumn ||
      prevSortDirectionRef.current !== questionTableSortDirection ||
      prevSearchRef.current !== questionSearch ||
      prevRoleRef.current !== selectedRole ||
      prevShowQuestionPerfRef.current !== showQuestionPerf;

    if (showQuestionPerf && (hasChanged || !selectedQuestion)) {
      // Re-compute the sorted questions list to find the 1st record
      const sorted = [...data.questions_data]
        .filter(q => q.question_text.toLowerCase().includes(questionSearch.toLowerCase()))
        .map(q => {
          if (!selectedRole || !q.options || !data.users_lookup) return q;
          let roleWrongCount = 0;
          let roleAttempts = 0;
          q.options.forEach(opt => {
            if (opt.user_ids) {
              const roleUsers = opt.user_ids.filter(id => {
                const u = data.users_lookup[id];
                return u && u.role === selectedRole.role;
              });
              const count = roleUsers.length;
              roleAttempts += count;
              if (!opt.is_correct) {
                roleWrongCount += count;
              }
            }
          });
          return {
            ...q,
            wrong_count: roleWrongCount,
            attempts: roleAttempts,
            options: q.options.map(opt => ({
              ...opt,
              user_ids: opt.user_ids ? opt.user_ids.filter(id => data.users_lookup[id]?.role === selectedRole.role) : [],
              count: opt.user_ids ? opt.user_ids.filter(id => data.users_lookup[id]?.role === selectedRole.role).length : 0
            }))
          };
        })
        .filter(q => {
          if (!selectedRole) return true;
          return q.wrong_count > 0;
        })
        .sort((a, b) =>
          compareAnalyticsQuestions(a, b, questionTableSortColumn, questionTableSortDirection)
        );

      if (sorted.length > 0) {
        setSelectedQuestion(sorted[0]);
        setSelectedOption(null);
      } else {
        setSelectedQuestion(null);
        setSelectedOption(null);
      }
    }

    // Update refs
    prevSortColumnRef.current = questionTableSortColumn;
    prevSortDirectionRef.current = questionTableSortDirection;
    prevSearchRef.current = questionSearch;
    prevRoleRef.current = selectedRole;
    prevShowQuestionPerfRef.current = showQuestionPerf;
  }, [
    showQuestionPerf,
    data,
    questionTableSortColumn,
    questionTableSortDirection,
    questionSearch,
    selectedRole,
    selectedQuestion
  ]);

  // Clean up all charts on unmount
  useEffect(() => {
    return () => {
      if (overallRootRef.current) overallRootRef.current.dispose();
      if (missedRootRef.current) missedRootRef.current.dispose();
      if (roleAnalysisRootRef.current) roleAnalysisRootRef.current.dispose();
      if (optionRootRef.current) optionRootRef.current.dispose();
      if (individualRootRef.current) individualRootRef.current.dispose();
      if (topCandidatesRootRef.current) topCandidatesRootRef.current.dispose();
      if (bottomCandidatesRootRef.current) bottomCandidatesRootRef.current.dispose();
    };
  }, []);

  // Render Overall Result Analysis Chart
  useEffect(() => {
    if (overallRootRef.current) {
      overallRootRef.current.dispose();
      overallRootRef.current = null;
    }

    if (!amChartsLoaded || !data || !document.getElementById('overallChartDiv')) return;

    const am5 = window.am5;
    const am5xy = window.am5xy;
    const am5themes_Animated = window.am5themes_Animated;

    const root = am5.Root.new('overallChartDiv');
    overallRootRef.current = root;

    if (root._logo) {
      root._logo.dispose();
    }

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: root.verticalLayout,
        paddingLeft: 10,
        paddingRight: 10,
        paddingTop: 40,
        paddingBottom: 10
      })
    );

    const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
    xRenderer.labels.template.setAll({
      paddingTop: 4,
      paddingBottom: 4
    });

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'category',
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(root, {})
      })
    );

    const yRenderer = am5xy.AxisRendererY.new(root, {});
    yRenderer.labels.template.setAll({
      paddingRight: 6,
      paddingLeft: 6
    });

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        max: 110,
        numberFormat: "#'%'",
        renderer: yRenderer
      })
    );

    const tooltip = am5.Tooltip.new(root, {
      getFillFromSprite: false,
      autoTextColor: false,
      labelText: '{categoryX}\nPercentage: [bold]{value}%[/]\nCandidates: [bold]{count}[/]'
    });

    tooltip.get("background").setAll({
      fill: am5.color(0xffffff),
      fillOpacity: 0.95,
      stroke: am5.color(0xe2e8f0),
      strokeWidth: 1,
      shadowColor: am5.color(0x000000),
      shadowBlur: 10,
      shadowOpacity: 0.08,
      shadowOffsetX: 0,
      shadowOffsetY: 4
    });

    tooltip.label.setAll({
      fill: am5.color(0x000000),
      fontSize: 11
    });

    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Percentage',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'displayValue',
        categoryXField: 'category',
        tooltip: tooltip
      })
    );
    series.bulletsContainer.set("mask", undefined);

    series.columns.template.setAll({
      strokeOpacity: 0,
      cornerRadiusTL: 8,
      cornerRadiusTR: 8,
      tooltipText: '{categoryX}\nPercentage: [bold]{value}%[/]\nCandidates: [bold]{count}[/]',
      tooltip: tooltip
    });

    series.columns.template.adapters.add('fill', (fill, target) => {
      const dataContext = target.dataItem.dataContext;
      if (dataContext && dataContext.color) {
        return am5.color(dataContext.color);
      }
      return fill;
    });

    // Click handler: clicking a bar filters the candidate table
    series.columns.template.events.on('click', function(ev) {
      const dataContext = ev.target.dataItem.dataContext;
      if (!dataContext) return;
      const allowedFilters = ['High', 'Average', 'Low'];
      const newFilter = dataContext.category;
      if (allowedFilters.includes(newFilter)) {
        setOverviewFilter(prev => prev === newFilter ? 'All' : newFilter);
        setSelectedRole(null);
        setShowMissedColumn(false);
        setOverallPage(1);

        setTimeout(() => {
          document.getElementById('candidate-details-section')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    });

    // Highlight active bar by dimming others
    series.columns.template.states.create('dimmed', { opacity: 0.35 });

    series.bullets.push(function() {
      const label = am5.Label.new(root, {
        fill: am5.color(0x0f172a),
        fontSize: 12,
        fontWeight: "800",
        populateText: true,
        centerX: am5.p50,
        centerY: am5.p100,
        dy: -6
      });

      label.adapters.add("text", function(text, target) {
        const dataContext = target.dataItem.dataContext;
        if (dataContext && dataContext.count > 0) {
          return String(dataContext.count);
        }
        return "";
      });

      return am5.Bullet.new(root, {
        locationX: 0.5,
        locationY: 1,
        sprite: label
      });
    });


    const candidatesList = uniqueCandidatesList;

    const topCands = candidatesList.filter(c => (c.overall_pct ?? c.accuracy ?? 0) >= 75);
    const avgCands = candidatesList.filter(c => {
      const pct = c.overall_pct ?? c.accuracy ?? 0;
      const passPercentage = c.pass_percentage ?? 50;
      return pct >= passPercentage && pct < 75;
    });
    const lowCands = candidatesList.filter(c => {
      const pct = c.overall_pct ?? c.accuracy ?? 0;
      const passPercentage = c.pass_percentage ?? 50;
      return pct < passPercentage;
    });

    const calcAvg = (list) => {
      if (list.length === 0) return 0;
      const sum = list.reduce((s, c) => s + (c.overall_pct ?? c.accuracy ?? 0), 0);
      return Math.round(sum / list.length);
    };

    const chartData = [
      { category: 'High', value: calcAvg(topCands), displayValue: (calcAvg(topCands) === 0 && topCands.length > 0) ? 2 : calcAvg(topCands), count: topCands.length, color: '#10B981' },
      { category: 'Average', value: calcAvg(avgCands), displayValue: (calcAvg(avgCands) === 0 && avgCands.length > 0) ? 2 : calcAvg(avgCands), count: avgCands.length, color: '#3B82F6' },
      { category: 'Low', value: calcAvg(lowCands), displayValue: (calcAvg(lowCands) === 0 && lowCands.length > 0) ? 2 : calcAvg(lowCands), count: lowCands.length, color: '#EF4444' }
    ];

    xAxis.data.setAll(chartData);
    series.data.setAll(chartData);

    series.appear(1000);
    chart.appear(1000, 100);
  }, [amChartsLoaded, data, uniqueCandidatesList, showQuestionPerf, showGroupCompare, setSelectedOverallMetric, setSelectedRole, setOverallPage, setOverallSearch, setShowQuestionPerf, setSelectedQuestion]);

  // Render Role Wise Analysis Chart (Donut Chart)
  useEffect(() => {
    if (roleAnalysisRootRef.current) {
      roleAnalysisRootRef.current.dispose();
      roleAnalysisRootRef.current = null;
    }

    if (!amChartsLoaded || !data || !document.getElementById('roleAnalysisChartDiv')) return;

    const candidatesList = uniqueCandidatesList;
    const roleCounts = {};
    candidatesList.forEach(c => {
      let r = c.role || 'Unknown';
      if (r.toLowerCase() === 'overall') {
        r = 'Candidate';
      }
      roleCounts[r] = (roleCounts[r] || 0) + 1;
    });

    const chartData = Object.keys(roleCounts).map(r => ({
      role: r,
      total_candidates: roleCounts[r]
    })).filter(item => item.role.toLowerCase() !== 'overall');

    const isMobile = windowWidth < 640;
    const isTablet = windowWidth >= 640 && windowWidth < 1024;
    const isDesktop = windowWidth >= 1024;

    const am5 = window.am5;
    const am5percent = window.am5percent;
    const am5themes_Animated = window.am5themes_Animated;

    const roleCount = chartData.length;
    const chartDiv = document.getElementById('roleAnalysisChartDiv');
    if (chartDiv) {
      const labelsPerSide = Math.ceil(roleCount / 2);
      if (isMobile) {
        chartDiv.style.height = `${Math.max(280, labelsPerSide * 26)}px`;
      } else if (isTablet) {
        chartDiv.style.height = `${Math.max(340, labelsPerSide * 28)}px`;
      } else {
        chartDiv.style.height = `${Math.max(380, labelsPerSide * 30)}px`;
      }
    }

    const root = am5.Root.new('roleAnalysisChartDiv');
    roleAnalysisRootRef.current = root;

    if (root._logo) {
      root._logo.dispose();
    }

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5percent.PieChart.new(root, {
        layout: root.verticalLayout,
        innerRadius: am5.percent(60),
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 4,
        paddingRight: 4
      })
    );

    let seriesRadius = 75;
    let labelMaxWidth = 115;
    let labelFontSize = 10.5;
    let tickLength = 10;
    let labelRadius = 10;

    if (isMobile) {
      seriesRadius = 60;
      labelMaxWidth = 75;
      labelFontSize = 9;
      tickLength = 6;
      labelRadius = 6;
    } else if (isTablet) {
      seriesRadius = 65;
      labelMaxWidth = 95;
      labelFontSize = 9.5;
      tickLength = 8;
      labelRadius = 8;
    }

    if (roleCount > 10) {
      labelFontSize = Math.max(isMobile ? 8 : 9, labelFontSize - 1);
      labelMaxWidth = Math.max(isMobile ? 65 : 85, labelMaxWidth - 15);
      seriesRadius = Math.max(isMobile ? 55 : 62, seriesRadius - 5);
    }

    const series = chart.series.push(
      am5percent.PieSeries.new(root, {
        name: 'Role Analysis',
        valueField: 'total_candidates',
        categoryField: 'role',
        alignLabels: true,
        radius: am5.percent(seriesRadius)
      })
    );

    // Vibrant palette matching the reference image
    series.set("colors", am5.ColorSet.new(root, {
      colors: [
        am5.color(0x3B82F6), am5.color(0xF59E0B), am5.color(0x10B981),
        am5.color(0xEF4444), am5.color(0x8B5CF6), am5.color(0x06B6D4),
        am5.color(0xF97316), am5.color(0xEC4899), am5.color(0x14B8A6),
        am5.color(0x6366F1), am5.color(0x84CC16), am5.color(0xE11D48),
        am5.color(0xFBBF24), am5.color(0x7C3AED), am5.color(0x0EA5E9),
        am5.color(0x22C55E), am5.color(0xF43F5E), am5.color(0xA855F7)
      ]
    }));

    series.labels.template.setAll({
      text: "{category}: [bold #EF4444]{value}[/]",
      inside: false,
      fontSize: labelFontSize,
      fontWeight: '500',
      fill: am5.color(0x334155),
      maxWidth: labelMaxWidth,
      oversizedBehavior: "wrap",
      paddingTop: 1,
      paddingBottom: 1,
      paddingLeft: 2,
      paddingRight: 2,
      radius: labelRadius
    });

    const totalForPct = chartData.reduce((sum, item) => sum + (item.total_candidates || 0), 0);

    series.ticks.template.setAll({
      stroke: am5.color(0x475569),
      strokeWidth: 1.5,
      visible: true,
      length: tickLength
    });

    const labelContainer = series.children.push(am5.Container.new(root, {
      centerX: am5.p50,
      centerY: am5.p50,
      layout: root.verticalLayout
    }));

    let centerValueFontSize = isMobile ? 22 : 28;
    let centerLabelFontSize = isMobile ? 8 : 10;

    if (roleCount > 10) {
      centerValueFontSize = isMobile ? 18 : 22;
      centerLabelFontSize = isMobile ? 7 : 8;
    }

    labelContainer.children.push(am5.Label.new(root, {
      text: "OVERALL",
      fontSize: centerLabelFontSize,
      fontWeight: "700",
      letterSpacing: 1,
      fill: am5.color(0x94a3b8),
      centerX: am5.p50
    }));

    const totalCandidates = candidatesList.length;

    labelContainer.children.push(am5.Label.new(root, {
      text: String(totalCandidates),
      fontSize: centerValueFontSize,
      fontWeight: "900",
      fill: am5.color(0x1e293b),
      centerX: am5.p50,
      paddingTop: 2,
      paddingBottom: 2
    }));

    labelContainer.children.push(am5.Label.new(root, {
      text: "CANDIDATES",
      fontSize: centerLabelFontSize,
      fontWeight: "700",
      letterSpacing: 1,
      fill: am5.color(0x94a3b8),
      centerX: am5.p50
    }));

    const tooltip = am5.Tooltip.new(root, {
      getFillFromSprite: false,
      autoTextColor: false,
      labelText: "[bold]{category}[/]\nCandidate Count: {value}\nPercentage: {valuePercentTotal.formatNumber('#.#')}%"
    });

    tooltip.get("background").setAll({
      fill: am5.color(0xffffff),
      fillOpacity: 1,
      stroke: am5.color(0xcbd5e1),
      strokeWidth: 1.5,
      shadowColor: am5.color(0x0f172a),
      shadowBlur: 12,
      shadowOpacity: 0.1,
      shadowOffsetX: 0,
      shadowOffsetY: 5
    });

    tooltip.label.setAll({
      fill: am5.color(0x334155),
      fontSize: 11,
      lineHeight: 1.4
    });

    series.slices.template.setAll({
      strokeOpacity: 1,
      stroke: am5.color(0xffffff),
      strokeWidth: 3,
      toggleKey: 'active',
      tooltipText: "[bold]{category}[/]\nCandidate Count: {value}\nPercentage: {valuePercentTotal.formatNumber('#.#')}%",
      tooltip: tooltip
    });

    series.slices.template.events.on('click', (ev) => {
      const dataContext = ev.target.dataItem.dataContext;
      if (!dataContext) return;
      const roleName = dataContext.role;
      setSelectedRole(prev => {
        if (prev && prev.role === roleName) {
          setActiveRoleChart(null);
          return null;
        }
        const matched = (data?.roles_data || []).find(r => r.role === roleName);
        setActiveRoleChart('roles');
        return matched || { role: roleName };
      });
      setShowMissedColumn(false);
      setOverviewFilter('All');
      setOverallPage(1);

      setTimeout(() => {
        document.getElementById('candidate-details-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    });

    series.slices.template.states.create('hover', {
      scale: 1.04,
      shiftRadius: 8
    });

    series.slices.template.states.create('active', {
      shiftRadius: 12
    });

    series.slices.template.events.on('pointerover', (ev) => {
      const targetSlice = ev.target;
      series.slices.each((slice) => {
        if (slice !== targetSlice) {
          slice.animate({
            key: "fillOpacity",
            to: 0.3,
            duration: 250,
            easing: am5.ease.out(am5.ease.cubic)
          });
        } else {
          slice.animate({
            key: "fillOpacity",
            to: 1,
            duration: 250,
            easing: am5.ease.out(am5.ease.cubic)
          });
        }
      });
    });

    series.slices.template.events.on('pointerout', (ev) => {
      series.slices.each((slice) => {
        slice.animate({
          key: "fillOpacity",
          to: 1,
          duration: 250,
          easing: am5.ease.out(am5.ease.cubic)
        });
        slice.animate({
          key: "strokeOpacity",
          to: 1,
          duration: 250,
          easing: am5.ease.out(am5.ease.cubic)
        });
      });
    });


    series.events.on("datavalidated", () => {
      if (selectedRole && activeRoleChart === 'roles') {
        series.slices.each((slice) => {
          if (slice.dataItem && slice.dataItem.dataContext && slice.dataItem.dataContext.role === selectedRole.role) {
            slice.set("active", true);
          }
        });
      }
    });

    series.data.setAll(chartData);

    // Legend removed for outer labels design

    series.appear(1000, 100);
    chart.appear(1000, 100);
  }, [amChartsLoaded, data, uniqueCandidatesList, showQuestionPerf, showGroupCompare, setSelectedRole, setSelectedOverallMetric, setOverallPage, setOverallSearch, setShowQuestionPerf, setSelectedQuestion, activeRoleChart, windowWidth]);

  // Render Missed Questions Chart (Pie Chart)
  useEffect(() => {
    if (missedRootRef.current) {
      missedRootRef.current.dispose();
      missedRootRef.current = null;
    }

    if (!amChartsLoaded || !data || !document.getElementById('missedChartDiv')) return;

    const chartData = missedChartData;

    const isMobile = windowWidth < 640;
    const isTablet = windowWidth >= 640 && windowWidth < 1024;
    const isDesktop = windowWidth >= 1024;

    const am5 = window.am5;
    const am5percent = window.am5percent;
    const am5themes_Animated = window.am5themes_Animated;

    const roleCount = chartData.length;
    const chartDiv = document.getElementById('missedChartDiv');
    if (chartDiv) {
      const labelsPerSide = Math.ceil(roleCount / 2);
      if (isMobile) {
        chartDiv.style.height = `${Math.max(280, labelsPerSide * 26)}px`;
      } else if (isTablet) {
        chartDiv.style.height = `${Math.max(340, labelsPerSide * 28)}px`;
      } else {
        chartDiv.style.height = `${Math.max(380, labelsPerSide * 30)}px`;
      }
    }

    const root = am5.Root.new('missedChartDiv');
    missedRootRef.current = root;

    if (root._logo) {
      root._logo.dispose();
    }

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5percent.PieChart.new(root, {
        layout: root.verticalLayout,
        innerRadius: am5.percent(60),
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 4,
        paddingRight: 4
      })
    );

    let seriesRadius = 75;
    let labelMaxWidth = 115;
    let labelFontSize = 10.5;
    let tickLength = 10;
    let labelRadius = 10;

    if (isMobile) {
      seriesRadius = 60;
      labelMaxWidth = 75;
      labelFontSize = 9;
      tickLength = 6;
      labelRadius = 6;
    } else if (isTablet) {
      seriesRadius = 65;
      labelMaxWidth = 95;
      labelFontSize = 9.5;
      tickLength = 8;
      labelRadius = 8;
    }

    if (roleCount > 10) {
      labelFontSize = Math.max(isMobile ? 8 : 9, labelFontSize - 1);
      labelMaxWidth = Math.max(isMobile ? 65 : 85, labelMaxWidth - 15);
      seriesRadius = Math.max(isMobile ? 55 : 62, seriesRadius - 5);
    }

    const series = chart.series.push(
      am5percent.PieSeries.new(root, {
        name: 'Missed Questions',
        valueField: 'total_missed',
        categoryField: 'role',
        alignLabels: true,
        radius: am5.percent(seriesRadius)
      })
    );

    // Vibrant palette matching the reference image
    series.set("colors", am5.ColorSet.new(root, {
      colors: [
        am5.color(0xEF4444), am5.color(0xF59E0B), am5.color(0x8B5CF6),
        am5.color(0x3B82F6), am5.color(0x10B981), am5.color(0xEC4899),
        am5.color(0xF97316), am5.color(0x06B6D4), am5.color(0x6366F1),
        am5.color(0x14B8A6), am5.color(0x84CC16), am5.color(0xE11D48),
        am5.color(0xFBBF24), am5.color(0x7C3AED), am5.color(0x0EA5E9),
        am5.color(0x22C55E), am5.color(0xF43F5E), am5.color(0xA855F7)
      ]
    }));

    const totalForPct = chartData.reduce((s, d) => s + d.total_missed, 0);

    // Show outer labels and ticks
    series.labels.template.setAll({
      text: "{category}: [bold #EF4444]{value}[/]",
      inside: false,
      fontSize: labelFontSize,
      fontWeight: '500',
      fill: am5.color(0x334155),
      maxWidth: labelMaxWidth,
      oversizedBehavior: "wrap",
      paddingTop: 1,
      paddingBottom: 1,
      paddingLeft: 2,
      paddingRight: 2,
      radius: labelRadius
    });

    series.labels.template.adapters.add("forceHidden", function(forceHidden, target) {
      return false;
    });

    series.ticks.template.setAll({
      stroke: am5.color(0x475569),
      strokeWidth: 1.5,
      visible: true,
      length: tickLength
    });

    series.ticks.template.adapters.add("forceHidden", function(forceHidden, target) {
      return false;
    });

    // Add center labels inside the donut hole
    const labelContainer = series.children.push(am5.Container.new(root, {
      centerX: am5.p50,
      centerY: am5.p50,
      layout: root.verticalLayout
    }));

    const centerValueFontSize = isMobile ? 22 : 28;
    const centerLabelFontSize = isMobile ? 8 : 10;

    labelContainer.children.push(am5.Label.new(root, {
      text: "OVERALL",
      fontSize: centerLabelFontSize,
      fontWeight: "700",
      letterSpacing: 1,
      fill: am5.color(0x94a3b8),
      centerX: am5.p50
    }));

    const totalWrongCount = chartData.reduce((sum, item) => sum + item.total_missed, 0);

    labelContainer.children.push(am5.Label.new(root, {
      text: String(totalWrongCount),
      fontSize: centerValueFontSize,
      fontWeight: "900",
      fill: am5.color(0x1e293b),
      centerX: am5.p50,
      paddingTop: 2,
      paddingBottom: 2
    }));

    labelContainer.children.push(am5.Label.new(root, {
      text: "MISSED",
      fontSize: centerLabelFontSize,
      fontWeight: "700",
      letterSpacing: 1,
      fill: am5.color(0x94a3b8),
      centerX: am5.p50
    }));

    const tooltip = am5.Tooltip.new(root, {
      getFillFromSprite: false,
      autoTextColor: false,
      labelText: "[bold]{category}[/]\nMissed Questions: {value}\nPercentage: {valuePercentTotal.formatNumber('#.#')}%"
    });

    tooltip.get("background").setAll({
      fill: am5.color(0xffffff),
      fillOpacity: 1,
      stroke: am5.color(0xcbd5e1),
      strokeWidth: 1.5,
      shadowColor: am5.color(0x0f172a),
      shadowBlur: 12,
      shadowOpacity: 0.1,
      shadowOffsetX: 0,
      shadowOffsetY: 5
    });

    tooltip.label.setAll({
      fill: am5.color(0x334155),
      fontSize: 11,
      lineHeight: 1.4
    });

    // Color palette
    series.slices.template.setAll({
      strokeOpacity: 1,
      stroke: am5.color(0xffffff),
      strokeWidth: 3,
      cursorOverStyle: 'pointer',
      toggleKey: 'active',
      tooltipText: "[bold]{category}[/]\nMissed Questions: {value}\nPercentage: {valuePercentTotal.formatNumber('#.#')}%",
      tooltip: tooltip
    });

    series.slices.template.states.create('hover', {
      scale: 1.04,
      shiftRadius: 8
    });

    series.slices.template.states.create('active', {
      shiftRadius: 12
    });

    series.slices.template.events.on('pointerover', (ev) => {
      const targetSlice = ev.target;
      series.slices.each((slice) => {
        if (slice !== targetSlice) {
          slice.animate({
            key: "fillOpacity",
            to: 0.3,
            duration: 250,
            easing: am5.ease.out(am5.ease.cubic)
          });
        } else {
          slice.animate({
            key: "fillOpacity",
            to: 1,
            duration: 250,
            easing: am5.ease.out(am5.ease.cubic)
          });
        }
      });
    });

    series.slices.template.events.on('pointerout', (ev) => {
      series.slices.each((slice) => {
        slice.animate({
          key: "fillOpacity",
          to: 1,
          duration: 250,
          easing: am5.ease.out(am5.ease.cubic)
        });
        slice.animate({
          key: "strokeOpacity",
          to: 1,
          duration: 250,
          easing: am5.ease.out(am5.ease.cubic)
        });
      });
    });

    series.slices.template.events.on('click', (ev) => {
      // Toggle active states for single slice selection
      series.slices.each((slice) => {
        if (slice !== ev.target) {
          slice.set("active", false);
        }
      });

      const dataItem = ev.target.dataItem.dataContext;
      const roleName = dataItem.role;
      setSelectedRole(prev => {
        if (prev && prev.role === roleName && activeRoleChart === 'missed') {
          setActiveRoleChart(null);
          return null;
        }
        const matched = (data?.roles_data || []).find(r => r.role === roleName);
        setActiveRoleChart('missed');
        return matched || { role: roleName };
      });
        setOverviewFilter('All');
        setOverallPage(1);
        setOverallSearch('');
        setShowQuestionPerf(false);
        setSelectedQuestion(null);
        setShowMissedColumn(true);
        
        setTimeout(() => {
          document.getElementById('candidate-details-section')?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    });

    series.events.on("datavalidated", () => {
      if (selectedRole && activeRoleChart === 'missed') {
        series.slices.each((slice) => {
          if (slice.dataItem && slice.dataItem.dataContext && slice.dataItem.dataContext.role === selectedRole.role) {
            slice.set("active", true);
          }
        });
      }
    });

    series.data.setAll(chartData);

    // Legend removed for outer labels design

    series.appear(1000, 100);
    chart.appear(1000, 100);
  }, [amChartsLoaded, data, missedChartData, showQuestionPerf, showGroupCompare, setSelectedRole, setSelectedOverallMetric, setOverallPage, setOverallSearch, setShowQuestionPerf, setSelectedQuestion, activeRoleChart, windowWidth]);

  // Sync selectedRole with pie charts active state without re-rendering them
  useEffect(() => {
    if (roleAnalysisRootRef.current) {
        try {
          const chart = roleAnalysisRootRef.current.container.children.getIndex(0);
          if (chart && chart.series.length > 0) {
            const series = chart.series.getIndex(0);
            series.slices.each(slice => {
              const dataContext = slice.dataItem?.dataContext;
              if (dataContext && selectedRole && activeRoleChart === 'roles') {
                slice.set("active", dataContext.role === selectedRole.role);
              } else {
                slice.set("active", false);
              }
            });
          }
        } catch(e) {}
    }

    if (missedRootRef.current) {
        try {
          const chart = missedRootRef.current.container.children.getIndex(0);
          if (chart && chart.series.length > 0) {
            const series = chart.series.getIndex(0);
            series.slices.each(slice => {
              const dataContext = slice.dataItem?.dataContext;
              if (dataContext && selectedRole && activeRoleChart === 'missed') {
                slice.set("active", dataContext.role === selectedRole.role);
              } else {
                slice.set("active", false);
              }
            });
          }
        } catch(e) {}
    }
  }, [selectedRole, activeRoleChart]);


  // Render Option Distribution Chart (Horizontal)
  useEffect(() => {
    if (optionRootRef.current) {
      optionRootRef.current.dispose();
      optionRootRef.current = null;
    }

    if (!amChartsLoaded || !selectedQuestion || !document.getElementById('optionChartDiv')) return;

    const am5 = window.am5;
    const am5xy = window.am5xy;
    const am5themes_Animated = window.am5themes_Animated;

    const root = am5.Root.new('optionChartDiv');
    optionRootRef.current = root;

    if (root._logo) {
      root._logo.dispose();
    }

    root.setThemes([am5themes_Animated.new(root)]);

    const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: root.verticalLayout,
        paddingTop: 10,
        paddingBottom: isMobile ? 30 : 10,
        paddingLeft: isMobile ? 15 : 25,
        paddingRight: 10
      })
    );


      const xRenderer = am5xy.AxisRendererX.new(root, {
        minGridDistance: 15,
        cellStartLocation: 0.1,
        cellEndLocation: 0.9
      });
      xRenderer.labels.template.setAll({
        paddingTop: 6,
        paddingBottom: 6,
        fontSize: isMobile ? 8.5 : 10,
        fontWeight: '700',
        fill: am5.color(0x000000),
        centerX: isMobile ? am5.p100 : am5.p50,
        centerY: isMobile ? am5.p50 : am5.p0,
        rotation: isMobile ? -40 : 0,
        textAlign: isMobile ? 'right' : 'center'
      });

      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          categoryField: 'option',
          renderer: xRenderer
        })
      );

      const yRenderer = am5xy.AxisRendererY.new(root, {});
      yRenderer.labels.template.setAll({
        paddingRight: 6,
        paddingLeft: 6,
        fill: am5.color(0x000000)
      });

      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          min: 0,
          extraMax: 0.15,
          maxPrecision: 0,
          renderer: yRenderer
        })
      );

      const tooltip = am5.Tooltip.new(root, {
        getFillFromSprite: false,
        autoTextColor: false,
        labelText: '[bold]{option}[/]: {text}'
      });

      tooltip.get("background").setAll({
        fill: am5.color(0xffffff),
        fillOpacity: 0.95,
        stroke: am5.color(0xe2e8f0),
        strokeWidth: 1,
        shadowColor: am5.color(0x000000),
        shadowBlur: 10,
        shadowOpacity: 0.08,
        shadowOffsetX: 0,
        shadowOffsetY: 4
      });

      tooltip.label.setAll({
        fill: am5.color(0x000000),
        fontSize: 11,
        wrap: true,
        maxWidth: 260
      });

      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: 'Distribution',
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'displayCount',
          categoryXField: 'option',
          tooltip: tooltip
        })
      );
      series.bulletsContainer.set("mask", undefined);

      series.columns.template.setAll({
        strokeOpacity: 0,
        cornerRadiusTL: 4,
        cornerRadiusTR: 4,
        cursorOverStyle: 'pointer',
        tooltipText: '[bold]{option}[/]: {text}',
        tooltip: tooltip
      });

      series.columns.template.adapters.add('fill', (fill, target) => {
        const dataContext = target.dataItem.dataContext;
        if (dataContext && dataContext.color) {
          return am5.color(dataContext.color);
        }
        return fill;
      });

      let clickTimeout = null;
      series.columns.template.events.on('click', (ev) => {
        const dataItem = ev.target.dataItem.dataContext;
        if (clickTimeout !== null) {
          clearTimeout(clickTimeout);
          clickTimeout = null;
          setSelectedOption(null);
          setEmployeePage(1);
        } else {
          clickTimeout = setTimeout(() => {
            clickTimeout = null;
            setSelectedOption(dataItem);
            setActiveDrilldownTab('option');
            setEmployeePage(1);

            setTimeout(() => {
              const element = document.getElementById('option-drilldown-table-section');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 150);
          }, 250);
        }
      });

      series.bullets.push(function() {
        const label = am5.Label.new(root, {
          text: "{count}",
          fill: am5.color(0x000000),
          fontSize: 13,
          fontWeight: '900',
          centerX: am5.percent(50),
          centerY: am5.percent(100),
          dy: -8,
          populateText: true,
          cursorOverStyle: 'pointer',
          tooltipText: '[bold]{option}[/]: {text}',
          tooltip: tooltip
        });

        label.adapters.add("text", (text, target) => {
          if (target.dataItem && target.dataItem.dataContext && target.dataItem.dataContext.count === 0) {
            return "";
          }
          return text;
        });

        label.events.on('click', (ev) => {
          const dataItem = ev.target.dataItem.dataContext;
          setSelectedOption(prevSelected => {
            if (prevSelected && prevSelected.option === dataItem.option) {
              setEmployeePage(1);
              return null; // Toggle off if clicking the same option
            } else {
              setActiveDrilldownTab('option');
              setEmployeePage(1);
              setTimeout(() => {
                const element = document.getElementById('option-drilldown-table-section');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 50);
              return dataItem; // Select the new option instantly
            }
          });
        });

        return am5.Bullet.new(root, {
          locationX: 0.5,
          locationY: 1,
          sprite: label
        });
      });

      const validCandidateIds = new Set(uniqueCandidatesList.map(c => String(c.user_id)));
      
      const filteredDistribution = selectedQuestion.distribution.map(d => {
        const filteredUserIds = (d.user_ids || []).filter(id => validCandidateIds.has(String(id)));
        return {
          ...d,
          user_ids: filteredUserIds,
          count: filteredUserIds.length
        };
      });

      let maxIncorrectCount = -1;
      let maxIncorrectOption = null;
      let absoluteMaxCount = 0;
      filteredDistribution.forEach(d => {
        if (d.count > absoluteMaxCount) absoluteMaxCount = d.count;
        if (!d.is_correct && d.option !== 'Unanswered' && d.count > maxIncorrectCount) {
          maxIncorrectCount = d.count;
          maxIncorrectOption = d.option;
        }
      });

      const minDisplayThreshold = Math.max(1, absoluteMaxCount * 0.08);

      const chartData = filteredDistribution.map((d) => {
        let color = '#94A3B8';
        if (d.is_correct) {
          color = '#10B981';
        } else if (d.option === 'Unanswered') {
          color = '#3B82F6';
        } else if (maxIncorrectOption && d.option === maxIncorrectOption && d.count > 0) {
          color = '#EF4444';
        }

        let isCorrectLabel = '[#EF4444]● Incorrect Option[/]';
        if (d.is_correct) {
          isCorrectLabel = '[#10B981]● Correct Answer[/]';
        } else if (d.option === 'Unanswered') {
          isCorrectLabel = '[#3B82F6]● Unanswered[/]';
        }

        return {
          option: d.option,
          text: d.text,
          count: d.count,
          displayCount: d.count > 0 && d.count < minDisplayThreshold ? minDisplayThreshold : d.count,
          is_correct: d.is_correct || false,
          is_correct_label: isCorrectLabel,
          user_ids: d.user_ids,
          color: color
        };
      });

      xAxis.data.setAll(chartData);
      series.data.setAll(chartData);

      const maxCount = Math.max(...chartData.map(d => d.count || 0), 0);
      if (maxCount <= 5) {
        yAxis.set("max", 5);
      } else {
        yAxis.set("max", undefined);
      }

      series.appear(1000);
      chart.appear(1000, 100);
  }, [amChartsLoaded, selectedQuestion, showQuestionPerf, showGroupCompare, uniqueCandidatesList]);

  // Render Individual Comparison Chart

  // Render Pedagogy Performance Chart
  useEffect(() => {
    if (pedagogyRootRef.current) {
      pedagogyRootRef.current.dispose();
      pedagogyRootRef.current = null;
    }

    if (!amChartsLoaded || !pedagogyData || pedagogyData.length === 0 || !document.getElementById('pedagogyChartDiv')) return;

    const am5 = window.am5;
    const am5xy = window.am5xy;
    const am5themes_Animated = window.am5themes_Animated;

    const root = am5.Root.new('pedagogyChartDiv');
    pedagogyRootRef.current = root;

    if (root._logo) {
      root._logo.dispose();
    }

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "none",
        wheelY: "none",
        layout: root.verticalLayout,
        paddingTop: 25,
        paddingBottom: 5,
        paddingLeft: 10,
        paddingRight: 10
      })
    );

    const xRenderer = am5xy.AxisRendererX.new(root, {
      minGridDistance: 30
    });
    
    xRenderer.labels.template.setAll({
      fontSize: 11,
      fontWeight: '600',
      fill: am5.color(0x000000),
      paddingTop: 8
    });

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "pedagogy",
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(root, {})
      })
    );

    const yRenderer = am5xy.AxisRendererY.new(root, {
      minGridDistance: 30
    });

    yRenderer.labels.template.setAll({
      fontSize: 10,
      fill: am5.color(0x000000),
      paddingRight: 8
    });

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        max: 115,
        renderer: yRenderer
      })
    );

    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: "Score",
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "score",
        categoryXField: "pedagogy",
        fill: am5.color(0x0284c7), // Sky blue
        stroke: am5.color(0x0284c7)
      })
    );
    series.bulletsContainer.set("mask", undefined);

    series.columns.template.setAll({
      tooltipText: "{categoryX}: {valueY}% ({candidateCount} candidates)",
      tooltipY: 0,
      strokeOpacity: 0,
      cornerRadiusTR: 4,
      cornerRadiusTL: 4,
      width: am5.percent(60),
      maxWidth: 32,
      cursorOverStyle: "pointer"
    });

    series.bullets.push(function() {
      return am5.Bullet.new(root, {
        locationX: 0.5,
        locationY: 1,
        sprite: am5.Label.new(root, {
          text: "{valueY}% ({candidateCount})",
          fill: am5.color(0x0f172a),
          fontSize: 10,
          fontWeight: "800",
          populateText: true,
          centerX: am5.p50,
          centerY: am5.p100,
          dy: -6
        })
      });
    });

    let maxVal = -1;
    let minVal = 101;
    pedagogyData.forEach(d => {
      if (d.score > maxVal) maxVal = d.score;
      if (d.score < minVal) minVal = d.score;
    });

    series.columns.template.adapters.add("fill", function(fill, target) {
      if (target.dataItem) {
        const val = target.dataItem.get("valueY");
        if (val === maxVal) return am5.color(0x10b981); // Green
        if (val === minVal) return am5.color(0xef4444); // Red
      }
      return fill;
    });

    series.columns.template.events.on('click', (ev) => {
      const dataItem = ev.target.dataItem.dataContext;
      setSelectedPedagogy(prevSelected => {
        if (prevSelected && prevSelected.pedagogy === dataItem.pedagogy) {
          setPedagogyPage(1);
          return null; // Toggle off
        } else {
          setActiveDrilldownTab('pedagogy');
          setPedagogyPage(1);
          return dataItem; // Select instantly
        }
      });
    });

    xAxis.data.setAll(pedagogyData);
    series.data.setAll(pedagogyData);

    series.appear(1000);
    chart.appear(1000, 100);
  }, [amChartsLoaded, pedagogyData, showQuestionPerf, showGroupCompare]);

// Render Individual Comparison Chart (Simple Column amCharts)
  useEffect(() => {
    if (individualRootRef.current) {
      individualRootRef.current.dispose();
      individualRootRef.current = null;
    }

    if (!amChartsLoaded || !selectedIndividual || !document.getElementById('individualCompareChartDiv')) return;

    const am5 = window.am5;
    const am5xy = window.am5xy;
    const am5themes_Animated = window.am5themes_Animated;

    const root = am5.Root.new('individualCompareChartDiv');
    individualRootRef.current = root;

    if (root._logo) {
      root._logo.dispose();
    }

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: root.verticalLayout,
        paddingLeft: 10,
        paddingRight: 10,
        paddingTop: 40,
        paddingBottom: 20
      })
    );

    const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
    xRenderer.labels.template.setAll({
      paddingTop: 6,
      paddingBottom: 6,
      fontSize: 10,
      fontWeight: '600',
      fill: am5.color(0x475569)
    });

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'label',
        renderer: xRenderer
      })
    );

    const yRenderer = am5xy.AxisRendererY.new(root, {});
    yRenderer.labels.template.setAll({
      paddingRight: 6,
      paddingLeft: 6
    });

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        max: 110,
        numberFormat: "#'%'",
        renderer: yRenderer
      })
    );

    const tooltip = am5.Tooltip.new(root, {
      getFillFromSprite: false,
      autoTextColor: false,
      labelText: '{label}\nScore: [bold]{valueY}%[/]'
    });

    tooltip.get("background").setAll({
      fill: am5.color(0xffffff),
      fillOpacity: 0.95,
      stroke: am5.color(0xe2e8f0),
      strokeWidth: 1,
      shadowColor: am5.color(0x000000),
      shadowBlur: 10,
      shadowOpacity: 0.08,
      shadowOffsetX: 0,
      shadowOffsetY: 4
    });

    tooltip.label.setAll({
      fill: am5.color(0x000000),
      fontSize: 11
    });

    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Score',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'score',
        categoryXField: 'label',
        tooltip: tooltip
      })
    );
    series.bulletsContainer.set("mask", undefined);

    series.columns.template.setAll({
      strokeOpacity: 0,
      cornerRadiusTL: 6,
      cornerRadiusTR: 6,
      tooltipText: '{label}\nScore: [bold]{valueY}%[/]',
      tooltip: tooltip,
      maxWidth: 40
    });

    series.columns.template.adapters.add('fill', (fill, target) => {
      const dataContext = target.dataItem.dataContext;
      if (dataContext && dataContext.score !== undefined) {
        const score = dataContext.score;
        return score >= 75 ? am5.color(0x10b981) : score >= 50 ? am5.color(0xf59e0b) : am5.color(0xef4444);
      }
      return fill;
    });

    series.bullets.push(function() {
      return am5.Bullet.new(root, {
        locationX: 0.5,
        locationY: 1,
        sprite: am5.Label.new(root, {
          text: "{valueY}%",
          fill: am5.color(0x0f172a),
          fontSize: 11,
          fontWeight: "800",
          populateText: true,
          centerX: am5.p50,
          centerY: am5.p100,
          dy: -6
        })
      });
    });

    xAxis.data.setAll(individualComparisonData);
    series.data.setAll(individualComparisonData);

    series.appear(1000);
    chart.appear(1000, 100);
  }, [amChartsLoaded, selectedIndividual, individualComparisonData, showGroupCompare, compareMode]);

  // Render Top Candidates Chart
  useEffect(() => {
    if (topCandidatesRootRef.current) {
      topCandidatesRootRef.current.dispose();
      topCandidatesRootRef.current = null;
    }

    if (!amChartsLoaded || selectedIndividual || compareMode !== 'individual' || !showGroupCompare || !document.getElementById('topCandidatesChartDiv')) return;

    const am5 = window.am5;
    const am5xy = window.am5xy;
    const am5themes_Animated = window.am5themes_Animated;

    const root = am5.Root.new('topCandidatesChartDiv');
    topCandidatesRootRef.current = root;

    if (root._logo) {
      root._logo.dispose();
    }

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: root.verticalLayout,
        paddingLeft: 10,
        paddingRight: 10,
        paddingTop: 20,
        paddingBottom: 20
      })
    );

    const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 20 });
    xRenderer.labels.template.setAll({
      paddingTop: 6,
      paddingBottom: 6,
      fontSize: 10,
      fontWeight: '600',
      fill: am5.color(0x475569)
    });

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'name',
        renderer: xRenderer
      })
    );

    const yRenderer = am5xy.AxisRendererY.new(root, {});
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        max: 100,
        numberFormat: "#'%'",
        renderer: yRenderer
      })
    );

    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Score',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'score',
        categoryXField: 'name'
      })
    );

    series.columns.template.setAll({
      strokeOpacity: 0,
      cornerRadiusTL: 6,
      cornerRadiusTR: 6
    });

    series.columns.template.adapters.add('fill', (fill, target) => {
      const dataContext = target.dataItem.dataContext;
      if (dataContext && dataContext.score !== undefined) {
        const score = dataContext.score;
        return score >= 75 ? am5.color(0x10b981) : score >= 50 ? am5.color(0xf59e0b) : am5.color(0xef4444);
      }
      return fill;
    });

    series.bullets.push(function() {
      return am5.Bullet.new(root, {
        locationY: 1,
        sprite: am5.Label.new(root, {
          text: "{valueY}%",
          fill: am5.color(0x0f172a),
          fontSize: 11,
          fontWeight: "800",
          populateText: true,
          centerX: am5.p50,
          centerY: am5.p100,
          dy: -6
        })
      });
    });

    xAxis.data.setAll(allCandidatesStats.topCandidates);
    series.data.setAll(allCandidatesStats.topCandidates);

    series.appear(1000);
    chart.appear(1000, 100);
  }, [amChartsLoaded, selectedIndividual, allCandidatesStats.topCandidates, compareMode, showGroupCompare]);

  // Render Bottom Candidates Chart
  useEffect(() => {
    if (bottomCandidatesRootRef.current) {
      bottomCandidatesRootRef.current.dispose();
      bottomCandidatesRootRef.current = null;
    }

    if (!amChartsLoaded || selectedIndividual || compareMode !== 'individual' || !showGroupCompare || !document.getElementById('bottomCandidatesChartDiv')) return;

    const am5 = window.am5;
    const am5xy = window.am5xy;
    const am5themes_Animated = window.am5themes_Animated;

    const root = am5.Root.new('bottomCandidatesChartDiv');
    bottomCandidatesRootRef.current = root;

    if (root._logo) {
      root._logo.dispose();
    }

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: root.verticalLayout,
        paddingLeft: 10,
        paddingRight: 10,
        paddingTop: 20,
        paddingBottom: 20
      })
    );

    const xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 20 });
    xRenderer.labels.template.setAll({
      paddingTop: 6,
      paddingBottom: 6,
      fontSize: 10,
      fontWeight: '600',
      fill: am5.color(0x475569)
    });

    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: 'name',
        renderer: xRenderer
      })
    );

    const yRenderer = am5xy.AxisRendererY.new(root, {});
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        min: 0,
        max: 100,
        numberFormat: "#'%'",
        renderer: yRenderer
      })
    );

    const series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        name: 'Score',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'score',
        categoryXField: 'name'
      })
    );

    series.columns.template.setAll({
      strokeOpacity: 0,
      cornerRadiusTL: 6,
      cornerRadiusTR: 6
    });

    series.columns.template.adapters.add('fill', (fill, target) => {
      const dataContext = target.dataItem.dataContext;
      if (dataContext && dataContext.score !== undefined) {
        const score = dataContext.score;
        return score >= 75 ? am5.color(0x10b981) : score >= 50 ? am5.color(0xf59e0b) : am5.color(0xef4444);
      }
      return fill;
    });

    series.bullets.push(function() {
      return am5.Bullet.new(root, {
        locationY: 1,
        sprite: am5.Label.new(root, {
          text: "{valueY}%",
          fill: am5.color(0x0f172a),
          fontSize: 11,
          fontWeight: "800",
          populateText: true,
          centerX: am5.p50,
          centerY: am5.p100,
          dy: -6
        })
      });
    });

    xAxis.data.setAll(allCandidatesStats.bottomCandidates);
    series.data.setAll(allCandidatesStats.bottomCandidates);

    series.appear(1000);
    chart.appear(1000, 100);
  }, [amChartsLoaded, selectedIndividual, allCandidatesStats.bottomCandidates, compareMode, showGroupCompare]);

  // Download Option Selected Users
  const downloadOptionCSV = (option, questionText) => {
    if (!data?.users_lookup) return;
    const list = option.user_ids.map(id => data.users_lookup[id]).filter(Boolean);
    let csv = "Employee Name,Employee ID,Role,Department\n";
    list.forEach(u => {
      const name = `"${u.name.replace(/"/g, '""')}"`;
      const empId = `"${u.emp_id.replace(/"/g, '""')}"`;
      const role = `"${u.role.replace(/"/g, '""')}"`;
      const dept = `"${u.department.replace(/"/g, '""')}"`;
      csv += `${name},${empId},${role},${dept}\n`;
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    const safeQName = questionText.slice(0, 20).replace(/[^a-zA-Z0-9]/g, '_');
    link.setAttribute("download", buildAssessmentExportFilename(`Option_${option.option}_Users_${safeQName}`, 'csv'));
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading || !amChartsLoaded) {
    return (
      <div className="p-8 min-h-screen bg-slate-50/50 flex flex-col items-center justify-center gap-4">
        <div className="w-12 h-12 border-4 border-red-600/20 border-t-red-600 rounded-full animate-spin"></div>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
          {!amChartsLoaded ? 'Loading Chart Libraries...' : 'Analyzing Results...'}
        </p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-8 min-h-screen bg-slate-50/50 flex flex-col items-center justify-center gap-4">
        <FiAlertTriangle className="text-4xl text-rose-500" />
        <p className="text-sm font-bold text-slate-500">Failed to load analytics data.</p>
        <button onClick={onBack} className="btn-assessment btn-assessment-danger">Go Back</button>
      </div>
    );
  }

  // Unified candidates details filtering
  const filteredCandidates = (() => {
    if (!data?.candidates_data) return [];
    const search = overallSearch.toLowerCase();
    let list = [...uniqueCandidatesList];

    // 1. Filter by overall metric if selected
    if (selectedOverallMetric === 'Top') {
      list = list.filter(c => (c.overall_pct ?? c.accuracy ?? 0) >= 75);
    } else if (selectedOverallMetric === 'Average') {
      list = list.filter(c => {
        const pct = c.overall_pct ?? c.accuracy ?? 0;
        const passPercentage = c.pass_percentage ?? 50;
        return pct >= passPercentage && pct < 75;
      });
    } else if (selectedOverallMetric === 'Low') {
      list = list.filter(c => {
        const pct = c.overall_pct ?? c.accuracy ?? 0;
        const passPercentage = c.pass_percentage ?? 50;
        return pct < passPercentage;
      });
    }

    // 2. Filter by role if selected
    if (selectedRole) {
      if (selectedRole.role === 'Others') {
        const tempCounts = {};
        uniqueCandidatesList.forEach(c => {
          let r = c.role || 'Unknown';
          if (r.toLowerCase() === 'overall') r = 'Candidate';
          const missedQs = getCandidateMissedQs(c, data?.questions_data);
          if (missedQs.length > 0) {
            tempCounts[r] = (tempCounts[r] || 0) + missedQs.length;
          }
        });
        const top10Roles = Object.keys(tempCounts)
          .filter(r => r.toLowerCase() !== 'overall')
          .sort((a, b) => tempCounts[b] - tempCounts[a])
          .slice(0, 10);
        list = list.filter(c => !top10Roles.includes(c.role || 'Unknown'));
      } else {
        list = list.filter(c => c.role === selectedRole.role);
      }
      if (activeRoleChart === 'missed') {
        if (selectedQuestion) {
          list = list.filter(c => {
            const missedList = getCandidateMissedQs(c, data?.questions_data);
            return missedList.some(q => String(q.question_id) === String(selectedQuestion.question_id));
          });
        } else {
          list = list.filter(c => {
            const missedList = getCandidateMissedQs(c, data?.questions_data);
            return missedList.length > 0;
          });
        }
      }
    }

    // 3. Search filter
    if (search.trim() !== '') {
      list = list.filter(c => 
        (c.name || '').toLowerCase().includes(search) ||
        (c.emp_id || '').toLowerCase().includes(search) ||
        (c.role || '').toLowerCase().includes(search) ||
        (c.department || '').toLowerCase().includes(search)
      );
    }

    return list;
  })();

  const effectiveItemsPerPage = itemsPerPage === 'All' ? filteredCandidates.length || 1 : itemsPerPage;
  const overallTotalPages = Math.ceil(filteredCandidates.length / effectiveItemsPerPage);
  const paginatedCandidates = filteredCandidates.slice(
    (overallPage - 1) * effectiveItemsPerPage,
    overallPage * effectiveItemsPerPage
  );

  // Paginate role missed questions
  const filteredRoleQuestions = computedSelectedRole 
    ? (computedSelectedRole.missed_questions || []).filter(mq => 
        mq.question_text.toLowerCase().includes(roleQuestionSearch.toLowerCase())
      )
    : [];

  const roleQuestionTotalPages = Math.ceil(filteredRoleQuestions.length / effectiveItemsPerPage);
  const paginatedRoleQuestions = filteredRoleQuestions.slice(
    (roleQuestionPage - 1) * effectiveItemsPerPage,
    roleQuestionPage * effectiveItemsPerPage
  );

  const showMissedQuestionsCol = selectedRole !== null;
  const showProficiencyCol = !selectedRole;

  // Backward compatibility alias definitions for export modules
  const filteredOverallCandidates = filteredCandidates;
  const filteredRoleCandidates = filteredCandidates;

  // Download Role Excel Report
  const downloadRoleExcel = async (selectedFields) => {
    if (!filteredRoleCandidates || filteredRoleCandidates.length === 0) return;

    const formatExcelDate = (val) => {
  if (!val || val === 'N/A') return 'N/A';
  const match = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return `${match[3]}-${match[2]}-${match[1]}`;
  }
  return val;
};

const uniqueGroups = [];
const groupNamesMap = new Set();
filteredRoleCandidates.forEach(cand => {
  const gName = cand.pack_name || 'General Group';
  if (!groupNamesMap.has(gName)) {
    groupNamesMap.add(gName);
    uniqueGroups.push({
      pack_name: gName,
      assessment_name: cand.assessment_name || 'Test Report',
      date: formatExcelDate(cand.submitted_at ? cand.submitted_at.split(' ')[0] : ''),
      duration: cand.duration ? `${cand.duration} Minutes` : 'Not specified'
    });
  }
});
if (uniqueGroups.length === 0) {
  uniqueGroups.push({ pack_name: 'General Group', assessment_name: 'Test Report', date: 'N/A', duration: 'Not specified' });
}

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Role Details');

    // 1. Report Title Block
    worksheet.mergeCells('A1:E1');
    const titleCell = worksheet.getCell('A1');
    titleCell.value = 'TEST RESULTS REPORT';
    titleCell.font = { bold: true, size: 14, color: { argb: 'FFFFFFFF' } };
    titleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1F3864' },
    };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.getRow(1).height = 30;

    // 2. Left Metadata Block (Row 3-6)
    const leftMetadata = [
      ['Test name', testName],
      ['Group name', groupName],
      ['Date:', examDate],
      ['Duration:', durationText],
    ];

    leftMetadata.forEach((item, index) => {
      const rIdx = index + 3;
      const keyCell = worksheet.getCell(rIdx, 1);
      keyCell.value = item[0];
      keyCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E2F3' } };
      keyCell.font = { bold: true, color: { argb: 'FF1E293B' }, size: 10 };
      keyCell.alignment = { horizontal: 'left', vertical: 'middle' };
      keyCell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };

      const valCell = worksheet.getCell(rIdx, 2);
      valCell.value = item[1] ?? 'â€”';
      valCell.font = { size: 10, color: { argb: 'FF1E293B' } };
      valCell.alignment = { horizontal: 'left', vertical: 'middle' };
      valCell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };
      
      worksheet.getRow(rIdx).height = 20;
    });

    // 3. Right Metadata Block (Row 3-5: Selected Role, Role Accuracy, Total Missed)
    const rightMetadata = [
      ['Selected Role', computedSelectedRole.role],
      ['Role Percentage', `${computedSelectedRole.accuracy}%`],
      ['Total Missed', computedSelectedRole.total_missed]
    ];

    rightMetadata.forEach((item, index) => {
      const rIdx = index + 3;
      const labelCell = worksheet.getCell(rIdx, 4);
      labelCell.value = item[0];
      labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E2F3' } };
      labelCell.font = { bold: true, color: { argb: 'FF1E293B' }, size: 10 };
      labelCell.alignment = { horizontal: 'left', vertical: 'middle' };
      labelCell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };

      const valCell = worksheet.getCell(rIdx, rightColStart + 1);
      valCell.value = item[1] ?? 'â€”';
      valCell.font = { size: 10, color: { argb: 'FF1E293B' } };
      valCell.alignment = { horizontal: 'center', vertical: 'middle' };
      valCell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };
    });

    worksheet.getRow(7).height = 18;

    // Table Header Row
    const headerRowIdx = 8;
    worksheet.getRow(headerRowIdx).height = 24;

    const sNoColHeader = worksheet.getCell(headerRowIdx, 1);
    sNoColHeader.value = 'S.NO';
    sNoColHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F3864' } };
    sNoColHeader.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
    sNoColHeader.alignment = { horizontal: 'center', vertical: 'middle' };
    sNoColHeader.border = {
      top: { style: 'thin', color: { argb: 'FF000000' } },
      left: { style: 'thin', color: { argb: 'FF000000' } },
      bottom: { style: 'thin', color: { argb: 'FF000000' } },
      right: { style: 'thin', color: { argb: 'FF000000' } },
    };

    selectedFields.forEach((f, colIdx) => {
      const cell = worksheet.getCell(headerRowIdx, colIdx + 2);
      cell.value = f.label.toUpperCase();
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F3864' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };
    });

    // Populate data
    filteredRoleCandidates.forEach((cand, rowIndex) => {
      const excelRow = rowIndex + 9;
      
      const sNoCell = worksheet.getCell(excelRow, 1);
      sNoCell.value = rowIndex + 1;
      sNoCell.font = { size: 10, color: { argb: 'FF1E293B' } };
      sNoCell.alignment = { horizontal: 'center', vertical: 'middle' };
      sNoCell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };

      selectedFields.forEach((f, colIndex) => {
        const cell = worksheet.getCell(excelRow, colIndex + 2);
        let val = '';
        if (f.id === 'name') val = cand.name;
        else if (f.id === 'emp_id') val = cand.emp_id;
        else if (f.id === 'role') val = cand.role;
        else if (f.id === 'department') val = cand.assessment_name || cand.department || 'N/A';
        else if (f.id === 'marks') val = `${cand.mcq_score}/${cand.total_marks || 100}`;
        else if (f.id === 'accuracy') val = `${cand.overall_pct}%`;
        else if (f.id === 'weak_pedagogy') val = cand.weak_pedagogy && cand.weak_pedagogy !== 'None' ? cand.weak_pedagogy : 'N/A';

        cell.value = val ?? '';
        cell.font = { size: 10, color: { argb: 'FF1E293B' } };
        cell.alignment = {
          horizontal: f.id === 'name' ? 'left' : 'center',
          vertical: 'middle',
          wrapText: true,
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } },
        };
      });
      worksheet.getRow(excelRow).height = 18;
    });

    // Column widths
    worksheet.getColumn(1).width = 8;
    selectedFields.forEach((f, c) => {
      worksheet.getColumn(c + 2).width = f.id === 'name' ? 28 : 18;
    });
    worksheet.getColumn(rightColStart).width = 22;
    worksheet.getColumn(rightColStart + 1).width = 15;

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      buildAssessmentExportFilename(`Candidates_Role_${selectedRole.role.replace(/[^a-zA-Z0-9]/g, '_')}_Details`, 'xlsx')
    );
  };

  // Build Role PDF Doc
  const buildRolePdfDoc = async (selectedFields) => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const formatExcelDate = (val) => {
  if (!val || val === 'N/A') return 'N/A';
  const match = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return `${match[3]}-${match[2]}-${match[1]}`;
  }
  return val;
};

const uniqueGroups = [];
const groupNamesMap = new Set();
filteredRoleCandidates.forEach(cand => {
  const gName = cand.pack_name || 'General Group';
  if (!groupNamesMap.has(gName)) {
    groupNamesMap.add(gName);
    uniqueGroups.push({
      pack_name: gName,
      assessment_name: cand.assessment_name || 'Test Report',
      date: formatExcelDate(cand.submitted_at ? cand.submitted_at.split(' ')[0] : ''),
      duration: cand.duration ? `${cand.duration} Minutes` : 'Not specified'
    });
  }
});
if (uniqueGroups.length === 0) {
  uniqueGroups.push({ pack_name: 'General Group', assessment_name: 'Test Report', date: 'N/A', duration: 'Not specified' });
}

    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(30, 41, 59);
    doc.text(`CANDIDATES ROLE DETAILS REPORT`, pageWidth / 2, 22, { align: 'center' });

    // Metadata Table Block
    const metadataBody = [
      ['Test name', ...uniqueGroups.map(g => g.assessment_name), 'Selected Role', computedSelectedRole.role],
      ['Group name', ...uniqueGroups.map(g => g.pack_name), 'Role Percentage', `${computedSelectedRole.accuracy}%`],
      ['Date:', ...uniqueGroups.map(g => g.date), 'Total Missed', String(computedSelectedRole.total_missed)],
      ['Duration:', ...uniqueGroups.map(g => g.duration), '', ''],
    ];
    let colStyles = {
      0: { fillColor: [217, 226, 243], textColor: [30, 41, 59], fontStyle: 'bold' }
    };
    const lastIdx = uniqueGroups.length + 1;
    colStyles[lastIdx] = { fillColor: [217, 226, 243], textColor: [30, 41, 59], fontStyle: 'bold' };
    
    let tblWidth = 90 + (uniqueGroups.length * 35) + 40;
    if (tblWidth > pageWidth - 28) tblWidth = pageWidth - 28;

    autoTable(doc, {
      startY: 28,
      body: metadataBody,
      styles: { fontSize: 8, cellPadding: 2.2 },
      columnStyles: colStyles,
      tableWidth: tblWidth,
      theme: 'grid',
    });

    const headers = ['S.No', ...selectedFields.map(f => f.label)];
    const body = filteredRoleCandidates.map((cand, idx) => {
      return [
        idx + 1,
        ...selectedFields.map(f => {
          if (f.id === 'name') return cand.name;
          if (f.id === 'emp_id') return cand.emp_id;
          if (f.id === 'role') return cand.role;
          if (f.id === 'department') return cand.assessment_name || cand.department || 'N/A';
          if (f.id === 'marks') return `${cand.mcq_score}/${cand.total_marks || 100}`;
          if (f.id === 'accuracy') return `${cand.overall_pct}%`;
          if (f.id === 'weak_pedagogy') return cand.weak_pedagogy && cand.weak_pedagogy !== 'None' ? cand.weak_pedagogy : 'N/A';
          return '';
        })
      ];
    });

    const mainColStyles = {
      0: { halign: 'center' }
    };
    selectedFields.forEach((field, colIdx) => {
      const targetIdx = colIdx + 1;
      if (field.id === 'marks' || field.id === 'accuracy' || field.id === 'emp_id') {
        mainColStyles[targetIdx] = { halign: 'center' };
      } else {
        mainColStyles[targetIdx] = { halign: 'left' };
      }
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [headers.map(h => h.toUpperCase())],
      body: body,
      margin: { left: 14, right: 14, top: 30, bottom: 16 },
      tableWidth: pageWidth - 28,
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [31, 56, 100], textColor: [255, 255, 255] },
      columnStyles: mainColStyles,
      theme: 'grid',
    });

    try {
      const logoDataUrl = await getEnovaLogoDataUrl();
      applyAssessmentPdfBranding(doc, logoDataUrl);
    } catch (e) {
      console.warn('PDF Branding failed', e);
    }

    const filename = buildAssessmentExportFilename(`Candidates_Role_${selectedRole.role.replace(/[^a-zA-Z0-9]/g, '_')}_Details`, 'pdf');
    return { doc, filename };
  };

  // Download Overall Excel Report
  const downloadOverallExcel = async (selectedFields) => {
    if (!filteredOverallCandidates || filteredOverallCandidates.length === 0) return;

    const formatExcelDate = (val) => {
  if (!val || val === 'N/A') return 'N/A';
  const match = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return `${match[3]}-${match[2]}-${match[1]}`;
  }
  return val;
};

const uniqueGroups = [];
const groupNamesMap = new Set();
displayCandidates.forEach(cand => {
  const gName = cand.pack_name || 'General Group';
  if (!groupNamesMap.has(gName)) {
    groupNamesMap.add(gName);
    uniqueGroups.push({
      pack_name: gName,
      assessment_name: cand.assessment_name || 'Test Report',
      date: formatExcelDate(cand.submitted_at ? cand.submitted_at.split(' ')[0] : ''),
      duration: cand.duration ? `${cand.duration} Minutes` : 'Not specified'
    });
  }
});
if (uniqueGroups.length === 0) {
  uniqueGroups.push({ pack_name: 'General Group', assessment_name: 'Test Report', date: 'N/A', duration: 'Not specified' });
}

    const candidatesList = data?.candidates_data || [];

    const topCands = candidatesList.filter(c => (c.overall_pct ?? c.accuracy ?? 0) >= 75);
    const avgCands = candidatesList.filter(c => {
      const pct = c.overall_pct ?? c.accuracy ?? 0;
      const passPercentage = c.pass_percentage ?? 50;
      return pct >= passPercentage && pct < 75;
    });
    const lowCands = candidatesList.filter(c => {
      const pct = c.overall_pct ?? c.accuracy ?? 0;
      const passPercentage = c.pass_percentage ?? 50;
      return pct < passPercentage;
    });

    const calcAvg = (list) => {
      if (list.length === 0) return 0;
      const sum = list.reduce((s, c) => s + (c.overall_pct ?? c.accuracy ?? 0), 0);
      return Math.round(sum / list.length);
    };

    const topperAcc = `${calcAvg(topCands)}%`;
    const avgAcc = `${calcAvg(avgCands)}%`;
    const lowestAcc = `${calcAvg(lowCands)}%`;

    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Test Module';
    workbook.created = new Date();
    
    const metricLabel = selectedOverallMetric || 'All';
    const worksheet = workbook.addWorksheet(`${metricLabel} Candidates`, {
      views: [{ state: 'frozen', ySplit: 8 }]
    });

    const tableColCount = selectedFields.length + 1; // S.No + selected columns count

    // Row 1: Merged Title Row
    worksheet.mergeCells(1, 1, 1, Math.max(tableColCount, 5));
    const titleCell = worksheet.getCell(1, 1);
    titleCell.value = `TEST RESULTS REPORT - ${metricLabel.toUpperCase()} CANDIDATES`;
    titleCell.font = { bold: true, color: { argb: 'FF1F3864' }, size: 16 };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.border = {
      top: { style: 'thin', color: { argb: 'FF000000' } },
      left: { style: 'thin', color: { argb: 'FF000000' } },
      bottom: { style: 'thin', color: { argb: 'FF000000' } },
      right: { style: 'thin', color: { argb: 'FF000000' } },
    };
    worksheet.getRow(1).height = 24;

    // Row 2: Empty
    worksheet.getRow(2).height = 18;

    // Rows 3 to 6: Left Metadata Blocks
    const metadata = [
      ['Test name', ...uniqueGroups.map(g => g.assessment_name)],
      ['Group name', ...uniqueGroups.map(g => g.pack_name)],
      ['Date:', ...uniqueGroups.map(g => g.date)],
      ['Duration:', ...uniqueGroups.map(g => g.duration)]
    ];

    metadata.forEach((item, index) => {
      const rIdx = index + 3;

      const labelCell = worksheet.getCell(rIdx, 1);
      labelCell.value = item[0];
      labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E2F3' } };
      labelCell.font = { bold: true, color: { argb: 'FF1E293B' }, size: 10 };
      labelCell.alignment = { horizontal: 'left', vertical: 'middle' };
      labelCell.border = { top: { style: 'thin', color: { argb: 'FF000000' } }, left: { style: 'thin', color: { argb: 'FF000000' } }, bottom: { style: 'thin', color: { argb: 'FF000000' } }, right: { style: 'thin', color: { argb: 'FF000000' } } };

      for (let c = 1; c < item.length; c++) {
        const valCell = worksheet.getCell(rIdx, c + 1);
        valCell.value = item[c] ?? 'â€”';
        valCell.font = { size: 10, color: { argb: 'FF1E293B' } };
        valCell.alignment = { horizontal: 'center', vertical: 'middle' };
        valCell.border = { top: { style: 'thin', color: { argb: 'FF000000' } }, left: { style: 'thin', color: { argb: 'FF000000' } }, bottom: { style: 'thin', color: { argb: 'FF000000' } }, right: { style: 'thin', color: { argb: 'FF000000' } } };
      }

      worksheet.getRow(rIdx).height = 20;
    });

    // Row 3: Right Metadata Block (Top or Average or Low Percentage based on selection)
    const rightColStart = uniqueGroups.length + 3;
    let accuracyLabel = '';
    let accuracyValue = '';
    if (selectedOverallMetric === 'Top') {
      accuracyLabel = 'Top Percentage';
      accuracyValue = topperAcc;
    } else if (selectedOverallMetric === 'Average') {
      accuracyLabel = 'Average Percentage';
      accuracyValue = avgAcc;
    } else if (selectedOverallMetric === 'Low') {
      accuracyLabel = 'Low Percentage';
      accuracyValue = lowestAcc;
    }

    if (accuracyLabel) {
      const labelCell = worksheet.getCell(3, rightColStart);
      labelCell.value = accuracyLabel;
      labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9E2F3' } };
      labelCell.font = { bold: true, color: { argb: 'FF1E293B' }, size: 10 };
      labelCell.alignment = { horizontal: 'left', vertical: 'middle' };
      labelCell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };

      const valCell = worksheet.getCell(3, rightColStart + 1); // Column E
      valCell.value = accuracyValue;
      valCell.font = { size: 10, color: { argb: 'FF1E293B' } };
      valCell.alignment = { horizontal: 'center', vertical: 'middle' };
      valCell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };
    }

    // Row 7: Empty
    worksheet.getRow(7).height = 18;

    // Row 8: Table Header Row
    const headers = ['S.NO', ...selectedFields.map(f => f.label.toUpperCase())];
    headers.forEach((label, i) => {
      const cell = worksheet.getCell(8, i + 1);
      cell.value = label;
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F3864' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };
    });
    worksheet.getRow(8).height = 24;

    // Body Rows
    filteredOverallCandidates.forEach((cand, rowIndex) => {
      const excelRow = rowIndex + 9;
      
      // S.No cell
      const sNoCell = worksheet.getCell(excelRow, 1);
      sNoCell.value = rowIndex + 1;
      sNoCell.font = { size: 10, color: { argb: 'FF1E293B' } };
      sNoCell.alignment = { horizontal: 'center', vertical: 'middle' };
      sNoCell.border = {
        top: { style: 'thin', color: { argb: 'FF000000' } },
        left: { style: 'thin', color: { argb: 'FF000000' } },
        bottom: { style: 'thin', color: { argb: 'FF000000' } },
        right: { style: 'thin', color: { argb: 'FF000000' } },
      };

      selectedFields.forEach((f, colIndex) => {
        const cell = worksheet.getCell(excelRow, colIndex + 2);
        let val = '';
        if (f.id === 'name') val = cand.name;
        else if (f.id === 'emp_id') val = cand.emp_id;
        else if (f.id === 'role') val = cand.role;
        else if (f.id === 'department') val = cand.assessment_name || cand.department || 'N/A';
        else if (f.id === 'marks') val = `${cand.mcq_score}/${cand.total_marks || 100}`;
        else if (f.id === 'accuracy') val = `${cand.overall_pct}%`;
        else if (f.id === 'weak_pedagogy') val = cand.weak_pedagogy && cand.weak_pedagogy !== 'None' ? cand.weak_pedagogy : 'N/A';

        cell.value = val ?? '';
        cell.font = { size: 10, color: { argb: 'FF1E293B' } };
        cell.alignment = {
          horizontal: f.id === 'name' ? 'left' : 'center',
          vertical: 'middle',
          wrapText: true,
        };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } },
        };
      });
      worksheet.getRow(excelRow).height = 18;
    });

    // Column widths
    worksheet.getColumn(1).width = 8;
    selectedFields.forEach((f, c) => {
      worksheet.getColumn(c + 2).width = f.id === 'name' ? 28 : 18;
    });
    worksheet.getColumn(rightColStart).width = 22; // Topper/Average/Lowest Percentage labels
    worksheet.getColumn(rightColStart + 1).width = 15; // Metric values

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      buildAssessmentExportFilename(`Candidates_${metricLabel}_Percentage_Details`, 'xlsx')
    );
  };

  // Build Overall PDF Doc
  const buildOverallPdfDoc = async (selectedFields) => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const formatExcelDate = (val) => {
  if (!val || val === 'N/A') return 'N/A';
  const match = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    return `${match[3]}-${match[2]}-${match[1]}`;
  }
  return val;
};

const uniqueGroups = [];
const groupNamesMap = new Set();
filteredOverallCandidates.forEach(cand => {
  const gName = cand.pack_name || 'General Group';
  if (!groupNamesMap.has(gName)) {
    groupNamesMap.add(gName);
    uniqueGroups.push({
      pack_name: gName,
      assessment_name: cand.assessment_name || 'Test Report',
      date: formatExcelDate(cand.submitted_at ? cand.submitted_at.split(' ')[0] : ''),
      duration: cand.duration ? `${cand.duration} Minutes` : 'Not specified'
    });
  }
});
if (uniqueGroups.length === 0) {
  uniqueGroups.push({ pack_name: 'General Group', assessment_name: 'Test Report', date: 'N/A', duration: 'Not specified' });
}

    const candidatesList = data?.candidates_data || [];

    const topCands = candidatesList.filter(c => (c.overall_pct ?? c.accuracy ?? 0) >= 75);
    const avgCands = candidatesList.filter(c => {
      const pct = c.overall_pct ?? c.accuracy ?? 0;
      const passPercentage = c.pass_percentage ?? 50;
      return pct >= passPercentage && pct < 75;
    });
    const lowCands = candidatesList.filter(c => {
      const pct = c.overall_pct ?? c.accuracy ?? 0;
      const passPercentage = c.pass_percentage ?? 50;
      return pct < passPercentage;
    });

    const calcAvg = (list) => {
      if (list.length === 0) return 0;
      const sum = list.reduce((s, c) => s + (c.overall_pct ?? c.accuracy ?? 0), 0);
      return Math.round(sum / list.length);
    };

    const topperAcc = `${calcAvg(topCands)}%`;
    const avgAcc = `${calcAvg(avgCands)}%`;
    const lowestAcc = `${calcAvg(lowCands)}%`;

    const metricLabel = selectedOverallMetric || 'All';
    const pageWidth = doc.internal.pageSize.getWidth();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(30, 41, 59);
    doc.text(`CANDIDATES ${metricLabel.toUpperCase()} PERCENTAGE REPORT`, pageWidth / 2, 22, { align: 'center' });

    // Metadata Table Block
    let metadataBody = [
      ['Test name', ...uniqueGroups.map(g => g.assessment_name)],
      ['Group name', ...uniqueGroups.map(g => g.pack_name)],
      ['Date:', ...uniqueGroups.map(g => g.date)],
      ['Duration:', ...uniqueGroups.map(g => g.duration)],
    ];
    let colStyles = {
      0: { fillColor: [217, 226, 243], textColor: [30, 41, 59], fontStyle: 'bold' }
    };
    let tblWidth = 90 + (uniqueGroups.length * 40); // dynamic width
    if (tblWidth > pageWidth - 28) tblWidth = pageWidth - 28;

    if (selectedOverallMetric !== 'All' && selectedOverallMetric !== null) {
      const emptyCols = new Array(uniqueGroups.length).fill('');
      metadataBody = metadataBody.map(row => [...row, '', '']);
      const lastIdx = uniqueGroups.length + 1;
      
      if (selectedOverallMetric === 'Top') {
        metadataBody[0][lastIdx] = 'Top Percentage';
        metadataBody[0][lastIdx + 1] = topperAcc;
      } else if (selectedOverallMetric === 'Average') {
        metadataBody[0][lastIdx] = 'Average Percentage';
        metadataBody[0][lastIdx + 1] = avgAcc;
      } else if (selectedOverallMetric === 'Low') {
        metadataBody[0][lastIdx] = 'Low Percentage';
        metadataBody[0][lastIdx + 1] = lowestAcc;
      }
      colStyles[lastIdx] = { fillColor: [217, 226, 243], textColor: [30, 41, 59], fontStyle: 'bold' };
      tblWidth += 60;
    }

    autoTable(doc, {
      startY: 28,
      body: metadataBody,
      styles: { fontSize: 8, cellPadding: 2.2 },
      columnStyles: colStyles,
      tableWidth: tblWidth,
      theme: 'grid',
    });

    const headers = ['S.No', ...selectedFields.map(f => f.label)];
    const body = displayCandidates.map((cand, idx) => {
      return [
        idx + 1,
        ...selectedFields.map(f => {
          if (f.id === 'name') return cand.name;
          if (f.id === 'emp_id') return cand.emp_id;
          if (f.id === 'role') return cand.role;
          if (f.id === 'department') return cand.assessment_name || cand.department || 'N/A';
          if (f.id === 'marks') return `${cand.mcq_score}/${cand.total_marks || 100}`;
          if (f.id === 'accuracy') return `${cand.overall_pct}%`;
          if (f.id === 'weak_pedagogy') return cand.weak_pedagogy && cand.weak_pedagogy !== 'None' ? cand.weak_pedagogy : 'N/A';
          return '';
        })
      ];
    });

    const mainColStyles = {
      0: { halign: 'center' }
    };
    selectedFields.forEach((field, colIdx) => {
      const targetIdx = colIdx + 1;
      if (field.id === 'marks' || field.id === 'accuracy' || field.id === 'emp_id') {
        mainColStyles[targetIdx] = { halign: 'center' };
      } else {
        mainColStyles[targetIdx] = { halign: 'left' };
      }
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [headers.map(h => h.toUpperCase())],
      body: body,
      margin: { left: 14, right: 14, top: 30, bottom: 16 },
      tableWidth: pageWidth - 28,
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [31, 56, 100], textColor: [255, 255, 255] },
      columnStyles: mainColStyles,
      theme: 'grid',
    });

    try {
      const logoDataUrl = await getEnovaLogoDataUrl();
      applyAssessmentPdfBranding(doc, logoDataUrl);
    } catch (e) {
      console.warn('PDF Branding failed', e);
    }

    const filename = buildAssessmentExportFilename(`Candidates_${metricLabel}_Percentage_Details`, 'pdf');
    return { doc, filename };
  };

  // Sort and Paginate question performance
  const sortedQuestions = [...data.questions_data]
    .filter(q => q.question_text.toLowerCase().includes(questionSearch.toLowerCase()))
    .filter(q => {
      if (!selectedQuestionPedagogy) return true;
      const qPed = q.pedagogy || 'General';
      return qPed.toLowerCase() === selectedQuestionPedagogy.pedagogy.toLowerCase();
    })
    .map(q => {
      // Recompute stats if selectedRole is active
      if (!selectedRole || !q.distribution || !data.users_lookup) return q;
      
      let roleWrongCount = 0;
      let roleAttempts = 0;
      
      q.distribution.forEach(opt => {
        if (opt.user_ids) {
          const roleUsers = opt.user_ids.filter(id => {
            const u = data.users_lookup[id];
            return u && u.role === selectedRole.role;
          });
          const count = roleUsers.length;
          roleAttempts += count;
          if (!opt.is_correct && opt.option !== 'Unanswered') {
            roleWrongCount += count;
          }
        }
      });
      
      return {
        ...q,
        wrong_count: roleWrongCount,
        attempts: roleAttempts,
        distribution: q.distribution.map(opt => ({
          ...opt,
          user_ids: opt.user_ids ? opt.user_ids.filter(id => data.users_lookup[id]?.role === selectedRole.role) : [],
          count: opt.user_ids ? opt.user_ids.filter(id => data.users_lookup[id]?.role === selectedRole.role).length : 0
        }))
      };
    })
    .filter(q => {
      if (!selectedRole) return true;
      return q.wrong_count > 0;
    })
    .sort((a, b) =>
      compareAnalyticsQuestions(a, b, questionTableSortColumn, questionTableSortDirection)
    );

  const effectiveQuestionItemsPerPage = questionItemsPerPage === 'All' ? sortedQuestions.length || 1 : questionItemsPerPage;
  const questionTotalPages = Math.ceil(sortedQuestions.length / effectiveQuestionItemsPerPage);
  const paginatedQuestions = sortedQuestions.slice(
    (questionPage - 1) * effectiveQuestionItemsPerPage,
    questionPage * effectiveQuestionItemsPerPage
  );
  const questionListUsesScroll =
    questionItemsPerPage === 'All' || Number(questionItemsPerPage) > 6;
  const questionFillRowCount = Math.min(6, Math.max(paginatedQuestions.length, 1));

  // Get candidate answer helper
  const getCandidateAnswer = (candidateId) => {
    if (!selectedQuestion || !selectedQuestion.distribution) return 'Unanswered';
    const foundOpt = selectedQuestion.distribution.find(d => 
      d.user_ids && d.user_ids.some(id => String(id) === String(candidateId))
    );
    return foundOpt ? foundOpt.option : 'Unanswered';
  };

  const currentQuestionIndex = selectedQuestion 
    ? sortedQuestions.findIndex(q => q.question_id === selectedQuestion.question_id)
    : -1;

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      const prevQ = sortedQuestions[currentQuestionIndex - 1];
      setSelectedQuestion(prevQ);
      setSelectedOption(null);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < sortedQuestions.length - 1) {
      const nextQ = sortedQuestions[currentQuestionIndex + 1];
      setSelectedQuestion(nextQ);
      setSelectedOption(null);
    }
  };

  // Option employee drilldown
  // When an option is selected, show users who picked that option (deduplicated by user_id).
  // When no option is selected, use the already-deduped uniqueCandidatesList (group-filtered).
  const optionEmployees = selectedOption
    ? uniqueCandidatesList.filter(c => {
        if (selectedOption.user_ids && Array.isArray(selectedOption.user_ids)) {
          return selectedOption.user_ids.some(id => String(id) === String(c.user_id));
        }
        return getCandidateAnswer(c.user_id).replace('Option ', '') === selectedOption.option.replace('Option ', '');
      })
    : uniqueCandidatesList;

  const filteredOptionEmployees = optionEmployees.filter(emp =>
    emp.name.toLowerCase().includes(employeeSearch.toLowerCase()) ||
    emp.emp_id.toLowerCase().includes(employeeSearch.toLowerCase()) ||
    emp.role.toLowerCase().includes(employeeSearch.toLowerCase()) ||
    emp.department.toLowerCase().includes(employeeSearch.toLowerCase())
  ).sort((a, b) => {
    let aVal = '', bVal = '';
    if (optionTableSortColumn === 'name') {
      aVal = (a.name || '').toLowerCase();
      bVal = (b.name || '').toLowerCase();
    } else if (optionTableSortColumn === 'role') {
      aVal = (a.role || '').toLowerCase();
      bVal = (b.role || '').toLowerCase();
    } else if (optionTableSortColumn === 'option') {
      aVal = getCandidateAnswer(a.user_id);
      bVal = getCandidateAnswer(b.user_id);
    } else if (optionTableSortColumn === 'status') {
      const aAns = getCandidateAnswer(a.user_id);
      const bAns = getCandidateAnswer(b.user_id);
      const aStatus = aAns === selectedQuestion?.correct_answer ? 'correct' : (aAns === 'Unanswered' || aAns === '-' ? 'unanswered' : 'incorrect');
      const bStatus = bAns === selectedQuestion?.correct_answer ? 'correct' : (bAns === 'Unanswered' || bAns === '-' ? 'unanswered' : 'incorrect');
      aVal = aStatus;
      bVal = bStatus;
    }
    
    if (aVal < bVal) return optionTableSortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return optionTableSortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const effectiveOptionEmployeesPerPage = optionEmployeesPerPage === 'All' ? filteredOptionEmployees.length || 1 : optionEmployeesPerPage;
  const employeeTotalPages = Math.ceil(filteredOptionEmployees.length / effectiveOptionEmployeesPerPage);
  const paginatedEmployees = filteredOptionEmployees.slice(
    (employeePage - 1) * effectiveOptionEmployeesPerPage,
    employeePage * effectiveOptionEmployeesPerPage
  );

  // Compute overview data
  const candidatesList = uniqueCandidatesList;
  const rolesList = data?.roles_data || [];
  
  const avgScore = candidatesList.length > 0 
    ? Math.round(candidatesList.reduce((acc, curr) => acc + (curr.overall_pct || 0), 0) / candidatesList.length) 
    : 0;

  const passedCandidates = candidatesList.filter(c => (c.overall_pct || 0) >= 75);
  const passedCount = passedCandidates.length;
  const passRate = candidatesList.length > 0 ? Math.round((passedCount / candidatesList.length) * 100) : 0;

  const averageCandidates = candidatesList.filter(c => (c.overall_pct || 0) >= (c.pass_percentage ?? 50) && (c.overall_pct || 0) < 75);
  const averageCount = averageCandidates.length;

  const needsWorkCandidates = candidatesList.filter(c => (c.overall_pct || 0) < (c.pass_percentage ?? 50));
  const needsWorkCount = needsWorkCandidates.length;

  const displayCandidates = candidatesList.filter(c => {
    const roleStr = c.role || 'Unknown';
    if (selectedRole && roleStr !== selectedRole.role) return false;

    if (showMissedColumn || activeRoleChart === 'missed') {
      const missedQs = getCandidateMissedQs(c, data?.questions_data);
      if (missedQs.length === 0) return false;
    }
    
    const pct = c.overall_pct ?? c.accuracy ?? 0;
    const passPercentage = c.pass_percentage ?? 50;
    
    if (overviewFilter === 'High') {
      if (pct < 75) return false;
    } else if (overviewFilter === 'Average') {
      if (pct < passPercentage || pct >= 75) return false;
    } else if (overviewFilter === 'Low') {
      if (pct >= passPercentage) return false;
    }

    if (overallSearch) {
      const searchLower = overallSearch.toLowerCase();
      const nameMatch = c.name?.toLowerCase().includes(searchLower);
      const roleMatch = c.role?.toLowerCase().includes(searchLower);
      const empIdMatch = c.emp_id?.toLowerCase().includes(searchLower);
      const weakMatch = c.weak_pedagogy?.toLowerCase().includes(searchLower);
      if (!nameMatch && !roleMatch && !empIdMatch && !weakMatch) {
        return false;
      }
    }
    
    return true;
  }).sort((a, b) => {
    let aVal, bVal;
    if (tableSortColumn === 'name') {
      aVal = (a.name || '').toLowerCase();
      bVal = (b.name || '').toLowerCase();
    } else if (tableSortColumn === 'role') {
      aVal = (a.role || '').toLowerCase();
      bVal = (b.role || '').toLowerCase();
    } else if (tableSortColumn === 'missed_total') {
      aVal = getCandidateMissedQs(a, data?.questions_data).length;
      bVal = getCandidateMissedQs(b, data?.questions_data).length;
    } else {
      aVal = a.overall_pct || 0;
      bVal = b.overall_pct || 0;
    }

    if (aVal < bVal) return tableSortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return tableSortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (col) => {
    if (tableSortColumn === col) {
      setTableSortDirection(tableSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setTableSortColumn(col);
      setTableSortDirection(col === 'name' || col === 'role' ? 'asc' : 'desc');
    }
    setOverallPage(1);
  };

  
  const handleQuestionTableSort = (col) => {
    if (questionTableSortColumn === col) {
      setQuestionTableSortDirection(questionTableSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setQuestionTableSortColumn(col);
      setQuestionTableSortDirection(
        col === 'text' || col === 'pedagogy' ? 'asc' : 'desc'
      );
    }
    setQuestionPage(1);
  };

  const renderQuestionSortIcon = (col) => {
    if (questionTableSortColumn !== col) return <i className="bi bi-arrow-down-up text-[8px] ml-1 text-slate-300"></i>;
    return questionTableSortDirection === 'asc' 
      ? <i className="bi bi-arrow-up text-[10px] ml-1 text-[#B91C1C]"></i>
      : <i className="bi bi-arrow-down text-[10px] ml-1 text-[#B91C1C]"></i>;
  };

  const handleGroupTableSort = (col) => {
    if (groupTableSortColumn === col) {
      setGroupTableSortDirection(groupTableSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setGroupTableSortColumn(col);
      setGroupTableSortDirection('asc');
    }
  };

  const renderGroupSortIcon = (col) => {
    if (groupTableSortColumn !== col) return <i className="bi bi-arrow-down-up text-[8px] ml-1 text-slate-300"></i>;
    return groupTableSortDirection === 'asc' 
      ? <i className="bi bi-arrow-up text-[10px] ml-1 text-[#B91C1C]"></i>
      : <i className="bi bi-arrow-down text-[10px] ml-1 text-[#B91C1C]"></i>;
  };

  const handleIndTableSort = (col) => {
    if (indTableSortColumn === col) {
      setIndTableSortDirection(indTableSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setIndTableSortColumn(col);
      setIndTableSortDirection('asc');
    }
  };

  const renderIndSortIcon = (col) => {
    if (indTableSortColumn !== col) return <i className="bi bi-arrow-down-up text-[8px] ml-1 text-slate-300"></i>;
    return indTableSortDirection === 'asc' 
      ? <i className="bi bi-arrow-up text-[10px] ml-1 text-[#B91C1C]"></i>
      : <i className="bi bi-arrow-down text-[10px] ml-1 text-[#B91C1C]"></i>;
  };


  const handleOptionTableSort = (col) => {
    if (optionTableSortColumn === col) {
      setOptionTableSortDirection(optionTableSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setOptionTableSortColumn(col);
      setOptionTableSortDirection('asc');
    }
    setEmployeePage(1);
  };

  const renderOptionSortIcon = (col) => {
    if (optionTableSortColumn !== col) return <i className="bi bi-arrow-down-up text-[8px] ml-1 text-slate-300"></i>;
    return optionTableSortDirection === 'asc' 
      ? <i className="bi bi-arrow-up text-[10px] ml-1 text-[#B91C1C]"></i>
      : <i className="bi bi-arrow-down text-[10px] ml-1 text-[#B91C1C]"></i>;
  };

  const renderSortIcon = (col) => {
    if (tableSortColumn !== col) return <i className="bi bi-arrow-down-up text-[8px] ml-1 text-slate-300"></i>;
    return tableSortDirection === 'asc' 
      ? <i className="bi bi-arrow-up text-[10px] ml-1 text-[#B91C1C]"></i>
      : <i className="bi bi-arrow-down text-[10px] ml-1 text-[#B91C1C]"></i>;
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const getAvatarBgColor = (name) => {
    if (!name) return 'bg-slate-500';
    const charCodeSum = name.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
    const colors = [
      'bg-indigo-500',
      'bg-emerald-500',
      'bg-blue-500',
      'bg-amber-500',
      'bg-rose-500',
      'bg-purple-500',
      'bg-cyan-500',
      'bg-teal-500'
    ];
    return colors[charCodeSum % colors.length];
  };

  const getPedagogyColorInfo = (pedName) => {
    const name = (pedName || '').toLowerCase();
    if (name.includes('conceptual')) {
      return {
        bg: 'bg-blue-500',
        text: 'text-blue-600',
        border: 'border-blue-200',
        lightBg: 'bg-blue-50/50 hover:bg-blue-50',
        activeRing: 'ring-2 ring-blue-500 ring-offset-2',
        badgeBg: 'bg-blue-50 text-blue-700 border-blue-100',
      };
    }
    if (name.includes('procedural')) {
      return {
        bg: 'bg-emerald-500',
        text: 'text-emerald-600',
        border: 'border-emerald-200',
        lightBg: 'bg-emerald-50/50 hover:bg-emerald-50',
        activeRing: 'ring-2 ring-emerald-500 ring-offset-2',
        badgeBg: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      };
    }
    if (name.includes('application')) {
      return {
        bg: 'bg-amber-500',
        text: 'text-amber-600',
        border: 'border-amber-200',
        lightBg: 'bg-amber-50/50 hover:bg-amber-50',
        activeRing: 'ring-2 ring-amber-500 ring-offset-2',
        badgeBg: 'bg-amber-50 text-amber-700 border-amber-100',
      };
    }
    if (name.includes('ethical')) {
      return {
        bg: 'bg-purple-500',
        text: 'text-purple-600',
        border: 'border-purple-200',
        lightBg: 'bg-purple-50/50 hover:bg-purple-50',
        activeRing: 'ring-2 ring-purple-500 ring-offset-2',
        badgeBg: 'bg-purple-50 text-purple-700 border-purple-100',
      };
    }
    if (name.includes('evaluation')) {
      return {
        bg: 'bg-rose-500',
        text: 'text-rose-600',
        border: 'border-rose-200',
        lightBg: 'bg-rose-50/50 hover:bg-rose-50',
        activeRing: 'ring-2 ring-rose-500 ring-offset-2',
        badgeBg: 'bg-rose-50 text-rose-700 border-rose-100',
      };
    }
    return {
      bg: 'bg-slate-500',
      text: 'text-slate-600',
      border: 'border-slate-200',
      lightBg: 'bg-slate-50/50 hover:bg-slate-50',
      activeRing: 'ring-2 ring-slate-500 ring-offset-2',
      badgeBg: 'bg-slate-50 text-slate-700 border-slate-100',
    };
  };

  return (
    <>
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <div className="p-4 sm:p-6 bg-slate-50/50 min-h-screen flex flex-col gap-4 select-none">
        
            {/* Sticky Header & Tabs Container */}
        <div className="sticky top-0 z-40 bg-slate-50/95 backdrop-blur-md -mt-4 sm:-mt-6 -mx-4 sm:-mx-6 pt-4 sm:pt-6 px-4 sm:px-6 pb-0 border-b border-slate-200/80">
          {/* Header Bar */}
          <div className="flex flex-col md:flex-row md:items-start justify-between mb-4 gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3">
                {/* Back button */}
                <button
                  onClick={() => {
                    if (showQuestionPerf) {
                      setShowQuestionPerf(false);
                      setSelectedQuestion(null);
                      setSelectedOption(null);
                    } else {
                      onBack();
                    }
                  }}
                  className="w-9 h-9 rounded-xl bg-white border border-slate-200 text-[#B91C1C] flex items-center justify-center transition-all cursor-pointer shadow-sm hover:bg-red-50/40 hover:border-red-200 lg:hidden shrink-0"
                >
                  <FiArrowLeft className="text-base font-black stroke-[3px]" />
                </button>
                
                <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                  Test Analytics
                </h1>
              </div>
              <p className="text-[11px] font-semibold text-slate-400 pl-[48px] lg:pl-0 flex items-center gap-2 flex-wrap">
                 <span>{uniqueCandidatesList.length} candidates &middot; {data?.all_tests?.length || 0} tests</span>
                 {selectedGroup !== 'All Groups' && (
                   <span className="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-[#B91C1C] text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full">
                     <span className="w-1.5 h-1.5 rounded-full bg-[#B91C1C]" />
                     {selectedGroup}
                   </span>
                 )}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 w-full md:w-auto justify-start md:justify-end">
              {/* Test Selection Dropdown */}
              {!showGroupCompare && (
              <div className="relative bg-white border border-slate-200 rounded-xl px-3 h-[32px] sm:h-[36px] flex items-center gap-2 text-xs font-bold text-slate-700 shadow-sm hover:border-slate-300 transition-all cursor-pointer focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-300">
                 <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest shrink-0">TEST</span>
                 <select
                   value={selectedTestFilter}
                   onChange={(e) => {
                     setSelectedTestFilter(e.target.value);
                     setOverallPage(1);
                     setEmployeePage(1);
                     setQuestionPage(1);
                     setRoleQuestionPage(1);
                   }}
                   className="appearance-none bg-transparent border-0 text-slate-800 py-0 pl-0 pr-6 text-[11px] sm:text-xs font-extrabold tracking-wide cursor-pointer focus:outline-none focus:ring-0 w-full h-full truncate max-w-[150px] sm:max-w-[200px]"
                 >
                   {data?.all_tests?.map((testName, i) => (
                     <option key={i} value={testName}>
                       {testName}
                     </option>
                   ))}
                 </select>
                 <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
                   <FiChevronDown className="text-[14px]" />
                 </div>
              </div>
              )}

              {/* Group / Batch Filter Dropdown */}
              {!showGroupCompare && availableGroups.length > 1 && (
              <div className="relative bg-white border border-slate-200 rounded-xl px-3 h-[32px] sm:h-[36px] flex items-center gap-2 text-xs font-bold text-slate-700 shadow-sm hover:border-slate-300 transition-all cursor-pointer focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-300">
                 <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest shrink-0">GROUP</span>
                 <select
                   value={selectedGroup}
                   onChange={(e) => {
                     setSelectedGroup(e.target.value);
                     setOverallPage(1);
                     setEmployeePage(1);
                     setQuestionPage(1);
                     setRoleQuestionPage(1);
                     setSelectedPedagogy(null);
                   }}
                   className="appearance-none bg-transparent border-0 text-slate-800 py-0 pl-0 pr-6 text-[11px] sm:text-xs font-extrabold tracking-wide cursor-pointer focus:outline-none focus:ring-0 w-full h-full truncate max-w-[130px] sm:max-w-[180px]"
                 >
                   <option value="All Groups">All Groups</option>
                   {availableGroups.map((grp, i) => (
                     <option key={i} value={grp}>{grp}</option>
                   ))}
                 </select>
                 <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
                   <FiChevronDown className="text-[14px]" />
                 </div>
              </div>
              )}

              <button
                onClick={() => {
                  if (showQuestionPerf) {
                    setShowQuestionPerf(false);
                    setSelectedQuestion(null);
                    setSelectedOption(null);
                  } else {
                    onBack();
                  }
                }}
                className="btn-assessment btn-assessment-danger hidden lg:inline-flex items-center gap-1.5 cursor-pointer h-[32px] sm:h-[36px] py-0 px-3.5 sm:px-4.5 justify-center shrink-0"
              >
                <FiArrowLeft className="text-xs sm:text-sm shrink-0" />
                <span>Back</span>
              </button>
            </div>
          </div>

          {/* TABS */}
          <div className="flex items-center gap-6 mt-2 px-1 overflow-x-auto">
            <button 
              onClick={() => { setShowQuestionPerf(false); setShowGroupCompare(false); }}
              className={`flex items-center gap-2 pb-3 border-b-2 transition-colors shrink-0 ${!showQuestionPerf && !showGroupCompare ? 'border-[#B91C1C] text-[#B91C1C]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
               <i className="bi bi-bar-chart-fill text-lg"></i>
               <div className="flex flex-col items-start">
                  <span className="text-sm font-bold">Overview</span>
                  <span className="text-[9px] font-semibold text-slate-400">Results & role breakdown</span>
               </div>
            </button>
            <button 
              onClick={() => { setShowQuestionPerf(true); setShowGroupCompare(false); }}
              className={`flex items-center gap-2 pb-3 border-b-2 transition-colors shrink-0 ${showQuestionPerf ? 'border-[#B91C1C] text-[#B91C1C]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
               <i className="bi bi-clipboard-data-fill text-lg"></i>
               <div className="flex flex-col items-start">
                  <span className="text-sm font-bold">Question Analysis</span>
                  <span className="text-[9px] font-semibold text-slate-400">Questions & pedagogy</span>
               </div>
            </button>
            <button 
              onClick={() => { setShowGroupCompare(true); setShowQuestionPerf(false); }}
              className={`flex items-center gap-2 pb-3 border-b-2 transition-colors shrink-0 ${showGroupCompare ? 'border-[#B91C1C] text-[#B91C1C]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            >
               <i className="bi bi-people-fill text-lg"></i>
               <div className="flex flex-col items-start">
                  <span className="text-sm font-bold">Group Compare</span>
                  <span className="text-[9px] font-semibold text-slate-400">Group and Individual</span>
               </div>
            </button>
          </div>
        </div>

        {/* GROUP COMPARE CONTENT */}
        {showGroupCompare && (
          <div className="mt-6 px-1">
            {/* Sub-tabs: Group vs Individual */}
            <div className="flex items-center gap-1 mb-5 bg-slate-100 rounded-xl p-1 w-fit">
              <button
                onClick={() => setCompareMode('group')}
                className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${
                  compareMode === 'group'
                    ? 'bg-white text-[#B91C1C] shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <i className="bi bi-people-fill mr-1.5"></i>Group
              </button>
              <button
                onClick={() => setCompareMode('individual')}
                className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${
                  compareMode === 'individual'
                    ? 'bg-white text-[#B91C1C] shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                <i className="bi bi-person-fill mr-1.5"></i>Individual
              </button>
            </div>

            {/* === GROUP COMPARE MODE === */}
            {compareMode === 'group' && (
            <div>
            {/* Controls Row */}
            <div className="flex flex-wrap items-start gap-4 mb-5">
              {/* Select Assessments (Multi-select dropdown) */}
              <div className="flex flex-col gap-1.5 min-w-[240px]" ref={compareAssessmentDropdownRef}>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Select Tests</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setCompareAssessmentDropdownOpen(prev => !prev)}
                    className="w-full flex items-center justify-between bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm font-bold text-slate-700 shadow-sm hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all cursor-pointer text-left"
                  >
                    <span className={selectedCompareAssessments.length === 0 ? 'text-slate-400 font-semibold' : ''}>
                      {selectedCompareAssessments.length === 0
                        ? 'All Tests'
                        : `${selectedCompareAssessments.length} test${selectedCompareAssessments.length > 1 ? 's' : ''} selected`}
                    </span>
                    <FiChevronDown className={`text-sm text-slate-400 transition-transform ${compareAssessmentDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Panel */}
                  {compareAssessmentDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-[300px] overflow-hidden flex flex-col">
                      {/* Search */}
                      <div className="p-2.5 border-b border-slate-100">
                        <div className="relative">
                          <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                          <input
                            type="text"
                            value={compareAssessmentSearch}
                            onChange={e => setCompareAssessmentSearch(e.target.value)}
                            placeholder="Search test..."
                            className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-lg text-xs font-semibold text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-300"
                          />
                        </div>
                      </div>

                      {/* Select All / Clear All */}
                      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-50">
                        <label className="flex items-center gap-2 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={compareAvailableAssessments.length > 0 && compareAvailableAssessments.every(t => selectedCompareAssessments.includes(t))}
                            onChange={e => {
                              if (e.target.checked) {
                                setSelectedCompareAssessments([...compareAvailableAssessments]);
                              } else {
                                setSelectedCompareAssessments([]);
                                setSelectedCompareGroups([]);
                              }
                            }}
                            className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                          />
                          <span className="text-[11px] font-bold text-slate-600 group-hover:text-slate-800">Select All</span>
                        </label>
                        <button
                          onClick={() => { setSelectedCompareAssessments([]); setSelectedCompareGroups([]); }}
                          className="text-[11px] font-bold text-indigo-500 hover:text-indigo-700 transition-colors"
                        >
                          Clear All
                        </button>
                      </div>

                      {/* Assessments List */}
                      <div className="overflow-y-auto flex-1 py-1">
                        {compareAvailableAssessments
                          .filter(t => !compareAssessmentSearch || t.toLowerCase().includes(compareAssessmentSearch.toLowerCase()))
                          .map(t => (
                            <label
                              key={t}
                              className="flex items-center gap-2.5 px-3 py-2 hover:bg-indigo-50/50 cursor-pointer transition-colors group"
                            >
                              <input
                                type="checkbox"
                                checked={selectedCompareAssessments.includes(t)}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setSelectedCompareAssessments(prev => [...prev, t]);
                                  } else {
                                    setSelectedCompareAssessments(prev => prev.filter(item => item !== t));
                                    // Remove any groups selected from this assessment
                                    setSelectedCompareGroups(prev => prev.filter(k => !k.startsWith(`${t}::`)));
                                  }
                                }}
                                className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer shrink-0"
                              />
                              <span className="text-[12px] font-bold text-slate-700 group-hover:text-slate-900 truncate">{t}</span>
                            </label>
                          ))}
                        {compareAvailableAssessments.filter(t => !compareAssessmentSearch || t.toLowerCase().includes(compareAssessmentSearch.toLowerCase())).length === 0 && (
                          <div className="px-4 py-6 text-center text-xs font-bold text-slate-400 italic">No tests found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Select Groups (Multi-select dropdown) */}
              <div className="flex flex-col gap-1.5 min-w-[260px]" ref={compareGroupDropdownRef}>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Select Groups</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setCompareGroupDropdownOpen(prev => !prev)}
                    className="w-full flex items-center justify-between bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm font-bold text-slate-700 shadow-sm hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all cursor-pointer text-left"
                  >
                    <span className={selectedCompareGroups.length === 0 ? 'text-slate-400 font-semibold' : ''}>
                      {selectedCompareGroups.length === 0
                        ? 'Select groups...'
                        : `${selectedCompareGroups.length} group${selectedCompareGroups.length > 1 ? 's' : ''} selected`}
                    </span>
                    <FiChevronDown className={`text-sm text-slate-400 transition-transform ${compareGroupDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Panel */}
                  {compareGroupDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-[340px] overflow-hidden flex flex-col">
                      {/* Search */}
                      <div className="p-2.5 border-b border-slate-100">
                        <div className="relative">
                          <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                          <input
                            type="text"
                            value={compareGroupSearch}
                            onChange={e => setCompareGroupSearch(e.target.value)}
                            placeholder="Search group..."
                            className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-lg text-xs font-semibold text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-300"
                          />
                        </div>
                      </div>

                      {/* Select All / Clear All */}
                      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-50">
                        <label className="flex items-center gap-2 cursor-pointer group">
                          <input
                            type="checkbox"
                            checked={(() => {
                              const allKeys = compareGroupsByAssessment
                                .filter(a => selectedCompareAssessments.length === 0 || selectedCompareAssessments.includes(a.assessment))
                                .flatMap(a => a.groups.map(g => g.key));
                              return allKeys.length > 0 && allKeys.every(k => selectedCompareGroups.includes(k));
                            })()}
                            onChange={e => {
                              const allKeys = compareGroupsByAssessment
                                .filter(a => selectedCompareAssessments.length === 0 || selectedCompareAssessments.includes(a.assessment))
                                .flatMap(a => a.groups.map(g => g.key));
                              if (e.target.checked) {
                                setSelectedCompareGroups(prev => [...new Set([...prev, ...allKeys])]);
                              } else {
                                setSelectedCompareGroups(prev => prev.filter(k => !allKeys.includes(k)));
                              }
                            }}
                            className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                          />
                          <span className="text-[11px] font-bold text-slate-600 group-hover:text-slate-800">Select All</span>
                        </label>
                        <button
                          onClick={() => setSelectedCompareGroups([])}
                          className="text-[11px] font-bold text-indigo-500 hover:text-indigo-700 transition-colors"
                        >
                          Clear All
                        </button>
                      </div>

                      {/* Groups list organized by assessment */}
                      <div className="overflow-y-auto flex-1 py-1">
                        {compareGroupsByAssessment
                          .filter(a => selectedCompareAssessments.length === 0 || selectedCompareAssessments.includes(a.assessment))
                          .map(assessmentGroup => {
                            const filteredGroups = assessmentGroup.groups.filter(g =>
                              !compareGroupSearch || g.name.toLowerCase().includes(compareGroupSearch.toLowerCase())
                            );
                            if (filteredGroups.length === 0) return null;
                            return (
                              <div key={assessmentGroup.assessment}>
                                <div className="px-3 py-1.5 text-[10px] font-black text-slate-400 uppercase tracking-wider bg-slate-50/70 border-y border-slate-50 sticky top-0">
                                  {assessmentGroup.assessment}
                                </div>
                                {filteredGroups.map(group => (
                                  <label
                                    key={group.key}
                                    className="flex items-center gap-2.5 px-3 py-2 hover:bg-indigo-50/50 cursor-pointer transition-colors group"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={selectedCompareGroups.includes(group.key)}
                                      onChange={e => {
                                        if (e.target.checked) {
                                          setSelectedCompareGroups(prev => [...prev, group.key]);
                                        } else {
                                          setSelectedCompareGroups(prev => prev.filter(k => k !== group.key));
                                        }
                                      }}
                                      className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer shrink-0"
                                    />
                                    <div className="flex items-center gap-1.5 min-w-0">
                                      <span className="text-[12px] font-bold text-slate-700 group-hover:text-slate-900 truncate">{group.name}</span>
                                      <span className="text-[10px] font-semibold text-slate-400 shrink-0">
                                        ({group.candidateCount} Candidates - {group.avgScore}%)
                                      </span>
                                    </div>
                                  </label>
                                ))}
                              </div>
                            );
                          })}
                        {compareGroupsByAssessment
                          .filter(a => selectedCompareAssessments.length === 0 || selectedCompareAssessments.includes(a.assessment))
                          .flatMap(a => a.groups)
                          .filter(g => !compareGroupSearch || g.name.toLowerCase().includes(compareGroupSearch.toLowerCase()))
                          .length === 0 && (
                          <div className="px-4 py-6 text-center text-xs font-bold text-slate-400 italic">No groups found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>



              {/* Clear all */}
              {(selectedCompareGroups.length > 0 || selectedCompareAssessments.length > 0) && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] font-black text-transparent uppercase tracking-widest select-none">.</label>
                  <button
                    onClick={() => { setSelectedCompareGroups([]); setSelectedCompareAssessments([]); }}
                    className="px-3 py-2.5 text-xs font-bold text-slate-500 hover:text-rose-600 transition-colors"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* Selected Groups Chips */}
            {selectedCompareGroups.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-5">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mr-1">Selected Groups:</span>
                {selectedCompareGroups.map(groupKey => {
                  const [, groupName] = groupKey.split('::');
                  return (
                    <span
                      key={groupKey}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-[11px] font-bold border border-indigo-100 shadow-sm"
                    >
                      {groupName}
                      <button
                        onClick={() => setSelectedCompareGroups(prev => prev.filter(k => k !== groupKey))}
                        className="hover:text-rose-600 transition-colors ml-0.5"
                      >
                        <FiX className="text-xs" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {/* Comparison Chart */}
            {comparisonData.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                <div className="flex items-center justify-between border-b border-slate-50 pb-4 mb-4">
                  <h3 className="text-sm font-black text-slate-800">Group Performance Comparison</h3>
                  <span className="text-[10px] font-bold text-slate-400">{comparisonData.length} groups compared</span>
                </div>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={comparisonData} margin={{ top: 20, right: 30, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis
                      dataKey="label"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 11, fill: '#64748b', fontWeight: 600 }}
                      interval={0}
                      angle={-25}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      domain={[0, 100]}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: '#64748b' }}
                      dx={-10}
                      tickFormatter={v => `${v}%`}
                    />
                    <Tooltip
                      cursor={{ fill: '#f8fafc' }}
                      formatter={(value, name) => [`${value}%`, name]}
                      contentStyle={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
                    />
                    <Legend wrapperStyle={{ paddingTop: '20px' }} />
                    <Bar
                      dataKey="avgScore"
                      name="Avg Score"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={56}
                    >
                      {comparisonData.map((entry, index) => {
                        const colors = ['#6366f1', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
                        return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>

                {/* Summary Table */}
                <div className="mt-6 overflow-x-auto max-h-[250px] overflow-y-auto">
                  <table className="w-full text-left border-collapse">
                    <thead className="sticky top-0 z-10 bg-slate-50">
                      <tr className="bg-slate-50">
                        <th onClick={() => handleGroupTableSort('group')} className="py-2.5 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors select-none">
                          <div className="flex items-center gap-1">Group {renderGroupSortIcon('group')}</div>
                        </th>
                        <th onClick={() => handleGroupTableSort('assessment')} className="py-2.5 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors select-none">
                          <div className="flex items-center gap-1">Test {renderGroupSortIcon('assessment')}</div>
                        </th>
                        <th onClick={() => handleGroupTableSort('candidates')} className="py-2.5 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center cursor-pointer hover:bg-slate-100 transition-colors select-none">
                          <div className="flex items-center justify-center gap-1">Candidates {renderGroupSortIcon('candidates')}</div>
                        </th>
                        <th onClick={() => handleGroupTableSort('score')} className="py-2.5 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center cursor-pointer hover:bg-slate-100 transition-colors select-none">
                          <div className="flex items-center justify-center gap-1">Avg Score {renderGroupSortIcon('score')}</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonData.slice().sort((a, b) => {
                        let aVal = '', bVal = '';
                        if (groupTableSortColumn === 'group') {
                          aVal = (a.group || '').toLowerCase();
                          bVal = (b.group || '').toLowerCase();
                        } else if (groupTableSortColumn === 'assessment') {
                          aVal = (a.assessment || '').toLowerCase();
                          bVal = (b.assessment || '').toLowerCase();
                        } else if (groupTableSortColumn === 'candidates') {
                          aVal = a.candidateCount || 0;
                          bVal = b.candidateCount || 0;
                        } else if (groupTableSortColumn === 'score') {
                          aVal = a.avgScore || 0;
                          bVal = b.avgScore || 0;
                        }
                        if (aVal < bVal) return groupTableSortDirection === 'asc' ? -1 : 1;
                        if (aVal > bVal) return groupTableSortDirection === 'asc' ? 1 : -1;
                        return 0;
                      }).map((row, i) => (
                        <tr key={i} className="border-b border-slate-50/80 hover:bg-slate-50/50 transition-colors">
                          <td className="py-2.5 px-4 text-[12px] font-black text-slate-700">{row.group}</td>
                          <td className="py-2.5 px-4 text-[11px] font-semibold text-slate-500">{row.assessment}</td>
                          <td className="py-2.5 px-4 text-[12px] font-bold text-slate-600 text-center">{row.candidateCount}</td>
                          <td className="py-2.5 px-4 text-center">
                            <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-black ${
                              row.avgScore >= 75 ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                              row.avgScore >= (row.pass_percentage ?? 50) ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                              'bg-rose-50 text-rose-600 border border-rose-100'
                            }`}>
                              {row.avgScore}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Empty state */}
            {comparisonData.length === 0 && (
              <div className="bg-white border border-slate-200 rounded-2xl p-12 shadow-sm flex flex-col items-center justify-center gap-3">
                <div className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-400 flex items-center justify-center text-2xl">
                  <FiBarChart2 />
                </div>
                <p className="text-sm font-bold text-slate-500">Select at least 2 groups to compare</p>
                <p className="text-[11px] font-semibold text-slate-400">Use the dropdowns above to pick groups from any test</p>
              </div>
            )}
            </div>
            )}

            {/* === INDIVIDUAL COMPARE MODE === */}
            {compareMode === 'individual' && (
              <div>
                {/* Candidate Search Dropdowns */}
                <div className="flex flex-wrap items-start gap-4 mb-5">
                  {/* Select Assessments (Multi-select dropdown) */}
                  <div className="flex flex-col gap-1.5 min-w-[240px]" ref={indAssessmentDropdownRef}>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Select Tests</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setIndAssessmentDropdownOpen(prev => !prev)}
                        className="w-full flex items-center justify-between bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm font-bold text-slate-700 shadow-sm hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all cursor-pointer text-left"
                      >
                        <span className={indSelectedAssessments.length === 0 ? 'text-slate-400 font-semibold' : ''}>
                          {indSelectedAssessments.length === 0
                            ? 'All Tests'
                            : `${indSelectedAssessments.length} test${indSelectedAssessments.length > 1 ? 's' : ''} selected`}
                        </span>
                        <FiChevronDown className={`text-sm text-slate-400 transition-transform ${indAssessmentDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {indAssessmentDropdownOpen && (
                        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-[300px] overflow-hidden flex flex-col">
                          <div className="p-2.5 border-b border-slate-100">
                            <div className="relative">
                              <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                              <input
                                type="text"
                                value={indAssessmentSearch}
                                onChange={e => setIndAssessmentSearch(e.target.value)}
                                placeholder="Search test..."
                                className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-lg text-xs font-semibold text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-300"
                              />
                            </div>
                          </div>

                          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-50">
                            <label className="flex items-center gap-2 cursor-pointer group">
                              <input
                                type="checkbox"
                                checked={compareAvailableAssessments.length > 0 && compareAvailableAssessments.every(t => indSelectedAssessments.includes(t))}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setIndSelectedAssessments([...compareAvailableAssessments]);
                                  } else {
                                    setIndSelectedAssessments([]);
                                    setIndSelectedGroups([]);
                                  }
                                }}
                                className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                              />
                              <span className="text-[11px] font-bold text-slate-600 group-hover:text-slate-800">Select All</span>
                            </label>
                            <button
                              onClick={() => { setIndSelectedAssessments([]); setIndSelectedGroups([]); }}
                              className="text-[11px] font-bold text-indigo-500 hover:text-indigo-700 transition-colors"
                            >
                              Clear All
                            </button>
                          </div>

                          <div className="overflow-y-auto flex-1 py-1">
                            {compareAvailableAssessments
                              .filter(t => !indAssessmentSearch || t.toLowerCase().includes(indAssessmentSearch.toLowerCase()))
                              .map(t => (
                                <label
                                  key={t}
                                  className="flex items-center gap-2.5 px-3 py-2 hover:bg-indigo-50/50 cursor-pointer transition-colors group"
                                >
                                  <input
                                    type="checkbox"
                                    checked={indSelectedAssessments.includes(t)}
                                    onChange={e => {
                                      if (e.target.checked) {
                                        setIndSelectedAssessments(prev => [...prev, t]);
                                      } else {
                                        setIndSelectedAssessments(prev => prev.filter(item => item !== t));
                                        setIndSelectedGroups(prev => prev.filter(k => !k.startsWith(`${t}::`)));
                                      }
                                    }}
                                    className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer shrink-0"
                                  />
                                  <span className="text-[12px] font-bold text-slate-700 group-hover:text-slate-900 truncate">{t}</span>
                                </label>
                              ))}
                            {compareAvailableAssessments.filter(t => !indAssessmentSearch || t.toLowerCase().includes(indAssessmentSearch.toLowerCase())).length === 0 && (
                              <div className="px-4 py-6 text-center text-xs font-bold text-slate-400 italic">No tests found</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Select Groups (Multi-select dropdown) */}
                  <div className="flex flex-col gap-1.5 min-w-[260px]" ref={indGroupDropdownRef}>
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Select Groups</label>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setIndGroupDropdownOpen(prev => !prev)}
                        className="w-full flex items-center justify-between bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm font-bold text-slate-700 shadow-sm hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all cursor-pointer text-left"
                      >
                        <span className={indSelectedGroups.length === 0 ? 'text-slate-400 font-semibold' : ''}>
                          {indSelectedGroups.length === 0
                            ? 'Select groups...'
                            : `${indSelectedGroups.length} group${indSelectedGroups.length > 1 ? 's' : ''} selected`}
                        </span>
                        <FiChevronDown className={`text-sm text-slate-400 transition-transform ${indGroupDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {indGroupDropdownOpen && (
                        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl z-50 max-h-[340px] overflow-hidden flex flex-col">
                          <div className="p-2.5 border-b border-slate-100">
                            <div className="relative">
                              <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                              <input
                                type="text"
                                value={indGroupSearch}
                                onChange={e => setIndGroupSearch(e.target.value)}
                                placeholder="Search group..."
                                className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-100 rounded-lg text-xs font-semibold text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-300"
                              />
                            </div>
                          </div>

                          <div className="flex items-center justify-between px-3 py-2 border-b border-slate-50">
                            <label className="flex items-center gap-2 cursor-pointer group">
                              <input
                                type="checkbox"
                                checked={(() => {
                                  const allKeys = compareGroupsByAssessment
                                    .filter(a => indSelectedAssessments.length === 0 || indSelectedAssessments.includes(a.assessment))
                                    .flatMap(a => a.groups.filter(g => !g.key.endsWith('::OVERALL_TEST_MEAN')).map(g => g.key));
                                  return allKeys.length > 0 && allKeys.every(k => indSelectedGroups.includes(k));
                                })()}
                                onChange={e => {
                                  const allKeys = compareGroupsByAssessment
                                    .filter(a => indSelectedAssessments.length === 0 || indSelectedAssessments.includes(a.assessment))
                                    .flatMap(a => a.groups.filter(g => !g.key.endsWith('::OVERALL_TEST_MEAN')).map(g => g.key));
                                  if (e.target.checked) {
                                    setIndSelectedGroups(prev => [...new Set([...prev, ...allKeys])]);
                                  } else {
                                    setIndSelectedGroups(prev => prev.filter(k => !allKeys.includes(k)));
                                  }
                                }}
                                className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                              />
                              <span className="text-[11px] font-bold text-slate-600 group-hover:text-slate-800">Select All</span>
                            </label>
                            <button
                              onClick={() => setIndSelectedGroups([])}
                              className="text-[11px] font-bold text-indigo-500 hover:text-indigo-700 transition-colors"
                            >
                              Clear All
                            </button>
                          </div>

                          <div className="overflow-y-auto flex-1 py-1">
                            {compareGroupsByAssessment
                              .filter(a => indSelectedAssessments.length === 0 || indSelectedAssessments.includes(a.assessment))
                              .map(assessmentGroup => {
                                const filteredGroups = assessmentGroup.groups.filter(g =>
                                  !g.key.endsWith('::OVERALL_TEST_MEAN') &&
                                  (!indGroupSearch || g.name.toLowerCase().includes(indGroupSearch.toLowerCase()))
                                );
                                if (filteredGroups.length === 0) return null;
                                return (
                                  <div key={assessmentGroup.assessment}>
                                    <div className="px-3 py-1.5 text-[10px] font-black text-slate-400 uppercase tracking-wider bg-slate-50/70 border-y border-slate-50 sticky top-0">
                                      {assessmentGroup.assessment}
                                    </div>
                                    {filteredGroups.map(group => (
                                      <label
                                        key={group.key}
                                        className="flex items-center gap-2.5 px-3 py-2 hover:bg-indigo-50/50 cursor-pointer transition-colors group"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={indSelectedGroups.includes(group.key)}
                                          onChange={e => {
                                            if (e.target.checked) {
                                              setIndSelectedGroups(prev => [...prev, group.key]);
                                            } else {
                                              setIndSelectedGroups(prev => prev.filter(k => k !== group.key));
                                            }
                                          }}
                                          className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer shrink-0"
                                        />
                                        <div className="flex items-center gap-1.5 min-w-0">
                                          <span className="text-[12px] font-bold text-slate-700 group-hover:text-slate-900 truncate">{group.name}</span>
                                          <span className="text-[10px] font-semibold text-slate-400 shrink-0">
                                            ({group.candidateCount} Candidates)
                                          </span>
                                        </div>
                                      </label>
                                    ))}
                                  </div>
                                );
                              })}
                            {compareGroupsByAssessment
                              .filter(a => indSelectedAssessments.length === 0 || indSelectedAssessments.includes(a.assessment))
                              .flatMap(a => a.groups)
                              .filter(g => !g.key.endsWith('::OVERALL_TEST_MEAN') && (!indGroupSearch || g.name.toLowerCase().includes(indGroupSearch.toLowerCase())))
                              .length === 0 && (
                              <div className="px-4 py-6 text-center text-xs font-bold text-slate-400 italic">No groups found</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Search Candidate removed from dropdown row */}

                  {(indSelectedAssessments.length > 0 || indSelectedGroups.length > 0 || selectedIndividual) && (
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[10px] font-black text-transparent uppercase tracking-widest select-none">.</label>
                      <button
                        onClick={() => {
                          setIndSelectedAssessments([]);
                          setIndSelectedGroups([]);
                          setSelectedIndividual(null);
                        }}
                        className="px-3 py-2.5 text-xs font-bold text-slate-500 hover:text-rose-600 transition-colors"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>

                {/* Selected Groups Chips */}
                {indSelectedGroups.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mb-5">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mr-1">Selected Groups:</span>
                    {indSelectedGroups.map(groupKey => {
                      const [, groupName] = groupKey.split('::');
                      return (
                        <span
                          key={groupKey}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-[11px] font-bold border border-indigo-100 shadow-sm"
                        >
                          {groupName}
                          <button
                            onClick={() => setIndSelectedGroups(prev => prev.filter(k => k !== groupKey))}
                            className="hover:text-rose-600 transition-colors ml-0.5"
                          >
                            <FiX className="text-xs" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* 2-Column Grid Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
                  
                  {/* Left Column: Candidates Sidebar List */}
                  <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-col gap-3 lg:col-span-1">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                      <h4 className="text-xs font-black text-slate-700 uppercase tracking-wide">
                        Candidates ({compareUniqueCandidates.length})
                      </h4>
                      {selectedIndividual && (
                        <button 
                          onClick={() => setSelectedIndividual(null)}
                          className="text-[10px] font-extrabold text-rose-600 hover:text-[#B91C1C] transition-colors uppercase"
                        >
                          Deselect
                        </button>
                      )}
                    </div>
                    
                    {/* Sidebar Search Input */}
                    <div className="relative">
                      <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                      <input
                        type="text"
                        value={individualSearch}
                        onChange={e => setIndividualSearch(e.target.value)}
                        placeholder="Search candidate..."
                        className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition-all"
                      />
                    </div>
                    
                    {/* Candidates Scrollable List */}
                    <div className="overflow-y-auto flex flex-col gap-1.5 pr-1 max-h-[500px]">
                      {compareUniqueCandidates
                        .filter(c =>
                          !individualSearch ||
                          c.name.toLowerCase().includes(individualSearch.toLowerCase()) ||
                          (c.emp_id && c.emp_id.toLowerCase().includes(individualSearch.toLowerCase()))
                        )
                        .map(c => {
                          const isSelected = selectedIndividual?.user_id === c.user_id;
                          return (
                            <button
                              key={c.user_id}
                              onClick={() => setSelectedIndividual(isSelected ? null : c)}
                              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border transition-all text-left group ${
                                isSelected 
                                  ? 'bg-[#B91C1C]/5 border-[#B91C1C] shadow-sm' 
                                  : 'bg-white border-slate-100 hover:border-slate-200 hover:bg-slate-50/50'
                              }`}
                            >
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shrink-0 ${
                                isSelected ? 'bg-[#B91C1C] text-white' : 'bg-slate-100 text-slate-500'
                              }`}>
                                <FiUser />
                              </div>
                              <div className="flex flex-col min-w-0">
                                <span className={`text-[11px] font-black truncate ${isSelected ? 'text-[#B91C1C]' : 'text-slate-700'}`}>{c.name}</span>
                                <span className="text-[9px] font-semibold text-slate-400 truncate">
                                  {c.role || 'No role'}
                                </span>
                              </div>
                            </button>
                          );
                        })}
                      {compareUniqueCandidates.filter(c =>
                        !individualSearch ||
                        c.name.toLowerCase().includes(individualSearch.toLowerCase()) ||
                        (c.emp_id && c.emp_id.toLowerCase().includes(individualSearch.toLowerCase()))
                      ).length === 0 && (
                        <div className="py-8 text-center text-xs font-bold text-slate-400 italic">No candidates found</div>
                      )}
                    </div>
                  </div>

                  {/* Right Column: Chart / Details content */}
                  <div className="lg:col-span-3 flex flex-col gap-6">
                    {/* Selected Candidate Info */}
                    {selectedIndividual && (
                      <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl p-4 shadow-sm px-4">
                        <div className="w-10 h-10 rounded-full bg-[#B91C1C]/10 text-[#B91C1C] flex items-center justify-center text-lg font-black shrink-0">
                          {selectedIndividual.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-black text-slate-800">{selectedIndividual.name}</p>
                          <p className="text-[11px] font-semibold text-slate-400">
                            {selectedIndividual.role || 'No role'} Â· {individualComparisonData.length} test{individualComparisonData.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Individual Chart */}
                    {individualComparisonData.length > 0 && (
                      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                        <div className="flex items-center justify-between border-b border-slate-50 pb-4 mb-4">
                          <h3 className="text-sm font-black text-slate-800">Performance Across Tests</h3>
                        </div>
                        <div id="individualCompareChartDiv" style={{ width: '100%', height: '350px' }}></div>

                        {/* Detail Table */}
                        <div className="mt-6 overflow-x-auto max-h-[250px] overflow-y-auto">
                          <table className="w-full text-left border-collapse">
                            <thead className="sticky top-0 z-10 bg-slate-50">
                              <tr className="bg-slate-50">
                                <th onClick={() => handleIndTableSort('assessment')} className="py-2.5 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors select-none">
                                  <div className="flex items-center gap-1">Test {renderIndSortIcon('assessment')}</div>
                                </th>
                                <th onClick={() => handleIndTableSort('group')} className="py-2.5 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100 transition-colors select-none">
                                  <div className="flex items-center gap-1">Group {renderIndSortIcon('group')}</div>
                                </th>
                                <th onClick={() => handleIndTableSort('marks')} className="py-2.5 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center cursor-pointer hover:bg-slate-100 transition-colors select-none">
                                  <div className="flex items-center justify-center gap-1">Marks {renderIndSortIcon('marks')}</div>
                                </th>
                                <th onClick={() => handleIndTableSort('score')} className="py-2.5 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center cursor-pointer hover:bg-slate-100 transition-colors select-none">
                                  <div className="flex items-center justify-center gap-1">Score {renderIndSortIcon('score')}</div>
                                </th>
                                <th onClick={() => handleIndTableSort('status')} className="py-2.5 px-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center cursor-pointer hover:bg-slate-100 transition-colors select-none">
                                  <div className="flex items-center justify-center gap-1">Status {renderIndSortIcon('status')}</div>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {individualComparisonData.slice().sort((a, b) => {
                                let aVal = '', bVal = '';
                                if (indTableSortColumn === 'assessment') {
                                  aVal = (a.assessment || '').toLowerCase();
                                  bVal = (b.assessment || '').toLowerCase();
                                } else if (indTableSortColumn === 'group') {
                                  aVal = (a.group || '').toLowerCase();
                                  bVal = (b.group || '').toLowerCase();
                                } else if (indTableSortColumn === 'marks') {
                                  aVal = a.mcq_score || 0;
                                  bVal = b.mcq_score || 0;
                                } else if (indTableSortColumn === 'score' || indTableSortColumn === 'status') {
                                  aVal = a.score || 0;
                                  bVal = b.score || 0;
                                }
                                if (aVal < bVal) return indTableSortDirection === 'asc' ? -1 : 1;
                                if (aVal > bVal) return indTableSortDirection === 'asc' ? 1 : -1;
                                return 0;
                              }).map((row, i) => (
                                <tr key={i} className="border-b border-slate-50/80 hover:bg-slate-50/50 transition-colors">
                                  <td className="py-2.5 px-4 text-[12px] font-black text-slate-700">{row.assessment}</td>
                                  <td className="py-2.5 px-4 text-[11px] font-semibold text-slate-500">{row.group}</td>
                                  <td className="py-2.5 px-4 text-[12px] font-bold text-slate-600 text-center">{row.mcq_score}/{row.total_marks}</td>
                                  <td className="py-2.5 px-4 text-center">
                                    <span className={`inline-flex px-2.5 py-1 rounded-full text-[11px] font-black ${
                                      row.score >= 75 ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                                      row.score >= (row.pass_percentage ?? 50) ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                                      'bg-rose-50 text-rose-600 border border-rose-100'
                                    }`}>
                                      {row.score}%
                                    </span>
                                  </td>
                                  <td className="py-2.5 px-4 text-center">
                                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wide ${RESULT_STATUS_BADGE_CLASS[row.status] || RESULT_STATUS_BADGE_CLASS.Low}`}>
                                      {row.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Empty state when no candidate is selected */}
                    {!selectedIndividual && (
                      <div className="bg-white border border-slate-200 rounded-2xl p-16 shadow-sm flex flex-col items-center justify-center gap-3 min-h-[400px]">
                        <div className="w-14 h-14 rounded-2xl bg-[#B91C1C]/5 text-[#B91C1C] flex items-center justify-center text-2xl shadow-sm border border-[#B91C1C]/10">
                          <FiUser />
                        </div>
                        <h3 className="text-sm font-black text-slate-800 mt-2">No Candidate Selected</h3>
                        <p className="text-xs font-bold text-slate-400 max-w-sm text-center leading-relaxed">
                          Please select a candidate from the left sidebar to view their comprehensive performance breakdown and test timeline.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

          </div>
        )}


        {/* OVERVIEW CONTENT */}
        {!showQuestionPerf && !showGroupCompare && (
          <div className="flex flex-col gap-6 mt-4">


             {/* 4 Summary Cards */}
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Card 1 */}
                <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4 shadow-sm">
                   <div className="w-11 h-11 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-lg shrink-0">
                      <FiUsers />
                   </div>
                   <div className="flex flex-col">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">CANDIDATES</span>
                      <span className="text-xl font-black text-slate-800 leading-tight">{candidatesList.length}</span>
                   </div>
                </div>
                {/* Card 2 */}
                <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4 shadow-sm">
                   <div className="w-11 h-11 rounded-xl bg-sky-50 text-sky-500 flex items-center justify-center text-lg shrink-0">
                      <i className="bi bi-bar-chart-fill" />
                   </div>
                   <div className="flex flex-col">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">AVG SCORE</span>
                      <span className="text-xl font-black text-slate-800 leading-tight">{avgScore}%</span>
                   </div>
                </div>
                {/* Card 3 */}
                <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4 shadow-sm">
                   <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-500 flex items-center justify-center text-lg shrink-0">
                      <FiAward />
                   </div>
                   <div className="flex flex-col">
                      <div className="flex justify-between w-full">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">PASS RATE</span>
                      </div>
                      <span className="text-xl font-black text-slate-800 leading-tight">{passRate}%</span>
                      <span className="text-[10px] font-bold text-slate-400 mt-0.5">{passedCount} candidates</span>
                   </div>
                </div>
                {/* Card 4 */}
                <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center gap-4 shadow-sm">
                   <div className="w-11 h-11 rounded-xl bg-orange-50 text-orange-500 flex items-center justify-center text-lg shrink-0">
                      <FiAlertTriangle />
                   </div>
                   <div className="flex flex-col">
                      <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">NEED ATTENTION</span>
                      <span className="text-xl font-black text-slate-800 leading-tight">{needsWorkCount}</span>
                      <span className="text-[10px] font-bold text-slate-400 mt-0.5">Below pass mark</span>
                   </div>
                </div>
             </div>

              {/* Charts Row */}
              <div className="flex flex-col lg:flex-row gap-6">
                  {/* Overall Result Analysis Bar Chart */}
                  <div className="flex-1 min-w-0 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                      <div className="flex items-center justify-between border-b border-slate-50 pb-4 mb-4">
                         <div className="flex flex-col gap-0.5">
                           <h3 className="text-sm font-black text-slate-800">Overall Score Distribution</h3>
                           <p className="text-[9px] font-bold text-slate-400 flex items-center gap-1">
                             <FiMousePointer size={8} className="shrink-0" />
                             Click a bar to filter the candidates table below
                           </p>
                         </div>
                      </div>
                      <div className="w-full overflow-x-auto overflow-y-hidden custom-scrollbar">
                          <div id="overallChartDiv" className="h-[250px] sm:h-[300px] w-full"></div>
                      </div>
                  </div>

                  {/* Roles Chart */}
                  <div className="flex-1 min-w-0 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex flex-col">
                     <div className="flex items-center justify-between border-b border-slate-50 pb-4 mb-4">
                        <div className="flex flex-col gap-0.5">
                           <h3 className="text-sm font-black text-slate-800">Rolewise Score Distribution</h3>
                           <p className="text-[9px] font-bold text-slate-400 flex items-center gap-1">
                             <FiMousePointer size={8} className="shrink-0" />
                             Click a slice to filter the candidates table below
                           </p>
                        </div>
                     </div>
                     <div id="roleAnalysisChartDiv" className="w-full flex-1 min-h-[250px] sm:min-h-[300px]"></div>
                  </div>

                  {/* Missed Questions Chart */}
                  <div className="flex-1 min-w-0 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex flex-col">
                     <div className="flex items-center justify-between border-b border-slate-50 pb-4 mb-4">
                        <div className="flex flex-col gap-0.5">
                           <h3 className="text-sm font-black text-slate-800">Rolewise Missed Questions</h3>
                           <p className="text-[9px] font-bold text-slate-400 flex items-center gap-1">
                             <FiMousePointer size={8} className="shrink-0" />
                             Click a slice to filter the candidates table below
                           </p>
                        </div>
                     </div>
                     {hasMissedQuestions ? (
                       <div id="missedChartDiv" className="w-full flex-1 min-h-[250px] sm:min-h-[300px]"></div>
                     ) : (
                       <div className="w-full flex-1 flex flex-col items-center justify-center min-h-[250px] sm:min-h-[300px] text-sm font-semibold text-slate-400">
                         No missed questions found
                       </div>
                     )}
                  </div>
              </div>

             {/* Candidates Table (Full Width) */}
             <div id="candidate-details-section" className="bg-white border border-slate-200 rounded-2xl p-0 shadow-sm overflow-hidden flex flex-col">
                   <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-white flex-wrap gap-4">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm font-black text-slate-800">Candidates List</h3>
                          {selectedRole && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border bg-purple-50 text-purple-700 border-purple-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                              Role: {selectedRole.role}
                              <button onClick={() => { setSelectedRole(null); setActiveRoleChart(null); setShowMissedColumn(false); setOverallPage(1); }} className="ml-0.5 opacity-60 hover:opacity-100 cursor-pointer border-0 bg-transparent p-0 leading-none">
                                <FiX size={9} />
                              </button>
                            </span>
                          )}
                          {overviewFilter !== 'All' && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider border ${
                              overviewFilter === 'High' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                              overviewFilter === 'Average' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                              'bg-rose-50 text-rose-700 border-rose-200'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${
                                overviewFilter === 'High' ? 'bg-emerald-500' :
                                overviewFilter === 'Average' ? 'bg-blue-500' : 'bg-rose-500'
                              }`} />
                              {overviewFilter}
                              <button onClick={() => { setOverviewFilter('All'); setOverallPage(1); }} className="ml-0.5 opacity-60 hover:opacity-100 cursor-pointer">
                                <FiX size={9} />
                              </button>
                            </span>
                          )}
                        </div>
                        {showMissedColumn && (
                          <p className="text-[10px] text-slate-400 font-bold">
                            Tip: Click the badge in the <span className="text-[#B91C1C] font-extrabold">MISSED</span> column to see their unanswered/wrong questions.
                          </p>
                        )}
                      </div>
                       <div className="flex items-center gap-4 flex-wrap flex-1 sm:flex-initial sm:justify-end">
                          {/* Rows selector and Search container */}
                          <div className="flex items-center gap-3">
                             <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">ROWS</span>
                             <div className="flex items-center justify-center bg-white border border-slate-200/80 rounded-2xl px-3.5 py-1 text-xs shadow-sm">
                                <select 
                                  value={itemsPerPage === 999999 ? 'All' : itemsPerPage} 
                                  onChange={(e) => { 
                                    setItemsPerPage(e.target.value === 'All' ? 999999 : Number(e.target.value)); 
                                    setOverallPage(1); 
                                  }}
                                  className="bg-transparent border-0 focus:ring-0 focus:outline-none font-bold text-slate-700 cursor-pointer text-[11px] py-0 m-0 text-center appearance-none"
                                  style={{
                                    minWidth: '24px',
                                    WebkitAppearance: 'none',
                                    MozAppearance: 'none',
                                    appearance: 'none',
                                    border: 'none',
                                    outline: 'none',
                                    boxShadow: 'none',
                                    padding: '0px'
                                  }}
                                >
                                  <option value="5">5</option>
                                  <option value="10">10</option>
                                  <option value="25">25</option>
                                  <option value="50">50</option>
                                  <option value="All">All</option>
                                </select>
                                <FiChevronDown className="text-slate-400 text-[10px] ml-0.5 pointer-events-none" />
                             </div>
                             
                             {/* Vertical divider line */}
                             <div className="h-6 w-px bg-slate-200" />

                             {/* Search Input box */}
                             <div className="relative flex items-center bg-white border border-slate-200/80 rounded-2xl px-3 py-1.5 shadow-sm focus-within:ring-2 focus-within:ring-red-500/20 focus-within:border-red-300 transition-all">
                                <i className="bi bi-search text-slate-400 text-[11px] mr-2 pointer-events-none"></i>
                                <input 
                                  type="text" 
                                  placeholder="Search candidates..." 
                                  value={overallSearch}
                                  onChange={(e) => {
                                    setOverallSearch(e.target.value);
                                    setOverallPage(1);
                                  }}
                                  className="bg-transparent border-none outline-none focus:ring-0 p-0 text-[11px] font-bold text-slate-700 placeholder:text-slate-400 w-[160px]"
                                />
                             </div>
                          </div>

                          <button
                            type="button"
                            className="btn-assessment assess-mod-leaderboard-export-btn"
                            onClick={() => setShowOverallExportModal(true)}
                          >
                            <i className="bi bi-file-earmark-spreadsheet-fill mr-1.5" aria-hidden />
                            Export
                          </button>
                       </div>
                    </div>
                                    <div className="overflow-x-auto flex-1 bg-white overflow-y-auto" style={{ maxHeight: itemsPerPage === 999999 ? '600px' : `${Math.min(itemsPerPage * 52 + 44, 600)}px` }}>
                      <table className="w-full text-left border-collapse">
                         <thead className="bg-slate-50 border-b border-slate-100 sticky top-0 z-10">
                            <tr>
                               <th onClick={() => handleSort('name')} className="py-3 px-4 sm:px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100 hover:text-slate-600 transition-colors select-none">NAME {renderSortIcon('name')}</th>
                               <th onClick={() => handleSort('role')} className="py-3 px-4 sm:px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100 hover:text-slate-600 transition-colors select-none">ROLE {renderSortIcon('role')}</th>
                               <th onClick={() => handleSort('score')} className="py-3 px-4 sm:px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100 hover:text-slate-600 transition-colors select-none">SCORE {renderSortIcon('score')}</th>
                               {showMissedColumn && <th onClick={() => handleSort('missed_total')} className="py-3 px-4 sm:px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center cursor-pointer hover:bg-slate-100 hover:text-slate-600 transition-colors select-none">MISSED {renderSortIcon('missed_total')}</th>}
                               <th className="py-3 px-4 sm:px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">WEAKEST TOPIC</th>
                               <th onClick={() => handleSort('score')} className="py-3 px-4 sm:px-6 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:bg-slate-100 hover:text-slate-600 transition-colors select-none">STATUS {renderSortIcon('score')}</th>
                            </tr>
                         </thead>
                          <tbody>
                             {displayCandidates.slice((overallPage - 1) * itemsPerPage, overallPage * itemsPerPage).map((cand, idx) => {
                               const pct = cand.overall_pct ?? cand.accuracy ?? 0;
                               const passPercentage = cand.pass_percentage ?? 50;
                               const resultStatus = getResultAnalysisStatus(pct, passPercentage);
                               const statusBadge = (
                                 <span className={`px-3 py-1 font-black text-[10px] rounded-full uppercase tracking-wide ${RESULT_STATUS_BADGE_CLASS[resultStatus]}`}>
                                   {resultStatus}
                                 </span>
                               );
                               
                               const candidateMissedQuestions = getCandidateMissedQs(cand, data?.questions_data);
                               const mcqMissed = candidateMissedQuestions.filter(q => q.is_mcq !== false).length;
                               const descMissed = candidateMissedQuestions.length - mcqMissed;

                               return (
                                  <tr key={idx} className={`border-b border-slate-50/80 hover:bg-slate-50/50 transition-colors cursor-pointer`} onClick={() => {
                                      if (activeRoleChart === 'missed' && candidateMissedQuestions.length > 0) {
                                          setCandidateMissedData({
                                              name: cand.name,
                                              user_id: cand.user_id,
                                              assessment_category: cand.assessment_category,
                                              questions: candidateMissedQuestions
                                          });
                                          setShowCandidateMissedModal(true);
                                      } else {
                                          sessionStorage.setItem('assessment_nav_from_result_analysis', 'true');
                                          const cat = String(cand.assessment_category || '').trim().toLowerCase();
                                          if (cat !== 'external' && cat !== 'hr recruitment-fresher') {
                                              navigate(`/employee-details?user_id=${cand.user_id}`);
                                          }
                                      }
                                  }}>
                                     <td className="py-3 sm:py-4 px-4 sm:px-6 text-[12px] font-black text-slate-700">{cand.name}</td>
                                     <td className="py-3 sm:py-4 px-4 sm:px-6 text-[11px] font-bold text-slate-500">{cand.role}</td>
                                     <td className="py-3 sm:py-4 px-4 sm:px-6 text-[12px] font-black text-slate-700">{pct}%</td>
                                     {showMissedColumn && (
                                       <td className="py-3 sm:py-4 px-4 sm:px-6 text-center">
                                         {candidateMissedQuestions.length > 0 ? (
                                           <button 
                                             onClick={(e) => {
                                               e.stopPropagation();
                                               setCandidateMissedData({
                                                   name: cand.name,
                                                   user_id: cand.user_id,
                                                   assessment_category: cand.assessment_category,
                                                   questions: candidateMissedQuestions
                                               });
                                               setShowCandidateMissedModal(true);
                                             }}
                                             title="Click to view missed questions"
                                             className="inline-flex items-center mx-auto bg-white hover:bg-slate-50 border border-slate-200 shadow-sm px-2 py-1.5 rounded-lg transition-all group hover:border-slate-300 cursor-pointer"
                                           >
                                             <span className="text-[12px] font-black text-slate-800">{candidateMissedQuestions.length}</span>
                                             <div className="flex items-center gap-1 border-l border-slate-200 pl-2 ml-2">
                                               {mcqMissed > 0 && <span className="text-[9px] font-black text-white bg-indigo-500 px-1.5 py-0.5 rounded shadow-sm uppercase tracking-wider" title="MCQ Missed">{mcqMissed} MCQ</span>}
                                               {descMissed > 0 && <span className="text-[9px] font-black text-white bg-amber-500 px-1.5 py-0.5 rounded shadow-sm uppercase tracking-wider" title="Descriptive Missed">{descMissed} DESC</span>}
                                             </div>
                                           </button>
                                         ) : (
                                           <span className="text-slate-300 text-xs font-bold">-</span>
                                         )}
                                       </td>
                                     )}
                                     <td className="py-3 sm:py-4 px-4 sm:px-6">
                                        <div className="flex items-center gap-1.5">
                                          {cand.weak_pedagogy && cand.weak_pedagogy !== 'None' ? (
                                            <>
                                              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0"></span>
                                              <span className="text-[11px] font-bold text-slate-600 break-words whitespace-normal leading-tight" title={cand.weak_pedagogy}>
                                                {cand.weak_pedagogy}
                                              </span>
                                            </>
                                          ) : (
                                            <span className="text-[11px] font-bold text-slate-300 italic">N/A</span>
                                          )}
                                        </div>
                                     </td>
                                     <td className="py-3 sm:py-4 px-4 sm:px-6">{statusBadge}</td>
                                  </tr>
                               );
                            })}
                            {displayCandidates.length === 0 && (
                               <tr>
                                  <td colSpan="5" className="py-12 text-center text-xs font-bold text-slate-400 italic">No candidates found</td>
                               </tr>
                            )}
                         </tbody>
                      </table>
                   </div>
                   {/* Pagination */}
                   {displayCandidates.length > 0 && (
                      <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-white">
                         <span className="text-[10px] font-bold text-slate-400">
                            Showing {((overallPage - 1) * itemsPerPage) + 1} - {Math.min(overallPage * itemsPerPage, displayCandidates.length)} of {displayCandidates.length}
                         </span>
                         <div className="flex items-center gap-1">
                            <button 
                               disabled={overallPage === 1}
                               onClick={() => setOverallPage(p => Math.max(1, p - 1))}
                               className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 disabled:opacity-50 text-slate-500 transition-colors"
                            >
                               <FiChevronLeft />
                            </button>
                            {/* Page numbers */}
                             {(() => {
                               const totalPages = Math.ceil(displayCandidates.length / itemsPerPage);
                               let start = Math.max(1, overallPage - 1);
                               let end = Math.min(totalPages, overallPage + 1);
                               if (overallPage === 1) {
                                 end = Math.min(totalPages, 3);
                               } else if (overallPage === totalPages) {
                                 start = Math.max(1, totalPages - 2);
                               }
                               return Array.from({ length: end - start + 1 }, (_, i) => start + i);
                             })().map(p => (
                               <button
                                  key={p}
                                  onClick={() => setOverallPage(p)}
                                  className={`w-7 h-7 flex items-center justify-center rounded-lg text-xs font-bold transition-colors ${overallPage === p ? 'bg-[#B91C1C] text-white shadow-sm shadow-red-200' : 'hover:bg-slate-100 text-slate-600'}`}
                               >
                                  {p}
                               </button>
                            ))}
                            <button 
                               disabled={overallPage === Math.ceil(displayCandidates.length / itemsPerPage)}
                               onClick={() => setOverallPage(p => Math.min(Math.ceil(displayCandidates.length / itemsPerPage), p + 1))}
                               className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 disabled:opacity-50 text-slate-500 transition-colors"
                            >
                               <FiChevronRight />
                            </button>
                         </div>
                      </div>
                   )}
                </div>
          </div>
        )}

        <div className={`transform transition-all duration-500 ease-in-out origin-top overflow-hidden ${
          showQuestionPerf && !showGroupCompare
            ? 'max-h-[3000px] opacity-100 scale-100 mt-1' 
            : 'max-h-0 opacity-0 scale-95 pointer-events-none mt-0'
        }`}>
          {/* Top Row: Pedagogy Performance Column Chart + Candidate Details Table */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 sm:p-5 flex flex-col gap-4 mb-6">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
              <FiBarChart2 className="text-[#B91C1C]" size={14} />
              <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider">
                Pedagogy Performance
              </h3>
              <FiInfo className="text-slate-400 cursor-help" size={12} title="Click on a column in the chart to view candidate details" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
              {/* Left Column: Chart */}
              <div className="lg:col-span-6 flex flex-col h-full min-h-[260px]">
                <div id="pedagogyChartDiv" className="w-full flex-1" style={{ height: '100%', minHeight: '260px' }}></div>
              </div>

              {/* Right Column: Data Table */}
              <div className="lg:col-span-6 flex flex-col justify-between border-t lg:border-t-0 lg:border-l border-slate-100 pt-4 lg:pt-0 lg:pl-6 min-h-[280px]">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-3 mb-3 gap-2">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3 min-w-0">
                    <span className="text-[10px] font-black text-slate-700 uppercase tracking-wider shrink-0">Candidate Details</span>
                    {selectedPedagogy ? (
                      <span className="bg-slate-900 text-white px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest shrink-0">
                        {selectedPedagogy.pedagogy}
                      </span>
                    ) : (
                      <span className="text-[9px] font-bold text-slate-400 uppercase">No Pedagogy Selected</span>
                    )}
                  </div>
                  {selectedPedagogy && (
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="bg-red-50 border border-red-100/30 rounded-lg px-2 py-0.5 text-[#B91C1C] font-extrabold text-[9px] uppercase tracking-wider flex items-center gap-1.5 whitespace-nowrap">
                        <FiUsers className="text-[#B91C1C]/70" size={10} />
                        <span>{selectedPedagogy.candidateCount} RECORDS</span>
                      </div>

                      <div className="flex items-center gap-3">
                         <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">ROWS</span>
                         <div className="flex items-center justify-center bg-white border border-slate-200/80 rounded-2xl px-3.5 py-1 text-xs shadow-sm">
                            <select 
                              value={pedagogyItemsPerPage === 999999 ? 'All' : pedagogyItemsPerPage} 
                              onChange={(e) => { 
                                setPedagogyItemsPerPage(e.target.value === 'All' ? 999999 : Number(e.target.value)); 
                                setPedagogyPage(1); 
                              }}
                              className="bg-transparent border-0 focus:ring-0 focus:outline-none font-bold text-slate-700 cursor-pointer text-[11px] py-0 m-0 text-center appearance-none"
                              style={{
                                minWidth: '24px',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                                appearance: 'none',
                                border: 'none',
                                outline: 'none',
                                boxShadow: 'none',
                                padding: '0px'
                              }}
                            >
                              <option value="5">5</option>
                              <option value="10">10</option>
                              <option value="25">25</option>
                              <option value="50">50</option>
                              <option value="All">All</option>
                            </select>
                            <FiChevronDown className="text-slate-400 text-[10px] ml-0.5 pointer-events-none" />
                         </div>
                         
                         {/* Vertical divider line */}
                         <div className="h-6 w-px bg-slate-200" />

                         {/* Search Input box */}
                         <div className="relative flex items-center bg-white border border-slate-200/80 rounded-2xl px-3 py-1.5 shadow-sm focus-within:ring-2 focus-within:ring-red-500/20 focus-within:border-red-300 transition-all">
                            <i className="bi bi-search text-slate-400 text-[11px] mr-2 pointer-events-none"></i>
                            <input 
                              type="text" 
                              placeholder="Search..." 
                              value={pedagogySearch}
                              onChange={(e) => {
                                setPedagogySearch(e.target.value);
                                setPedagogyPage(1);
                              }}
                              className="bg-transparent border-none outline-none focus:ring-0 p-0 text-[11px] font-bold text-slate-700 placeholder:text-slate-400 w-[110px]"
                            />
                         </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex-1 flex flex-col justify-between">
                  {selectedPedagogy ? (() => {
                    const handlePedagogySort = (col) => {
                      if (pedagogyTableSortColumn === col) {
                        setPedagogyTableSortDirection(pedagogyTableSortDirection === 'asc' ? 'desc' : 'asc');
                      } else {
                        setPedagogyTableSortColumn(col);
                        setPedagogyTableSortDirection(col === 'name' || col === 'designation' ? 'asc' : 'desc');
                      }
                      setPedagogyPage(1);
                    };

                    const renderPedagogySortIcon = (col) => {
                      if (pedagogyTableSortColumn !== col) return <i className="bi bi-arrow-down-up text-[8px] ml-1 text-slate-300"></i>;
                      return pedagogyTableSortDirection === 'asc' 
                        ? <i className="bi bi-arrow-up text-[10px] ml-1 text-[#B91C1C]"></i>
                        : <i className="bi bi-arrow-down text-[10px] ml-1 text-[#B91C1C]"></i>;
                    };

                    const filtered = (selectedPedagogy.candidates || []).filter(c => {
                      if (!pedagogySearch.trim()) return true;
                      return c.name.toLowerCase().includes(pedagogySearch.toLowerCase()) || 
                             (c.role && c.role.toLowerCase().includes(pedagogySearch.toLowerCase()));
                    }).sort((a, b) => {
                       const pedA = a.pedagogy_stats && a.pedagogy_stats[selectedPedagogy.pedagogy];
                       const pedB = b.pedagogy_stats && b.pedagogy_stats[selectedPedagogy.pedagogy];
                       const accA = pedA && pedA.total > 0 ? Math.round((pedA.correct / pedA.total) * 100) : Math.round(a.accuracy || 0);
                       const accB = pedB && pedB.total > 0 ? Math.round((pedB.correct / pedB.total) * 100) : Math.round(b.accuracy || 0);
                       
                       let aVal, bVal;
                       if (pedagogyTableSortColumn === 'name') {
                         aVal = a.name.toLowerCase();
                         bVal = b.name.toLowerCase();
                       } else if (pedagogyTableSortColumn === 'designation') {
                         aVal = (a.role || '').toLowerCase();
                         bVal = (b.role || '').toLowerCase();
                       } else {
                         aVal = accA;
                         bVal = accB;
                       }

                       if (aVal < bVal) return pedagogyTableSortDirection === 'asc' ? -1 : 1;
                       if (aVal > bVal) return pedagogyTableSortDirection === 'asc' ? 1 : -1;
                       return 0;
                    });
                    
                    const totalPages = Math.ceil(filtered.length / pedagogyItemsPerPage);
                    const paginated = filtered.slice((pedagogyPage - 1) * pedagogyItemsPerPage, pedagogyPage * pedagogyItemsPerPage);

                    return (
                      <div className="flex flex-col justify-between h-full">
                        <div className="overflow-x-auto w-full overflow-y-auto" style={{ maxHeight: '230px' }}>
                          <table className="w-full text-left border-collapse">
                            <thead className="bg-slate-50 sticky top-0 z-10">
                              <tr className="border-b border-slate-100">
                                <th className="py-2 px-3 text-[9px] font-black text-slate-400 uppercase tracking-wider w-[10%] text-center select-none">S.No</th>
                                <th onClick={() => handlePedagogySort('name')} className="py-2 px-3 text-[9px] font-black text-slate-400 uppercase tracking-wider w-[50%] cursor-pointer hover:bg-slate-100 transition-colors select-none">
                                  Candidate Name {renderPedagogySortIcon('name')}
                                </th>
                                <th onClick={() => handlePedagogySort('designation')} className="py-2 px-2 text-[9px] font-black text-slate-400 uppercase tracking-wider w-[25%] cursor-pointer hover:bg-slate-100 transition-colors select-none">
                                  Designation {renderPedagogySortIcon('designation')}
                                </th>
                                <th onClick={() => handlePedagogySort('score')} className="py-2 px-2 text-[9px] font-black text-slate-400 uppercase tracking-wider w-[15%] text-right cursor-pointer hover:bg-slate-100 transition-colors select-none">
                                  <div className="flex items-center justify-end">Score {renderPedagogySortIcon('score')}</div>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginated.map((c, idx) => {
                                 const pedStats = c.pedagogy_stats && c.pedagogy_stats[selectedPedagogy.pedagogy];
                                 const acc = pedStats && pedStats.total > 0 ? Math.round((pedStats.correct / pedStats.total) * 100) : Math.round(c.accuracy || 0);
                                return (
                                  <tr key={c.user_id} className="border-b border-slate-50 hover:bg-slate-50/40 transition-colors">
                                    <td className="py-2 px-3 text-[10px] font-bold text-slate-400 text-center">{((pedagogyPage - 1) * pedagogyItemsPerPage) + idx + 1}</td>
                                    <td className="py-2 px-3 text-[11px] font-bold text-slate-750 break-words whitespace-normal leading-tight" title={c.name}>
                                      {c.name}
                                    </td>
                                    <td className="py-2 px-2 text-[10px] font-bold text-slate-455 capitalize break-words whitespace-normal leading-tight" title={c.role}>{c.role}</td>
                                    <td className="py-2 px-2 text-right">
                                      <span className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded text-[10px] font-black ${acc >= 75 ? 'bg-emerald-50 text-emerald-600' : acc >= 50 ? 'bg-amber-50 text-amber-600' : 'bg-rose-50 text-rose-600'}`}>
                                        {acc}%
                                      </span>
                                    </td>
                                  </tr>
                                );
                              })}
                              {paginated.length === 0 && (
                                <tr>
                                  <td colSpan="4" className="py-6 text-center text-[10px] font-bold text-slate-450 bg-slate-50/20 rounded-b-lg">No candidates found</td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        
                        {filtered.length > 0 && (
                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                            <div className="text-[8px] font-black text-slate-455 uppercase tracking-wider">
                              Showing {((pedagogyPage - 1) * pedagogyItemsPerPage) + 1} to {Math.min(pedagogyPage * pedagogyItemsPerPage, filtered.length)} of {filtered.length}
                            </div>
                            <div className="flex items-center gap-1">
                              <button disabled={pedagogyPage === 1} onClick={() => setPedagogyPage(p => Math.max(1, p - 1))} className="w-5 h-5 flex items-center justify-center bg-transparent border-0 disabled:opacity-30 text-slate-400 hover:text-slate-600 transition-all cursor-pointer"><FiChevronLeft size={12} /></button>
                              {(() => {
                                let start = Math.max(1, pedagogyPage - 1);
                                let end = Math.min(totalPages, pedagogyPage + 1);
                                if (pedagogyPage === 1) {
                                  end = Math.min(totalPages, 3);
                                } else if (pedagogyPage === totalPages) {
                                  start = Math.max(1, totalPages - 2);
                                }
                                return Array.from({ length: end - start + 1 }, (_, i) => start + i);
                              })().map(p => (
                                <button key={p} onClick={() => setPedagogyPage(p)} className={`w-5 h-5 rounded-md flex items-center justify-center font-black text-[9px] transition-all cursor-pointer ${pedagogyPage === p ? 'bg-[#B91C1C] text-white' : 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-50'}`}>{p}</button>
                              ))}
                              <button disabled={pedagogyPage === totalPages} onClick={() => setPedagogyPage(p => Math.min(totalPages, p + 1))} className="w-5 h-5 flex items-center justify-center bg-transparent border-0 disabled:opacity-30 text-slate-400 hover:text-slate-600 transition-all cursor-pointer"><FiChevronRight size={12} /></button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })() : (
                    <div className="h-full flex-1 flex items-center justify-center min-h-[160px]">
                      <span className="text-[10px] font-bold text-slate-400 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">Select a pedagogy column to view candidates</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Row: Questions performance — single container, 3 columns */}
          <div id="question-performance-section" className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 sm:p-5 mb-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 items-start lg:items-stretch">
            {/* Column 1: Questions Lists Table */}
            <div className="flex flex-col w-full min-h-0 h-full lg:pr-6 lg:border-r border-slate-100 pb-6 lg:pb-0 border-b lg:border-b-0">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-4 gap-4 shrink-0">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <FiList className="text-[#B91C1C]" size={14} />
                      <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider">
                        Questions List
                      </h3>
                    </div>
                    <p className="text-[9px] font-semibold text-slate-500 mt-0.5">
                      <span className="font-black text-slate-700">Note:</span> Click any row to view option distribution &amp; candidate responses
                    </p>
                  </div>
                  {/* Rows and Search container */}
                  <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
                    {/* Rows selector */}
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">ROWS</span>
                      <div className="flex items-center justify-center bg-white border border-slate-200 rounded-2xl px-2.5 py-1 text-xs shadow-sm">
                        <select 
                          value={questionItemsPerPage} 
                          onChange={(e) => { 
                            const val = e.target.value;
                            setQuestionItemsPerPage(val === 'All' ? 'All' : Number(val)); 
                            setQuestionPage(1); 
                          }}
                          className="bg-transparent border-0 focus:ring-0 focus:outline-none font-bold text-slate-700 cursor-pointer text-[10px] py-0 m-0 text-center appearance-none"
                          style={{
                            minWidth: '24px',
                            WebkitAppearance: 'none',
                            appearance: 'none',
                            border: 'none',
                            outline: 'none',
                            boxShadow: 'none',
                            padding: '0px'
                          }}
                        >
                          <option value={6}>6</option>
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value="All">All</option>
                        </select>
                        <FiChevronDown className="text-slate-400 text-[9px] ml-0.5 pointer-events-none" />
                      </div>
                    </div>

                    {/* Search Input Box */}
                    <div className="relative flex items-center bg-white border border-slate-200 rounded-2xl px-3 py-1.5 shadow-sm focus-within:ring-2 focus-within:ring-red-500/20 focus-within:border-red-300 transition-all w-full sm:w-auto">
                       <FiSearch className="text-slate-400 text-xs mr-2 pointer-events-none" />
                       <input 
                         type="text" 
                         placeholder="Search question..." 
                         value={questionSearch} 
                         onChange={(e) => {
                           setQuestionSearch(e.target.value);
                           setQuestionPage(1);
                         }}
                         className="bg-transparent border-none outline-none focus:ring-0 p-0 text-[11px] font-bold text-slate-700 placeholder:text-slate-400 w-full sm:w-[155px]"
                       />
                    </div>
                  </div>
                </div>
                <div
                  className={`assess-analytics-questions-table-wrap overflow-x-auto custom-scrollbar w-full rounded-xl border border-slate-100 bg-white mt-4 flex-1 min-h-0 ${
                    questionListUsesScroll
                      ? 'assess-analytics-questions-table-wrap--scroll'
                      : 'assess-analytics-questions-table-wrap--fill'
                  }`}
                  style={{
                    '--analytics-q-visible-rows': questionListUsesScroll ? 6 : questionFillRowCount,
                  }}
                >
                  <table className="assess-analytics-questions-table w-full text-left border-collapse">
                    <thead>
                      <tr className="text-[10px] sm:text-[11px] uppercase font-black text-slate-400 tracking-wider">
                        <th onClick={() => handleQuestionTableSort('text')} className="assess-analytics-q-th assess-analytics-q-th--question sticky top-0 bg-slate-50/95 backdrop-blur-sm z-10 border-b border-slate-100 px-2.5 sm:px-4 cursor-pointer hover:bg-slate-100/50 transition-colors select-none">
                          <div className="flex items-center gap-1">Question {renderQuestionSortIcon('text')}</div>
                        </th>
                        <th onClick={() => handleQuestionTableSort('pedagogy')} className="assess-analytics-q-th assess-analytics-q-th--pedagogy sticky top-0 bg-slate-50/95 backdrop-blur-sm z-10 border-b border-slate-100 px-2 sm:px-3 cursor-pointer hover:bg-slate-100/50 transition-colors select-none">
                          <div className="flex items-center gap-1">Pedagogy {renderQuestionSortIcon('pedagogy')}</div>
                        </th>
                        <th className="assess-analytics-q-th assess-analytics-q-th--action sticky top-0 bg-slate-50/95 backdrop-blur-sm z-10 border-b border-slate-100 px-2 text-center"></th>
                      </tr>
                    </thead>
                    <tbody className={questionListUsesScroll ? 'custom-scrollbar' : undefined}>
                      {paginatedQuestions.length > 0 ? (
                        paginatedQuestions.map((q, idx) => {
                          const isSelected = selectedQuestion?.question_id === q.question_id;
                          const colors = getPedagogyColorInfo(q.pedagogy);
                          const currentQuestionIndexOverall = data.questions_data.findIndex(qd => qd.question_id === q.question_id);
                          const displayIndex = currentQuestionIndexOverall !== -1 ? currentQuestionIndexOverall + 1 : idx + 1;
                          
                          return (
                            <tr 
                              key={q.question_id} 
                              onClick={() => {
                                setSelectedQuestion(q);
                                setSelectedOption(null);
                                // Removed setSelectedPedagogy(null) so contexts don't reset each other
                                setTimeout(() => {
                                  const element = document.getElementById('selected-question-section');
                                  if (element && window.innerWidth < 1024) {
                                    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                  }
                                }, 100);
                              }}
                              className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors text-xs sm:text-[13px] font-bold text-slate-600 cursor-pointer ${
                                isSelected ? 'bg-rose-50/20 ring-1 ring-red-100' : ''
                              }`}
                            >
                              <td className="assess-analytics-q-td assess-analytics-q-td--question px-2.5 sm:px-4 text-[11px] sm:text-[13px] text-left" title={q.question_text}>
                                <div className="assess-analytics-q-cell">
                                  <span className="assess-analytics-q-num">Q{displayIndex}.</span>
                                  <span className="assess-analytics-question-text text-slate-800 font-bold">
                                    {q.question_text}
                                  </span>
                                </div>
                              </td>
                              <td className="assess-analytics-q-td assess-analytics-q-td--pedagogy px-2 sm:px-3 text-[11px] sm:text-[13px] text-left text-slate-500 font-semibold" title={q.pedagogy}>
                                <span className={`assess-analytics-q-badge px-2 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wide border whitespace-nowrap ${colors.badgeBg}`}>
                                  {q.pedagogy || 'General'}
                                </span>
                              </td>
                              <td className="assess-analytics-q-td assess-analytics-q-td--action px-1 text-slate-400">
                                <span className="assess-analytics-q-chevron" aria-hidden="true">
                                  <FiChevronRight />
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="3" className="py-12 text-center text-xs font-bold text-slate-300 italic">
                            No questions found matching criteria
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

              {/* Pagination */}
              {sortedQuestions.length > 0 && (
                <div className="flex flex-col sm:flex-row items-center justify-between border-t border-slate-100 pt-3 mt-3 gap-3 shrink-0">
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest text-center sm:text-left">
                    Showing {((questionPage - 1) * effectiveQuestionItemsPerPage) + 1} to {Math.min(questionPage * effectiveQuestionItemsPerPage, sortedQuestions.length)} of {sortedQuestions.length}
                  </span>
                  <div className="flex items-center gap-1">
                    <button 
                      disabled={questionPage === 1}
                      onClick={() => setQuestionPage(p => Math.max(1, p - 1))}
                      className="w-6 h-6 flex items-center justify-center bg-transparent border-0 disabled:opacity-30 text-slate-400 hover:text-slate-600 transition-all cursor-pointer"
                    >
                      <FiChevronLeft className="text-base" />
                    </button>
                    {(() => {
                      let start = Math.max(1, questionPage - 1);
                      let end = Math.min(questionTotalPages, questionPage + 1);
                      if (questionPage === 1) {
                        end = Math.min(questionTotalPages, 3);
                      } else if (questionPage === questionTotalPages) {
                        start = Math.max(1, questionTotalPages - 2);
                      }
                      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
                    })().map(pageNum => (
                      <button
                        key={pageNum}
                        onClick={() => setQuestionPage(pageNum)}
                        className={`w-6 h-6 rounded-md flex items-center justify-center font-black text-[10px] transition-all cursor-pointer ${
                          questionPage === pageNum
                            ? 'bg-[#B91C1C] text-white shadow-sm'
                            : 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    ))}
                    <button 
                      disabled={questionPage === questionTotalPages}
                      onClick={() => setQuestionPage(p => Math.min(questionTotalPages, p + 1))}
                      className="w-6 h-6 flex items-center justify-center bg-transparent border-0 disabled:opacity-30 text-slate-400 hover:text-slate-600 transition-all cursor-pointer"
                    >
                      <FiChevronRight className="text-base" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Column 2 & Column 3 Content or Placeholder */}
            {selectedQuestion ? (
              <>
                {/* Column 2: Question details & Option Distribution Chart (Graph) */}
                <div 
                  key={selectedQuestion.question_id}
                  id="selected-question-section"
                  className="flex flex-col w-full overflow-hidden h-full animate-fade-in lg:px-6 lg:border-r border-slate-100 pb-6 lg:pb-0 border-b lg:border-b-0 pt-6 lg:pt-0"
                  style={{ animation: 'fadeIn 0.25s ease-in-out' }}
                >
                  {/* Header */}
                  <div className="px-4 py-3 sm:px-5 sm:py-3.5 border-b border-slate-150/70 bg-slate-50/50 flex items-center justify-between gap-4">
                    <span className="text-[10px] font-black text-slate-700 uppercase tracking-wider">
                      Question Analysis
                    </span>
                    <div className="flex items-center gap-3 shrink-0">
                      {/* Pagination Controls */}
                      <div className="flex items-center gap-1.5">
                        <button 
                          disabled={currentQuestionIndex <= 0}
                          onClick={handlePrevQuestion}
                          className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-slate-200/50 disabled:opacity-30 text-slate-400 hover:text-slate-600 transition-all border border-slate-200 bg-white cursor-pointer"
                        >
                          <FiChevronLeft size={12} className="stroke-[3]" />
                        </button>
                        <span className="text-[9px] font-black text-slate-500 min-w-[50px] text-center uppercase tracking-wider">
                          Q{currentQuestionIndex + 1} of {sortedQuestions.length}
                        </span>
                        <button 
                          disabled={currentQuestionIndex >= sortedQuestions.length - 1}
                          onClick={handleNextQuestion}
                          className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-slate-200/50 disabled:opacity-30 text-slate-400 hover:text-slate-600 transition-all border border-slate-200 bg-white cursor-pointer"
                        >
                          <FiChevronRight size={12} className="stroke-[3]" />
                        </button>
                      </div>
                      
                      {/* Total Candidates */}
                      <span className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-750 border border-blue-100 rounded-lg text-[9px] font-black uppercase">
                        Total Candidates:
                        <span className="text-[11px] font-extrabold tabular-nums">{selectedQuestion.attempts}</span>
                      </span>
                    </div>
                  </div>

                  {/* Body Container */}
                  <div className="p-4 sm:p-5 flex flex-col flex-1 gap-6">
                    {/* Question Text — primary focus */}
                    <div className="assess-analytics-question-highlight rounded-xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm border-l-4 border-l-[#B91C1C]">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-black text-slate-700 uppercase tracking-wider">
                          Question
                        </span>
                        {selectedQuestion.pedagogy && (
                          <span className="bg-slate-900 text-white px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest">
                            {selectedQuestion.pedagogy}
                          </span>
                        )}
                      </div>
                      <p className="text-[14px] sm:text-[15px] font-extrabold text-slate-900 leading-relaxed">
                        {selectedQuestion.question_text}
                      </p>
                    </div>
                       {/* Dynamic Question Chart Div with Selector */}
                    <div className="flex flex-col flex-1 gap-3 pt-4 border-t border-slate-100">
                      <div className="flex flex-row items-center justify-between gap-2 border-b border-slate-100 pb-2">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[10px] font-extrabold text-slate-450 uppercase tracking-wide">Question Analysis Chart</span>
                          <span className="text-[8px] font-black text-slate-400 uppercase">
                            Option selection distribution for this question
                          </span>
                        </div>
                      </div>

                      <div id="optionChartDiv" className="w-full flex-1 min-h-[260px] bg-slate-50/50 rounded-xl border border-slate-100"></div>
                      
                      {/* Dynamic Color Legend / Guidance */}
                      <>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-1 pb-2 border-b border-slate-100">
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-[#10B981] inline-block"></span>
                            <span className="text-[9px] font-bold text-slate-500">Correct Option</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-[#EF4444] inline-block"></span>
                            <span className="text-[9px] font-bold text-slate-500">Most Incorrect</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-[#3B82F6] inline-block"></span>
                            <span className="text-[9px] font-bold text-slate-500">Unanswered</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-[#94A3B8] inline-block"></span>
                            <span className="text-[9px] font-bold text-slate-500">Other Incorrect</span>
                          </div>
                        </div>
                        <span className="text-[9px] font-bold text-slate-455 mt-1 flex items-center gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                          Note: Touch/Hover any bar to view the option details. Click columns to filter candidates.
                        </span>
                      </>
                    </div>
                  </div>
                </div>

                {/* Column 3: Candidate Responses Table */}
                <div className="flex flex-col justify-between gap-4 h-full lg:pl-6 pt-6 lg:pt-0">
                  <div className="flex flex-col gap-3 flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-slate-700 uppercase tracking-wider">
                          Candidate Responses
                        </span>
                        {selectedOption ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-50 text-[#B91C1C] rounded text-[9px] font-extrabold uppercase border border-red-100/50">
                            Option {selectedOption.option.replace('Option ', '')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded text-[9px] font-extrabold uppercase">
                            All
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {selectedOption && (
                          <button 
                            onClick={() => setSelectedOption(null)}
                            className="text-[8px] font-black text-[#B91C1C] hover:text-[#991B1B] bg-red-55 hover:bg-red-100 px-2 py-1 rounded-lg transition-all border border-red-100 cursor-pointer"
                          >
                            Clear Filter
                          </button>
                        )}
                        {/* Rows selector */}
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">ROWS</span>
                          <div className="flex items-center justify-center bg-white border border-slate-200 rounded-2xl px-2 py-0.5 text-xs shadow-sm">
                            <select 
                              value={optionEmployeesPerPage === 'All' ? 'All' : optionEmployeesPerPage} 
                              onChange={(e) => { 
                                setOptionEmployeesPerPage(e.target.value === 'All' ? 'All' : Number(e.target.value)); 
                                setEmployeePage(1); 
                              }}
                              className="bg-transparent border-0 focus:ring-0 focus:outline-none font-bold text-slate-700 cursor-pointer text-[9px] py-0 m-0 text-center appearance-none"
                              style={{
                                minWidth: '20px',
                                WebkitAppearance: 'none',
                                appearance: 'none',
                                border: 'none',
                                outline: 'none',
                                boxShadow: 'none',
                                padding: '0px'
                              }}
                            >
                              <option value={10}>10</option>
                              <option value={20}>20</option>
                              <option value="All">All</option>
                            </select>
                            <FiChevronDown className="text-slate-400 text-[9px] ml-0.5 pointer-events-none" />
                          </div>
                        </div>

                        {/* Search Input Box */}
                        <div className="relative flex items-center bg-white border border-slate-200 rounded-2xl px-2.5 py-1 shadow-sm focus-within:ring-2 focus-within:ring-red-500/20 focus-within:border-red-300 transition-all w-24">
                           <FiSearch className="text-slate-400 text-[9px] mr-1.5 pointer-events-none" />
                           <input 
                             type="text" 
                             placeholder="Search..." 
                             value={employeeSearch} 
                             onChange={(e) => {
                               setEmployeeSearch(e.target.value);
                               setEmployeePage(1);
                             }}
                             className="bg-transparent border-none outline-none focus:ring-0 p-0 text-[10px] font-bold text-slate-700 placeholder:text-slate-400 w-full"
                           />
                        </div>
                      </div>
                    </div>

                    <div className={`overflow-x-auto overflow-y-auto w-full rounded-xl border border-slate-100 bg-white flex-1 min-h-0 ${optionEmployeesPerPage === 10 ? 'max-h-none' : 'max-h-[500px]'}`}>
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="text-[10px] sm:text-[11px] uppercase font-black text-slate-400 tracking-wider">
                            <th className="sticky top-0 bg-slate-50/95 backdrop-blur-sm z-10 border-b border-slate-100 py-2.5 px-2 w-[8%] text-center">S.No</th>
                            <th onClick={() => handleOptionTableSort('name')} className="sticky top-0 bg-slate-50/95 backdrop-blur-sm z-10 border-b border-slate-100 py-2.5 px-3 w-[32%] cursor-pointer hover:bg-slate-100/50 transition-colors select-none">
                              <div className="flex items-center gap-0.5">Name {renderOptionSortIcon('name')}</div>
                            </th>
                            <th onClick={() => handleOptionTableSort('role')} className="sticky top-0 bg-slate-50/95 backdrop-blur-sm z-10 border-b border-slate-100 py-2.5 px-3 w-[26%] cursor-pointer hover:bg-slate-100/50 transition-colors select-none">
                              <div className="flex items-center gap-0.5">Role {renderOptionSortIcon('role')}</div>
                            </th>

                            <th onClick={() => handleOptionTableSort('status')} className="sticky top-0 bg-slate-50/95 backdrop-blur-sm z-10 border-b border-slate-100 py-2.5 px-2 w-[18%] text-center cursor-pointer hover:bg-slate-100/50 transition-colors select-none">
                              <div className="flex items-center justify-center gap-0.5">Status {renderOptionSortIcon('status')}</div>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedEmployees.map((emp, i) => {
                            const ans = getCandidateAnswer(emp.user_id);
                            const isCorrect = ans.replace('Option ', '') === (selectedQuestion.correct_answer || '').replace('Option ', '');
                            const isUnanswered = ans === 'Unanswered' || ans === '-';
                            const isHighlightedOption = selectedOption && selectedOption.option.replace('Option ', '') === ans.replace('Option ', '');
                            
                            return (
                              <tr 
                                key={emp.user_id || i} 
                                className={`border-b border-slate-50 transition-colors ${
                                  isHighlightedOption 
                                    ? 'bg-red-50/20 border-l-2 border-l-[#B91C1C]' 
                                    : 'hover:bg-slate-50/50'
                                }`}
                              >
                                <td className="py-2.5 px-2 text-[10px] sm:text-[11px] font-bold text-slate-400 text-center">{((employeePage - 1) * effectiveOptionEmployeesPerPage) + i + 1}</td>
                                <td className="py-2.5 px-3 text-[11px] sm:text-[12px] font-bold text-slate-700 break-words whitespace-normal leading-tight" title={emp.name}>{emp.name}</td>
                                <td className="py-2.5 px-3 text-[10px] sm:text-[11px] font-bold text-slate-500 break-words whitespace-normal leading-tight" title={emp.role}>{emp.role}</td>

                                <td className="py-2.5 px-2 text-center">
                                  <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-[9px] sm:text-[10px] font-black uppercase tracking-wide ${isCorrect ? 'bg-emerald-50 text-emerald-700' : isUnanswered ? 'bg-slate-50 text-slate-450 border border-slate-100' : 'bg-rose-50 text-rose-750'}`}>
                                    {isCorrect ? 'Correct' : isUnanswered ? 'Unanswered' : 'Incorrect'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                          {paginatedEmployees.length === 0 && (
                            <tr>
                              <td colSpan="4" className="py-6 text-center text-xs font-bold text-slate-400 bg-slate-50/30 rounded-b-xl">No responses found</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination */}
                  {filteredOptionEmployees.length > 0 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between border-t border-slate-100 pt-3 mt-2 gap-2">
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest text-center sm:text-left">
                        Showing {((employeePage - 1) * effectiveOptionEmployeesPerPage) + 1} to {Math.min(employeePage * effectiveOptionEmployeesPerPage, filteredOptionEmployees.length)} of {filteredOptionEmployees.length}
                      </span>
                      <div className="flex items-center gap-1">
                        <button 
                          disabled={employeePage === 1}
                          onClick={() => setEmployeePage(p => Math.max(1, p - 1))}
                          className="w-5 h-5 flex items-center justify-center bg-transparent border-0 disabled:opacity-30 text-slate-400 hover:text-slate-600 transition-all cursor-pointer"
                        >
                          <FiChevronLeft className="text-sm" />
                        </button>
                        {(() => {
                          let start = Math.max(1, employeePage - 1);
                          let end = Math.min(employeeTotalPages, employeePage + 1);
                          if (employeePage === 1) {
                            end = Math.min(employeeTotalPages, 3);
                          } else if (employeePage === employeeTotalPages) {
                            start = Math.max(1, employeeTotalPages - 2);
                          }
                          return Array.from({ length: end - start + 1 }, (_, i) => start + i);
                        })().map(pageNum => (
                          <button
                            key={pageNum}
                            onClick={() => setEmployeePage(pageNum)}
                            className={`w-5 h-5 rounded-md flex items-center justify-center font-black text-[10px] transition-all cursor-pointer ${
                              employeePage === pageNum
                                ? 'bg-[#B91C1C] text-white shadow-sm'
                                : 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        ))}
                        <button 
                          disabled={employeePage === employeeTotalPages}
                          onClick={() => setEmployeePage(p => Math.min(employeeTotalPages, p + 1))}
                          className="w-5 h-5 flex items-center justify-center bg-transparent border-0 disabled:opacity-30 text-slate-400 hover:text-slate-600 transition-all cursor-pointer"
                        >
                          <FiChevronRight className="text-sm" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="lg:col-span-2 flex flex-col items-center justify-center text-center p-8 py-16 min-h-[400px] w-full gap-6 lg:pl-6 pt-6 lg:pt-0 border border-dashed border-slate-200 rounded-xl bg-slate-50/30">
                {/* Icon */}
                <div className="w-14 h-14 rounded-2xl bg-red-50 border border-red-100 flex items-center justify-center shadow-sm">
                  <FiMousePointer className="text-[#B91C1C]" size={22} />
                </div>

                {/* Title */}
                <div className="flex flex-col gap-1.5">
                  <h3 className="text-sm font-black text-slate-800 tracking-tight">Select a Question to Begin</h3>
                  <p className="text-[10px] font-semibold text-slate-400 max-w-[220px] leading-relaxed">
                    Pick any question from the list on the left — the analysis panels will appear here.
                  </p>
                </div>

                {/* Step guide */}
                <div className="flex flex-col gap-2 w-full max-w-[260px]">
                  <div className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2.5">
                    <span className="w-5 h-5 rounded-full bg-[#B91C1C] text-white text-[9px] font-black flex items-center justify-center shrink-0">1</span>
                    <span className="text-[10px] font-bold text-slate-600">Click a question row on the left</span>
                  </div>
                  <div className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2.5">
                    <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-500 text-[9px] font-black flex items-center justify-center shrink-0">2</span>
                    <span className="text-[10px] font-bold text-slate-400">View option distribution chart</span>
                  </div>
                  <div className="flex items-center gap-3 bg-slate-50 border border-slate-100 rounded-xl px-3 py-2.5">
                    <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-500 text-[9px] font-black flex items-center justify-center shrink-0">3</span>
                    <span className="text-[10px] font-bold text-slate-400">See which candidates picked each option</span>
                  </div>
                </div>

                {/* Tip */}
                <p className="text-[9px] font-semibold text-slate-350 flex items-center gap-1 mt-1">
                  <FiInfo size={9} className="text-slate-400 shrink-0" />
                  On the chart: <span className="font-black text-slate-500">single-click</span> to filter &nbsp;&bull;&nbsp; <span className="font-black text-slate-500">double-click</span> to reset
                </p>
              </div>
            )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Candidate Missed Questions Modal */}
      <AnimatePresence>
        {showCandidateMissedModal && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[85vh]"
            >
              <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                <div>
                  <h3 className="text-lg font-black text-slate-800">Missed Questions</h3>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <p className="text-xs font-bold text-slate-500">Candidate: <span className="text-[#B91C1C]">{candidateMissedData.name}</span></p>
                    {candidateMissedData.user_id && 
                     String(candidateMissedData.assessment_category || '').trim().toLowerCase() !== 'external' && 
                     String(candidateMissedData.assessment_category || '').trim().toLowerCase() !== 'hr recruitment-fresher' && (
                      <button
                        onClick={() => {
                          setShowCandidateMissedModal(false);
                          sessionStorage.setItem('assessment_nav_from_result_analysis', 'true');
                          navigate(`/employee-details?user_id=${candidateMissedData.user_id}`);
                        }}
                        className="text-[9px] bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 px-2 py-0.5 rounded-lg font-bold transition-all cursor-pointer inline-flex items-center gap-1"
                      >
                        View Profile
                      </button>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowCandidateMissedModal(false)}
                  className="w-8 h-8 rounded-xl bg-white border border-slate-200 text-slate-400 flex items-center justify-center hover:bg-slate-50 hover:text-slate-600 transition-colors"
                >
                  <FiX />
                </button>
              </div>
              <div className="overflow-y-auto px-4 pb-4 sm:px-6 sm:pb-6 bg-slate-50 flex-1">
                <div className="flex flex-col gap-3">
                  {(() => {
                    const mcqQs = candidateMissedData.questions.filter(q => q.is_mcq !== false);
                    const descQs = candidateMissedData.questions.filter(q => q.is_mcq === false);

                    const renderCard = (q, realIdx) => {
                      let selectedText = 'N/A';
                      let correctText = 'N/A';

                      const distSrc = q.distribution || q.options;
                      if (distSrc) {
                        const candidateId = String(displayCandidates.find(c => c.name === candidateMissedData.name)?.user_id);
                        const pickedOption = distSrc.find(opt => opt.option === 'Unanswered' && opt.user_ids?.some(id => String(id) === candidateId));
                        const correctOption = distSrc.find(opt => opt.is_correct);
                        selectedText = pickedOption ? pickedOption.text : 'N/A';
                        correctText = correctOption ? correctOption.text : 'N/A';
                      } else {
                        const getOptionText = (key) => {
                          if (!key || key === 'Unanswered') return key || 'N/A';
                          const safeKey = String(key);
                          const optKey = `option_${safeKey.toLowerCase()}`;
                          return q[optKey] ? q[optKey] : safeKey;
                        };
                        selectedText = getOptionText(q.selected_answer);
                        correctText = getOptionText(q.correct_answer);
                      }

                      return (
                        <div key={realIdx} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-3 last:mb-0">
                          <div className="flex items-start gap-3">
                            <span className="w-6 h-6 rounded-lg bg-red-50 text-red-500 flex items-center justify-center text-xs font-black shrink-0 mt-0.5">
                              {realIdx + 1}
                            </span>
                            <div className="flex flex-col gap-2 w-full">
                              <h4 className="text-sm font-bold text-slate-800" dangerouslySetInnerHTML={{ __html: q.question_text }}></h4>
                              <div className="bg-slate-50 border border-slate-100 rounded-lg p-2.5 mt-1">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">Their Answer</span>
                                <span className="text-xs font-bold text-red-600 line-through decoration-red-300" dangerouslySetInnerHTML={{ __html: selectedText }}></span>
                              </div>
                              {q.is_mcq !== false && (
                                <div className="bg-emerald-50/50 border border-emerald-100/50 rounded-lg p-2.5 mt-1">
                                  <span className="text-[10px] font-black text-emerald-600/70 uppercase tracking-widest block mb-1">Correct Answer</span>
                                  <span className="text-xs font-bold text-emerald-700" dangerouslySetInnerHTML={{ __html: correctText }}></span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    };

                    const hasMCQ = mcqQs.length > 0;
                    const hasDesc = descQs.length > 0;
                    
                    // Auto-correct tab if current tab is empty
                    const currentTab = (activeMissedTab === 'MCQ' && !hasMCQ && hasDesc) ? 'DESC' 
                                     : (activeMissedTab === 'DESC' && !hasDesc && hasMCQ) ? 'MCQ'
                                     : activeMissedTab;

                    return (
                      <div className="flex flex-col h-full">
                        {hasMCQ && hasDesc && (
                          <div className="sticky top-0 z-10 bg-slate-50 pt-4 sm:pt-6 pb-2 mb-4 border-b border-slate-200 flex items-center gap-2">
                            <button
                              onClick={() => setActiveMissedTab('MCQ')}
                              className={`px-4 py-2.5 text-[10px] font-black uppercase tracking-widest border-b-2 transition-colors ${currentTab === 'MCQ' ? 'border-[#B91C1C] text-[#B91C1C]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                            >
                              MCQ Questions ({mcqQs.length})
                            </button>
                            <button
                              onClick={() => setActiveMissedTab('DESC')}
                              className={`px-4 py-2.5 text-[10px] font-black uppercase tracking-widest border-b-2 transition-colors ${currentTab === 'DESC' ? 'border-[#B91C1C] text-[#B91C1C]' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
                            >
                              Descriptive ({descQs.length})
                            </button>
                          </div>
                        )}
                        {!(hasMCQ && hasDesc) && <div className="h-4 sm:h-6" />}
                        
                        {!hasMCQ && !hasDesc && (
                          <div className="py-8 text-center text-slate-400 text-xs font-bold">No missed questions found.</div>
                        )}

                        <div className="flex flex-col">
                          {currentTab === 'MCQ' && hasMCQ && mcqQs.map((q, idx) => renderCard(q, idx))}
                          {currentTab === 'DESC' && hasDesc && descQs.map((q, idx) => renderCard(q, idx))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <OverallExportModal
        isOpen={showOverallExportModal}
        onClose={() => setShowOverallExportModal(false)}
        selectedOverallMetric={selectedOverallMetric}
        filteredOverallCandidates={filteredOverallCandidates}
        downloadOverallExcel={downloadOverallExcel}
        buildOverallPdfDoc={buildOverallPdfDoc}
      />
      <RoleExportModal
        isOpen={showRoleExportModal}
        onClose={() => setShowRoleExportModal(false)}
        selectedRole={selectedRole}
        filteredRoleCandidates={filteredRoleCandidates}
        downloadRoleExcel={downloadRoleExcel}
        buildRolePdfDoc={buildRolePdfDoc}
      />
    </>
  );
};

const OverallExportModal = ({
  isOpen,
  onClose,
  selectedOverallMetric,
  filteredOverallCandidates,
  downloadOverallExcel,
  buildOverallPdfDoc,
}) => {
  const allFields = [
    { id: 'name', label: 'Candidate Name' },
    { id: 'role', label: 'Role' },
    { id: 'department', label: 'Test Name' },
    { id: 'marks', label: 'Marks Obtained' },
    { id: 'accuracy', label: 'Percentage' },
    { id: 'weak_pedagogy', label: 'Weakest Topic' }
  ];

  const [selectedFields, setSelectedFields] = useState(allFields.map(f => f.id));
  const [searchTerm, setSearchTerm] = useState('');
  const [pdfPreview, setPdfPreview] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setPdfPreview((prev) => {
        revokeAssessmentPdfPreview(prev);
        return null;
      });
    }
  }, [isOpen]);

  const clearPdfPreview = () => {
    setPdfPreview((prev) => {
      revokeAssessmentPdfPreview(prev);
      return null;
    });
  };

  if (!isOpen) return null;

  const filteredFields = allFields.filter(f => 
    f.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleField = (id) => {
    setSelectedFields(prev => 
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    );
  };

  const allSelected = selectedFields.length === allFields.length;
  const handleToggleSelectAll = () => {
    setSelectedFields(allSelected ? [] : allFields.map(f => f.id));
  };

  const validateExport = () => {
    if (selectedFields.length === 0) {
      toast.error('Please select at least one field to export.');
      return false;
    }
    return true;
  };

  const handleExportExcel = async () => {
    if (!validateExport()) return;
    try {
      const activeFields = allFields.filter(f => selectedFields.includes(f.id));
      await downloadOverallExcel(activeFields);
      toast.success('Excel report downloaded successfully', { position: 'top-right' });
      onClose();
    } catch (err) {
      console.error(err);
      toast.error('Failed to export Excel report', { position: 'top-right' });
    }
  };

  const handleExportPdf = async () => {
    if (!validateExport()) return;
    try {
      const activeFields = allFields.filter(f => selectedFields.includes(f.id));
      const { doc, filename } = await buildOverallPdfDoc(activeFields);
      setPdfPreview((prev) => {
        revokeAssessmentPdfPreview(prev);
        return createAssessmentPdfPreview(doc, filename);
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate PDF preview');
    }
  };

  const handleDownloadPdfPreview = () => {
    downloadAssessmentPdfPreview(pdfPreview);
    toast.success('PDF report downloaded successfully');
  };

  return (
    <>
      <AssessmentPdfFullscreenViewer
        preview={pdfPreview}
        onClose={clearPdfPreview}
        onDownload={handleDownloadPdfPreview}
      />
      <div className="assessment-popup-overlay" style={{ zIndex: 9999999 }} role="dialog" aria-modal="true">
        <div className="export-modal-v3 rounded-[24px] overflow-hidden w-[550px] animate-scale-in">
          <div className="export-header-v3">
            <div className="export-header-v3__leading">
              <div className="export-icon-v3" aria-hidden>
                <i className="bi bi-file-earmark-spreadsheet-fill"></i>
              </div>
              <div className="export-title-v3">
                <h4>EXPORT REPORT</h4>
                <p>CHOOSE FIELDS TO EXPORT</p>
              </div>
            </div>
            <div className="export-header-v3__actions">
              <div className="export-format-actions export-format-actions--header">
                <button
                  type="button"
                  className="export-format-icon-btn"
                  onClick={handleExportPdf}
                  disabled={selectedFields.length === 0}
                  title="Preview PDF"
                >
                  <ExportPdfIcon />
                  <span className="export-format-icon-btn__label">Preview PDF</span>
                </button>
                <button
                  type="button"
                  className="export-format-icon-btn"
                  onClick={handleExportExcel}
                  disabled={selectedFields.length === 0}
                  title="Download Excel"
                >
                  <ExportExcelIcon />
                  <span className="export-format-icon-btn__label">Download Excel</span>
                </button>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="export-header-v3__close-btn"
                aria-label="Cancel"
                title="Cancel"
              >
                <i className="bi bi-x-lg" aria-hidden />
              </button>
            </div>
          </div>

          <div className="export-body-v3">
            <div className="flex justify-between items-center mb-6">
              <div className="text-[13px] font-black text-slate-800">Select Columns</div>
              <div className="export-search-v3 !mb-0 w-[180px]">
                <i className="bi bi-search"></i>
                <input 
                  type="text" 
                  placeholder="Search..." 
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="export-grid-v3">
              {filteredFields.map(f => (
                <div 
                  key={f.id} 
                  className="export-item-v3"
                  onClick={() => toggleField(f.id)}
                  style={{ 
                    background: selectedFields.includes(f.id) ? '#fff5f5' : '#f8fafc',
                    borderColor: selectedFields.includes(f.id) ? '#fee2e2' : '#f1f5f9'
                  }}
                >
                  <span style={{ 
                    background: selectedFields.includes(f.id) ? '#b91c1c' : '#cbd5e1' 
                  }}>
                    {selectedFields.includes(f.id) && <i className="bi bi-check-lg text-white text-[10px]"></i>}
                  </span>
                  {f.label}
                </div>
              ))}
            </div>

            <div className="export-footer-actions-v3">
              <button
                type="button"
                className="select-all-v3"
                onClick={handleToggleSelectAll}
                title={allSelected ? 'Deselect all columns' : 'Select all columns'}
              >
                <span
                  className={`select-all-v3__box ${allSelected ? 'select-all-v3__box--checked' : ''}`}
                >
                  {allSelected && <i className="bi bi-check-lg text-white text-[10px]"></i>}
                </span>
                <span className="select-all-v3__label">SELECT ALL</span>
              </button>
              <div className="selection-count-v3">
                {selectedFields.length} OF {allFields.length} SELECTED
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const RoleExportModal = ({
  isOpen,
  onClose,
  selectedRole,
  filteredRoleCandidates,
  downloadRoleExcel,
  buildRolePdfDoc,
}) => {
  const allFields = [
    { id: 'name', label: 'Candidate Name' },
    { id: 'role', label: 'Role' },
    { id: 'department', label: 'Test Name' },
    { id: 'marks', label: 'Marks Obtained' },
    { id: 'accuracy', label: 'Percentage' }
  ];

  const [selectedFields, setSelectedFields] = useState(allFields.map(f => f.id));
  const [searchTerm, setSearchTerm] = useState('');
  const [pdfPreview, setPdfPreview] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setPdfPreview((prev) => {
        revokeAssessmentPdfPreview(prev);
        return null;
      });
    }
  }, [isOpen]);

  const clearPdfPreview = () => {
    setPdfPreview((prev) => {
      revokeAssessmentPdfPreview(prev);
      return null;
    });
  };

  if (!isOpen) return null;

  const filteredFields = allFields.filter(f => 
    f.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleField = (id) => {
    setSelectedFields(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
    clearPdfPreview();
  };

  const allSelected = selectedFields.length === allFields.length;
  const handleToggleSelectAll = () => {
    if (allSelected) {
      setSelectedFields([]);
    } else {
      setSelectedFields(allFields.map(f => f.id));
    }
    clearPdfPreview();
  };

  const validateExport = () => {
    if (selectedFields.length === 0) {
      toast.error('Please select at least one field to export.');
      return false;
    }
    return true;
  };

  const handleExportExcel = async () => {
    if (!validateExport()) return;
    try {
      const activeFields = allFields.filter(f => selectedFields.includes(f.id));
      await downloadRoleExcel(activeFields);
      toast.success('Excel report downloaded successfully', { position: 'top-right' });
      onClose();
    } catch (err) {
      console.error(err);
      toast.error('Failed to export Excel report', { position: 'top-right' });
    }
  };

  const handleExportPdf = async () => {
    if (!validateExport()) return;
    try {
      const activeFields = allFields.filter(f => selectedFields.includes(f.id));
      const { doc, filename } = await buildRolePdfDoc(activeFields);
      setPdfPreview((prev) => {
        revokeAssessmentPdfPreview(prev);
        return createAssessmentPdfPreview(doc, filename);
      });
    } catch (err) {
      console.error('PDF Export failed', err);
      toast.error('Failed to generate PDF preview');
    }
  };

  const handleDownloadPdfPreview = () => {
    downloadAssessmentPdfPreview(pdfPreview);
    toast.success('PDF report downloaded successfully');
  };

  return (
    <>
      <AssessmentPdfFullscreenViewer
        preview={pdfPreview}
        onClose={clearPdfPreview}
        onDownload={handleDownloadPdfPreview}
      />
      <div className="assessment-popup-overlay" style={{ zIndex: 9999999 }} role="dialog" aria-modal="true">
        <div className="export-modal-v3 rounded-[24px] overflow-hidden w-[550px] animate-scale-in" style={{ fontFamily: 'Outfit, sans-serif' }}>

          <div className="export-header-v3">
            <div className="flex items-center gap-3">
              <div className="export-header-v3__icon">
                <i className="bi bi-file-earmark-spreadsheet-fill text-xl" />
              </div>
              <div>
                <h3 className="export-header-v3__title">EXPORT OPTIONS</h3>
                <p className="export-header-v3__subtitle">ROLE: {selectedRole?.role?.toUpperCase()}</p>
              </div>
            </div>
            <div className="export-header-v3__actions">
              <div className="export-format-actions export-format-actions--header">
                <button
                  type="button"
                  className="export-format-icon-btn"
                  onClick={handleExportPdf}
                  disabled={selectedFields.length === 0}
                  title="Preview PDF"
                >
                  <ExportPdfIcon />
                  <span className="export-format-icon-btn__label">Preview PDF</span>
                </button>
                <button
                  type="button"
                  className="export-format-icon-btn"
                  onClick={handleExportExcel}
                  disabled={selectedFields.length === 0}
                  title="Download Excel"
                >
                  <ExportExcelIcon />
                  <span className="export-format-icon-btn__label">Download Excel</span>
                </button>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="export-header-v3__close-btn"
                aria-label="Cancel"
                title="Cancel"
              >
                <i className="bi bi-x-lg" aria-hidden />
              </button>
            </div>
          </div>

          <div className="export-body-v3">
            <div className="flex justify-between items-center mb-6">
              <div className="text-[13px] font-black text-slate-800">Select Columns</div>
              <div className="export-search-v3 !mb-0 w-[180px]">
                <i className="bi bi-search"></i>
                <input 
                  type="text" 
                  placeholder="Search..." 
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="export-grid-v3">
              {filteredFields.map(f => (
                <div 
                  key={f.id} 
                  className="export-item-v3"
                  onClick={() => toggleField(f.id)}
                  style={{ 
                    background: selectedFields.includes(f.id) ? '#fff5f5' : '#f8fafc',
                    borderColor: selectedFields.includes(f.id) ? '#fee2e2' : '#f1f5f9'
                  }}
                >
                  <span style={{ 
                    background: selectedFields.includes(f.id) ? '#b91c1c' : '#cbd5e1' 
                  }}>
                    {selectedFields.includes(f.id) && <i className="bi bi-check-lg text-white text-[10px]"></i>}
                  </span>
                  {f.label}
                </div>
              ))}
            </div>

            <div className="export-footer-actions-v3">
              <button
                type="button"
                className="select-all-v3"
                onClick={handleToggleSelectAll}
                title={allSelected ? 'Deselect all columns' : 'Select all columns'}
              >
                <span
                  className={`select-all-v3__box ${allSelected ? 'select-all-v3__box--checked' : ''}`}
                >
                  {allSelected && <i className="bi bi-check-lg text-white text-[10px]"></i>}
                </span>
                <span className="select-all-v3__label">SELECT ALL</span>
              </button>
              <div className="selection-count-v3">
                {selectedFields.length} OF {allFields.length} SELECTED
              </div>
            </div>
          </div>
        </div>
      </div>
      


    </>
  );
};



/* ==========================================
   MERGED COMPONENTS
   ========================================== */


/* --- Component from DrillDownDashboard.jsx --- */
const TRUE_RED = '#c62828';

const getPerformanceCategory = (score) => {
  if (score >= 90) return { label: 'Top Performer', color: '#10b981' };
  if (score >= 75) return { label: 'Strong Performer', color: '#3b82f6' };
  if (score >= 50) return { label: 'Average Performer', color: '#f59e0b' };
  return { label: 'Needs Improvement', color: '#ef4444' };
};

function DrillDownDashboard({ onBack }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const [drillLevel, setDrillLevel] = useState(1); // 1: Dept, 2: Emp List, 3: Emp Detail
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedEmp, setSelectedEmp] = useState(null);

  const [search, setSearch] = useState('');
  const [testFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  useEffect(() => {
    fetchData();
  }, [testFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await assessmentApi.get('/assessment/analytics/dashboard', {
        params: { test_name: testFilter }
      });
      if (res.data?.status === 'success') {
        setData(res.data);
      }
    } catch {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const candidates = useMemo(() => {
    if (!data?.candidates_data) return [];
    let list = data.candidates_data;
    if (search) {
      const lowerSearch = search.toLowerCase();
      list = list.filter(c => 
        (c.name && c.name.toLowerCase().includes(lowerSearch)) || 
        (c.emp_id && c.emp_id.toLowerCase().includes(lowerSearch))
      );
    }
    if (roleFilter) {
      list = list.filter(c => c.role === roleFilter);
    }
    return list;
  }, [data, search, roleFilter]);

  const roles = useMemo(() => {
    if (!data?.candidates_data) return [];
    return [...new Set(data.candidates_data.map(c => c.role).filter(Boolean))];
  }, [data]);

  const departmentStats = useMemo(() => {
    const map = {};
    candidates.forEach(c => {
      const dept = c.department || 'General';
      if (!map[dept]) {
        map[dept] = { department: dept, total: 0, sumScore: 0, passCount: 0, failCount: 0 };
      }
      map[dept].total += 1;
      map[dept].sumScore += c.overall_pct;
      if (c.overall_pct >= c.pass_percentage) map[dept].passCount += 1;
      else map[dept].failCount += 1;
    });
    return Object.values(map).map(d => ({
      ...d,
      avgScore: Math.round(d.sumScore / d.total),
      passPct: Math.round((d.passCount / d.total) * 100),
      failPct: Math.round((d.failCount / d.total) * 100),
    }));
  }, [candidates]);

  const departmentEmployees = useMemo(() => {
    if (!selectedDept) return [];
    const list = candidates.filter(c => (c.department || 'General') === selectedDept);
    list.sort((a, b) => b.overall_pct - a.overall_pct);
    return list.map((c, idx) => ({
      ...c,
      rank: idx + 1,
      perfCategory: getPerformanceCategory(c.overall_pct)
    }));
  }, [candidates, selectedDept]);

  const employeeData = useMemo(() => {
    if (!selectedEmp) return null;
    const userSubmissions = data?.candidates_data?.filter(c => c.user_id === selectedEmp.user_id) || [];
    userSubmissions.sort((a, b) => new Date(a.submitted_at) - new Date(b.submitted_at));
    
    // Overall Stats
    const highestScore = Math.max(...userSubmissions.map(s => s.overall_pct));
    const avgScore = Math.round(userSubmissions.reduce((sum, s) => sum + s.overall_pct, 0) / userSubmissions.length);
    
    // Overall Rank across all
    const allAverages = {};
    data?.candidates_data?.forEach(c => {
      if (!allAverages[c.user_id]) allAverages[c.user_id] = { sum: 0, count: 0 };
      allAverages[c.user_id].sum += c.overall_pct;
      allAverages[c.user_id].count += 1;
    });
    const rankList = Object.keys(allAverages).map(uid => ({
      uid: parseInt(uid),
      avg: Math.round(allAverages[uid].sum / allAverages[uid].count)
    })).sort((a, b) => b.avg - a.avg);
    const overallRank = rankList.findIndex(r => r.uid === selectedEmp.user_id) + 1;

    // Pedagogy Stats
    const pedStats = {};
    userSubmissions.forEach(sub => {
      if (sub.pedagogy_stats) {
        Object.entries(sub.pedagogy_stats).forEach(([ped, stats]) => {
          if (!pedStats[ped]) pedStats[ped] = { total: 0, correct: 0 };
          pedStats[ped].total += stats.total;
          pedStats[ped].correct += stats.correct;
        });
      }
    });

    const radarData = Object.entries(pedStats).map(([ped, stats]) => ({
      subject: ped,
      score: Math.round((stats.correct / stats.total) * 100),
      fullMark: 100
    }));

    let strongest = { ped: 'N/A', score: -1 };
    let weakest = { ped: 'N/A', score: 101 };
    radarData.forEach(d => {
      if (d.score > strongest.score) strongest = { ped: d.subject, score: d.score };
      if (d.score < weakest.score) weakest = { ped: d.subject, score: d.score };
    });

    const bestAssessment = userSubmissions.reduce((prev, current) => (prev.overall_pct > current.overall_pct) ? prev : current, userSubmissions[0]);
    const lowestAssessment = userSubmissions.reduce((prev, current) => (prev.overall_pct < current.overall_pct) ? prev : current, userSubmissions[0]);

    return {
      submissions: userSubmissions,
      highestScore,
      avgScore,
      overallRank,
      radarData,
      strongest,
      weakest,
      bestAssessment,
      lowestAssessment
    };
  }, [selectedEmp, data]);

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Analytics Export', 14, 20);
    
    let rows = [];
    let head = [];
    if (drillLevel === 1) {
      head = [['Department', 'Total Candidates', 'Avg Score', 'Pass %', 'Fail %']];
      rows = departmentStats.map(d => [d.department, d.total, d.avgScore, d.passPct, d.failPct]);
    } else if (drillLevel === 2) {
      head = [['Rank', 'Employee', 'ID', 'Score', 'Category']];
      rows = departmentEmployees.map(e => [e.rank, e.name, e.emp_id, e.overall_pct, e.perfCategory.label]);
    } else if (drillLevel === 3) {
      head = [['Test', 'Date', 'Score', 'Pass Mark']];
      rows = employeeData.submissions.map(s => [s.assessment_name, s.submitted_at.split(' ')[0], s.overall_pct, s.pass_mark]);
      doc.setFontSize(12);
      doc.text(`Employee: ${selectedEmp.name}`, 14, 30);
    }

    autoTable(doc, {
      startY: drillLevel === 3 ? 35 : 25,
      head,
      body: rows,
      theme: 'grid',
      headStyles: { fillColor: [198, 40, 40] }
    });

    doc.save(buildAssessmentExportFilename(`Analytics_Export_Level_${drillLevel}`, 'pdf'));
  };

  const handleExportExcel = () => {
    let wsData = [];
    if (drillLevel === 1) {
      wsData = [['Department', 'Total Candidates', 'Avg Score', 'Pass %', 'Fail %'], ...departmentStats.map(d => [d.department, d.total, d.avgScore, d.passPct, d.failPct])];
    } else if (drillLevel === 2) {
      wsData = [['Rank', 'Employee', 'ID', 'Score', 'Category'], ...departmentEmployees.map(e => [e.rank, e.name, e.emp_id, e.overall_pct, e.perfCategory.label])];
    } else if (drillLevel === 3) {
      wsData = [['Employee Name', selectedEmp.name], [], ['Test', 'Date', 'Score', 'Pass Mark'], ...employeeData.submissions.map(s => [s.assessment_name, s.submitted_at.split(' ')[0], s.overall_pct, s.pass_mark])];
    }
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Analytics");
    XLSX.writeFile(wb, buildAssessmentExportFilename(`Analytics_Export_Level_${drillLevel}`, 'xlsx'));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="w-12 h-12 border-4 border-red-600/20 border-t-red-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-800 font-sans">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-8 py-5 sticky top-0 z-50 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => {
              if (drillLevel === 3) { setDrillLevel(2); setSelectedEmp(null); }
              else if (drillLevel === 2) { setDrillLevel(1); setSelectedDept(null); }
              else if (onBack) onBack();
            }}
            className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-500 transition-colors"
          >
            <FiArrowLeft className="text-xl" />
          </button>
          <div>
            <h1 className="text-2xl font-black tracking-tight text-slate-900">Drill-Down Analytics</h1>
            <div className="flex items-center gap-2 text-xs font-bold text-slate-500 mt-1 uppercase tracking-widest">
              <span>Overview</span>
              {drillLevel >= 2 && <><FiChevronRight /> <span>{selectedDept}</span></>}
              {drillLevel >= 3 && <><FiChevronRight /> <span>{selectedEmp?.name}</span></>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              type="text" 
              placeholder="Search Employee..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 w-64 transition-all"
            />
          </div>
          <button onClick={handleExportPDF} className="btn-assessment !bg-slate-800 hover:!bg-slate-900">
            <FiDownload /> PDF
          </button>
          <button onClick={handleExportExcel} className="btn-assessment !bg-emerald-600 hover:!bg-emerald-700">
            <FiDownload /> Excel
          </button>
        </div>
      </div>

      <div className="p-8 max-w-[1600px] mx-auto">
        <AnimatePresence mode="wait">
          {/* LEVEL 1: Department Dashboard */}
          {drillLevel === 1 && (
            <motion.div key="level1" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><FiTarget className="text-red-600"/> Department Performance</h2>
                <div className="flex items-center gap-3">
                  <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium outline-none">
                    <option value="">All Roles</option>
                    {roles.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm col-span-1 lg:col-span-2">
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-6">Average Score & Pass/Fail by Department</h3>
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={departmentStats} onClick={(data) => {
                        if (data && data.activePayload) {
                          setSelectedDept(data.activePayload[0].payload.department);
                          setDrillLevel(2);
                        }
                      }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="department" axisLine={false} tickLine={false} tick={{ fontSize: 12, fontWeight: 600, fill: '#64748b' }} dy={10} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fontWeight: 600, fill: '#64748b' }} dx={-10} />
                        <RechartsTooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                        <Legend wrapperStyle={{ paddingTop: '20px' }} />
                        <Bar dataKey="avgScore" name="Avg Score" fill={TRUE_RED} radius={[4, 4, 0, 0]} maxBarSize={60} />
                        <Bar dataKey="passPct" name="Pass %" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={60} />
                        <Bar dataKey="failPct" name="Fail %" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={60} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* LEVEL 2: Employee Performance */}
          {drillLevel === 2 && (
            <motion.div key="level2" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><FiUser className="text-blue-600"/> {selectedDept} Employees</h2>
              </div>

              <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-6">Current Test Performance (Ranking)</h3>
                <div className="h-[600px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart layout="vertical" data={departmentEmployees} onClick={(data) => {
                      if (data && data.activePayload) {
                        setSelectedEmp(data.activePayload[0].payload);
                        setDrillLevel(3);
                      }
                    }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                      <XAxis type="number" domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} />
                      <YAxis type="category" dataKey="name" width={150} axisLine={false} tickLine={false} tick={{ fontSize: 12, fontWeight: 600, fill: '#475569' }} />
                      <RechartsTooltip cursor={{ fill: '#f8fafc' }} content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const d = payload[0].payload;
                          return (
                            <div className="bg-white p-4 rounded-xl shadow-lg border border-slate-100">
                              <p className="font-bold text-slate-800">{d.name}</p>
                              <p className="text-xs text-slate-500 mb-2">ID: {d.emp_id}</p>
                              <p className="text-sm"><span className="font-semibold">Score:</span> {d.overall_pct}%</p>
                              <p className="text-sm"><span className="font-semibold">Rank:</span> #{d.rank}</p>
                              <div className="mt-2 inline-block px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: d.perfCategory.color + '20', color: d.perfCategory.color }}>
                                {d.perfCategory.label}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      }} />
                      <Bar dataKey="overall_pct" radius={[0, 4, 4, 0]} barSize={24} cursor="pointer">
                        {departmentEmployees.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.perfCategory.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </motion.div>
          )}

          {/* LEVEL 3: Employee Analytics */}
          {drillLevel === 3 && employeeData && (
            <motion.div key="level3" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-6">
              
              {/* Section A: KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {[
                  { label: 'Current Score', value: `${selectedEmp.overall_pct}%`, icon: FiTarget, color: 'text-blue-600', bg: 'bg-blue-50' },
                  { label: 'Dept Rank', value: `#${selectedEmp.rank}`, icon: FiAward, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                  { label: 'Overall Rank', value: `#${employeeData.overallRank}`, icon: FiTrendingUp, color: 'text-purple-600', bg: 'bg-purple-50' },
                  { label: 'Highest Score', value: `${employeeData.highestScore}%`, icon: FiAward, color: 'text-amber-600', bg: 'bg-amber-50' },
                  { label: 'Average Score', value: `${employeeData.avgScore}%`, icon: FiTarget, color: 'text-slate-600', bg: 'bg-slate-50' },
                  { label: 'Tests', value: employeeData.submissions.length, icon: FiAlertCircle, color: 'text-red-600', bg: 'bg-red-50' },
                ].map((kpi, i) => (
                  <div key={i} className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-center relative overflow-hidden group">
                    <div className={`w-10 h-10 rounded-xl ${kpi.bg} ${kpi.color} flex items-center justify-center mb-3 transition-transform group-hover:scale-110`}>
                      <kpi.icon className="text-xl" />
                    </div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{kpi.label}</span>
                    <span className="text-2xl font-black text-slate-800 mt-1">{kpi.value}</span>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Section B: Test Comparison */}
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-6">Test Comparison Analysis</h3>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={employeeData.submissions}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="assessment_name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b' }} dy={10} tickFormatter={(v) => v.length > 15 ? v.substring(0,15)+'...' : v} />
                        <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dx={-10} />
                        <RechartsTooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                        <Bar dataKey="overall_pct" name="Score" fill={TRUE_RED} radius={[4, 4, 0, 0]} maxBarSize={40} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Section C: Pedagogy Analysis */}
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-6">Pedagogy Analysis</h3>
                  {employeeData.radarData.length > 0 ? (
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <RadarChart outerRadius={90} data={employeeData.radarData}>
                          <PolarGrid stroke="#e2e8f0" />
                          <PolarAngleAxis dataKey="subject" tick={{ fill: '#475569', fontSize: 10, fontWeight: 600 }} />
                          <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                          <Radar name="Score %" dataKey="score" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                          <RechartsTooltip />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-sm font-bold text-slate-400">No Pedagogy Data Available</div>
                  )}
                </div>

                {/* Section D: Overall Assessment Performance (Line Chart) */}
                <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                  <h3 className="text-sm font-bold text-slate-500 uppercase tracking-widest mb-6">Learning Progression</h3>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={employeeData.submissions}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis dataKey="submitted_at" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#64748b' }} dy={10} tickFormatter={v => v.split(' ')[0]} />
                        <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#64748b' }} dx={-10} />
                        <RechartsTooltip cursor={{ stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '3 3' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                        <Line type="monotone" dataKey="overall_pct" name="Score" stroke="#10b981" strokeWidth={3} dot={{ r: 4, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Section E: Strength & Weakness Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-emerald-50 border border-emerald-100 p-6 rounded-2xl flex flex-col justify-center">
                    <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-1">Strongest Pedagogy</span>
                    <span className="text-xl font-black text-emerald-900">{employeeData.strongest.ped} ({employeeData.strongest.score}%)</span>
                  </div>
                  <div className="bg-red-50 border border-red-100 p-6 rounded-2xl flex flex-col justify-center">
                    <span className="text-xs font-bold text-red-600 uppercase tracking-widest mb-1">Weakest Pedagogy</span>
                    <span className="text-xl font-black text-red-900">{employeeData.weakest.ped} ({employeeData.weakest.score > -1 ? employeeData.weakest.score+'%' : 'N/A'})</span>
                  </div>
                  <div className="bg-blue-50 border border-blue-100 p-6 rounded-2xl flex flex-col justify-center">
                    <span className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-1">Best Test</span>
                    <span className="text-lg font-black text-blue-900 line-clamp-2">{employeeData.bestAssessment.assessment_name} ({employeeData.bestAssessment.overall_pct}%)</span>
                  </div>
                  <div className="bg-amber-50 border border-amber-100 p-6 rounded-2xl flex flex-col justify-center">
                    <span className="text-xs font-bold text-amber-600 uppercase tracking-widest mb-1">Lowest Test</span>
                    <span className="text-lg font-black text-amber-900 line-clamp-2">{employeeData.lowestAssessment.assessment_name} ({employeeData.lowestAssessment.overall_pct}%)</span>
                  </div>
                </div>

              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* --- Component from RolePerformanceDashboard.jsx --- */
// const TRUE_RED = '#c62828';




function RolePerformanceDashboard({ role, allCandidates, onBack, onViewCandidate }) {
  // role is the selected role object (e.g. { role: "Software Engineer", total_attempts: 10, ... })
  // allCandidates is data.candidates_data from AnalyticsDashboard
  
  // Filter candidates for this role
  const roleCandidates = useMemo(() => {
    return allCandidates.filter(c => c.role === role.role);
  }, [allCandidates, role]);

  // Aggregate metrics & process candidates for the table
  const processedCandidates = useMemo(() => {
    // Group by user_id to compute previous score and trend
    const userGroups = {};
    roleCandidates.forEach(c => {
      const key = c.user_id || c.name;
      if (!userGroups[key]) userGroups[key] = [];
      userGroups[key].push(c);
    });

    return Object.values(userGroups).map(group => {
      // Sort by submitted_at descending (latest first)
      group.sort((a, b) => {
        const da = new Date(a.submitted_at || a.date_time || 0).getTime();
        const db = new Date(b.submitted_at || b.date_time || 0).getTime();
        return db - da;
      });

      const latest = group[0];
      const previous = group.length > 1 ? group[1] : null;

      const previous_score = previous ? (previous.overall_pct || 0) : null;
      const trend = previous_score !== null ? (latest.overall_pct || 0) - previous_score : null;

      let totalP = 0, correctP = 0;
      if (latest.pedagogy_stats) {
        Object.values(latest.pedagogy_stats).forEach(s => {
          totalP += s.total;
          correctP += s.correct;
        });
      }
      const skillScore = totalP > 0 ? Math.round((correctP / totalP) * 100) : (latest.overall_pct || 0);
      const pedagogyScore = skillScore; // For now treating pedagogy and skill as equivalent metrics in display
      
      const pct = latest.overall_pct ?? 0;
      const status = pct >= 75 ? 'High' : (pct >= (latest.pass_percentage || 50) ? 'Medium' : 'Low');
      
      // Determine Primary Mindset Weakness (Zero-Click Root Cause)
      let mindsetWeakness = null;
      if (latest.pedagogy_stats) {
        let lowestPct = 100;
        Object.entries(latest.pedagogy_stats).forEach(([skill, s]) => {
           if (s.total > 0) {
              const skillPct = Math.round((s.correct / s.total) * 100);
              if (skillPct < lowestPct && skillPct < 50) {
                 lowestPct = skillPct;
                 mindsetWeakness = skill;
              }
           }
        });
      }
      // Consistency Risk Profile
      let consistencyProfile = 'Consistent';
      let varianceBadge = 'blue';
      if (latest.pedagogy_stats) {
        const scores = Object.values(latest.pedagogy_stats)
           .filter(s => s.total > 0)
           .map(s => (s.correct / s.total) * 100);
        
        if (scores.length > 1) {
          const mean = scores.reduce((a, b) => a + b, 0) / scores.length;
          const variance = scores.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / scores.length;
          const stdDev = Math.sqrt(variance);
          
          if (stdDev > 20) {
             consistencyProfile = 'Erratic (High Risk)';
             varianceBadge = 'red';
          } else if (stdDev > 10) {
             consistencyProfile = 'Moderate Risk';
             varianceBadge = 'amber';
          } else {
             consistencyProfile = 'Highly Consistent';
             varianceBadge = 'emerald';
          }
        }
      }

      return { ...latest, skillScore, pedagogyScore, status, mindsetWeakness, consistencyProfile, varianceBadge, previous_score, trend };
    });
  }, [roleCandidates]);

  // Table State
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTestFilter, setCurrentTestFilter] = useState('All');
  const [currentGroupFilter, setCurrentGroupFilter] = useState('All');
  const [showCurrentCharts, setShowCurrentCharts] = useState(false);
  const [selectedChartCandidate, setSelectedChartCandidate] = useState(null);
  const [selectedCompareTests, setSelectedCompareTests] = useState([]); // Empty means ALL tests
  const [drilldownTab, setDrilldownTab] = useState('progression'); // 'progression' or 'skills'
  
  const [sortConfig, setSortConfig] = useState({ key: 'score', direction: 'desc' }); // sorting config
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Custom label renderer with click handler
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value, index, payload, x, y, textAnchor }) => {
    const displayName = payload.name.length > 15 ? payload.name.substring(0, 15) + '...' : payload.name;
    return (
      <text 
        x={x} 
        y={y} 
        fill={selectedChartCandidate?.user_id === payload.candidate?.user_id ? '#ef4444' : '#475569'} 
        textAnchor={textAnchor} 
        dominantBaseline="central" 
        fontSize={10} 
        fontWeight={800}
        onClick={() => setSelectedChartCandidate(payload.candidate)}
        style={{ cursor: 'pointer', transition: 'fill 0.2s' }}
        className="hover:fill-slate-800"
      >
        {displayName}: <tspan fill="#ef4444">{value}%</tspan>
      </text>
    );
  };

  // Extract unique tests for filter
  const availableTests = useMemo(() => {
    const tests = new Set();
    roleCandidates.forEach(c => { if (c.assessment_name) tests.add(c.assessment_name); });
    return Array.from(tests).sort();
  }, [roleCandidates]);

  // Extract unique groups for filter
  const availableGroups = useMemo(() => {
    if (currentTestFilter === 'All') return [];
    const groups = new Set();
    roleCandidates.forEach(c => { 
      if (c.assessment_name === currentTestFilter && c.pack_name) groups.add(c.pack_name); 
    });
    return Array.from(groups).sort();
  }, [roleCandidates, currentTestFilter]);

  // Auto-select latest group when test changes
  useEffect(() => {
    if (currentTestFilter !== 'All' && availableGroups.length > 0) {
      setCurrentGroupFilter(availableGroups[0]);
    } else {
      setCurrentGroupFilter('All');
    }
  }, [currentTestFilter, availableGroups]);

  const filteredCandidates = useMemo(() => {
    let result = [...processedCandidates];

    if (currentTestFilter !== 'All') {
      result = result.filter(c => c.assessment_name === currentTestFilter);
    }

    if (currentGroupFilter !== 'All') {
      result = result.filter(c => c.pack_name === currentGroupFilter);
    }

    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(c => 
        (c.name || '').toLowerCase().includes(lower) || 
        (c.emp_id || '').toLowerCase().includes(lower)
      );
    }

    result.sort((a, b) => {
      let valA, valB;
      if (sortConfig.key === 'score') {
        valA = a.overall_pct || 0;
        valB = b.overall_pct || 0;
      } else if (sortConfig.key === 'name') {
        valA = (a.name || '').toLowerCase();
        valB = (b.name || '').toLowerCase();
      } else if (sortConfig.key === 'pedagogyScore') {
        valA = a.pedagogyScore || 0;
        valB = b.pedagogyScore || 0;
      } else if (sortConfig.key === 'skillScore') {
        valA = a.skillScore || 0;
        valB = b.skillScore || 0;
      }

      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;

      // Tie breaker: less time (duration) is better
      const durA = a.duration || '99:99:99';
      const durB = b.duration || '99:99:99';
      if (durA < durB) return -1;
      if (durA > durB) return 1;

      return 0;
    });

    return result;
  }, [processedCandidates, searchTerm, sortConfig, currentTestFilter, currentGroupFilter]);

  const totalPages = Math.ceil(filteredCandidates.length / itemsPerPage);
  const currentCandidates = filteredCandidates.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortConfig, currentTestFilter, currentGroupFilter]);

  // Reset chart selection when filters change
  useEffect(() => {
    setSelectedChartCandidate(null);
    setSelectedCompareTests([]);
  }, [currentTestFilter, showCurrentCharts]);

  // Reset comparison tests when candidate changes
  useEffect(() => {
    setSelectedCompareTests([]);
  }, [selectedChartCandidate]);

  // Data for Current Performance Charts (Employees)
  const employeeChartData = useMemo(() => {
    if (!showCurrentCharts) return [];
    return filteredCandidates.map((c, i) => {
      const score = c.overall_pct || 0;
      let riskColor = '#ef4444'; // Red (At Risk)
      if (score >= 75) {
        riskColor = '#10b981'; // Green (High Performer)
      } else if (score >= 50) {
        riskColor = '#f59e0b'; // Yellow (Average)
      }
      return {
        name: c.name,
        value: score,
        color: riskColor,
        candidate: c
      };
    }).filter(d => d.value > 0);
  }, [filteredCandidates, showCurrentCharts]);

  // Group Averages for Comparison Chart
  const groupAverages = useMemo(() => {
    const stats = {};
    let count = 0;
    filteredCandidates.forEach(c => {
      count++;
      if (c.pedagogy_stats) {
        Object.entries(c.pedagogy_stats).forEach(([skill, data]) => {
          if (!stats[skill]) stats[skill] = { total: 0, correct: 0 };
          stats[skill].total += data.total;
          stats[skill].correct += data.correct;
        });
      }
    });
    const result = {};
    Object.entries(stats).forEach(([skill, data]) => {
      result[skill] = data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0;
    });
    return result;
  }, [filteredCandidates]);

  // Comparison Data for Selected Candidate
  const comparisonData = useMemo(() => {
    if (!selectedChartCandidate || !selectedChartCandidate.pedagogy_stats) return [];
    return Object.entries(selectedChartCandidate.pedagogy_stats).map(([skill, data]) => {
      const empScore = data.total > 0 ? Math.round((data.correct / data.total) * 100) : 0;
      return {
        skill: skill.length > 12 ? skill.substring(0, 12) + '...' : skill,
        fullSkill: skill,
        employeeScore: empScore,
        groupAverage: groupAverages[skill] || 0
      };
    });
  }, [selectedChartCandidate, groupAverages]);

  // Unique tests for this specific candidate
  const candidateTests = useMemo(() => {
    if (!selectedChartCandidate) return [];
    const tests = roleCandidates.filter(c => c.user_id === selectedChartCandidate.user_id);
    const tNames = new Set();
    tests.forEach(t => { if (t.assessment_name) tNames.add(t.assessment_name); });
    return Array.from(tNames).sort();
  }, [selectedChartCandidate, roleCandidates]);

  // Candidate vs Group Average Comparison Data
  const employeeData = useMemo(() => {
    if (!selectedChartCandidate) return { flat: [], grouped: [], keys: [] };
    
    // Find all tests taken by this specific employee and apply comparison filter
    let tests = roleCandidates.filter(c => c.user_id === selectedChartCandidate.user_id);
    if (selectedCompareTests.length > 0) {
      tests = tests.filter(t => selectedCompareTests.includes(t.assessment_name));
    }
    
    const flat = [];
    const groupedMap = {};
    const keys = new Set();
    
    const testCounts = {};
    tests.forEach(t => {
      const sig = `${t.assessment_name}_${t.pack_name}`;
      testCounts[sig] = (testCounts[sig] || 0) + 1;
    });
    
    const currentCounts = {};
    
    tests.forEach(t => {
      const sig = `${t.assessment_name}_${t.pack_name}`;
      currentCounts[sig] = (currentCounts[sig] || 0) + 1;
      
      const baseName = t.assessment_name || 'Unknown Test';
      const groupSuffix = t.pack_name ? ` (${t.pack_name})` : '';
      let finalName = baseName + groupSuffix;
      
      if (testCounts[sig] > 1) {
        finalName += ` - Att ${currentCounts[sig]}`;
      }
      
      const score = t.overall_pct || 0;
      const passMark = t.pass_percentage || 50;

      // Add to flat list for insights
      flat.push({
        testName: finalName.length > 20 ? finalName.substring(0, 20) + '...' : finalName,
        fullTestName: finalName,
        candidateScore: score,
        passMark: passMark
      });
      
      // Group for BarChart (Grouped by baseName)
      if (!groupedMap[baseName]) {
        groupedMap[baseName] = { 
          testName: baseName.length > 20 ? baseName.substring(0, 20) + '...' : baseName,
          fullTestName: baseName 
        };
      }
      
      let keyName = t.pack_name ? t.pack_name : `Attempt ${currentCounts[sig]}`;
      if (t.pack_name && testCounts[sig] > 1) {
        keyName = `${t.pack_name} - Att ${currentCounts[sig]}`;
      }
      
      keys.add(keyName);
      groupedMap[baseName][keyName] = score;
      groupedMap[baseName][`${keyName}_passMark`] = passMark;
    });
    
    return {
      flat,
      grouped: Object.values(groupedMap),
      keys: Array.from(keys).sort()
    };
  }, [selectedChartCandidate, roleCandidates]);
  
  const employeeComparisonData = employeeData.flat;

  // Prepare data for Pace Analytics Scatter Chart
  const paceData = useMemo(() => {
     if (!selectedChartCandidate) return [];
     // For this we will show ALL candidates in the selected tests for the role to build the quadrant,
     // and highlight the current candidate.
     let candidates = roleCandidates;
     if (selectedCompareTests.length > 0) {
        candidates = candidates.filter(c => selectedCompareTests.includes(c.assessment_name));
     }
     
     return candidates.map(c => ({
        name: c.name,
        score: c.overall_pct || 0,
        time: parseInt(c.duration || c.time_taken || (Math.random() * 30 + 10)), // Simulated or real duration fallback
        isCurrent: c.user_id === selectedChartCandidate.user_id
     }));
  }, [roleCandidates, selectedChartCandidate, selectedCompareTests]);

  // Pace Custom Tooltip
  const PaceTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-slate-800 text-white p-3 rounded-lg shadow-xl border border-slate-700 text-xs">
          <div className="font-black mb-1">{data.name}</div>
          <div className="text-slate-300">Score: <span className="text-white font-bold">{data.score}%</span></div>
          <div className="text-slate-300">Time Taken: <span className="text-white font-bold">{data.time} mins</span></div>
        </div>
      );
    }
    return null;
  };

  // Initialize AmCharts 5 for Assessment Progression
  useEffect(() => {
    if (drilldownTab !== 'progression' || !employeeData || employeeData.grouped.length === 0) return;

    let root = am5.Root.new("amchart-progression");

    root.setThemes([am5themes_Animated.new(root)]);

    let chart = root.container.children.push(am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      wheelX: "none",
      wheelY: "none",
      layout: root.verticalLayout,
      paddingLeft: 0,
      paddingRight: 0
    }));

    let xRenderer = am5xy.AxisRendererX.new(root, {
      cellStartLocation: 0.2,
      cellEndLocation: 0.8,
      minGridDistance: 30
    });
    
    xRenderer.labels.template.setAll({
      fontSize: 10,
      fontWeight: "bold",
      fill: am5.color(0x64748b)
    });
    xRenderer.grid.template.set("visible", false);

    let xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
      categoryField: "testName",
      renderer: xRenderer,
      tooltip: am5.Tooltip.new(root, {})
    }));
    
    let yRenderer = am5xy.AxisRendererY.new(root, {});
    yRenderer.labels.template.setAll({
      fontSize: 10,
      fontWeight: "bold",
      fill: am5.color(0x64748b)
    });
    yRenderer.grid.template.set("visible", false);

    let yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      min: 0,
      max: 100,
      renderer: yRenderer
    }));

    let data = employeeData.grouped; 
    xAxis.data.setAll(data);

    let legend = chart.children.push(am5.Legend.new(root, {
      centerX: am5.p50,
      x: am5.p50,
      marginTop: 20
    }));
    legend.labels.template.setAll({
      fontSize: 10,
      fontWeight: "bold"
    });

    const colors = [am5.color(0xc62828), am5.color(0x1e293b), am5.color(0x64748b), am5.color(0xcbd5e1), am5.color(0x0ea5e9)];

    employeeData.keys.forEach((key, index) => {
      let series = chart.series.push(am5xy.ColumnSeries.new(root, {
        name: key,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: key,
        categoryXField: "testName",
        tooltip: am5.Tooltip.new(root, {
          labelText: "{name}: {valueY}%"
        })
      }));

      series.columns.template.setAll({
        width: am5.percent(90),
        tooltipY: 0,
        strokeOpacity: 0,
        fill: colors[index % colors.length],
        cornerRadiusTR: 4,
        cornerRadiusTL: 4
      });

      // Add score labels on top of columns
      series.bullets.push(function() {
        return am5.Bullet.new(root, {
          locationY: 1,
          sprite: am5.Label.new(root, {
            text: "{valueY}%",
            fill: colors[index % colors.length],
            centerY: am5.p100,
            centerX: am5.p50,
            populateText: true,
            fontSize: 9,
            fontWeight: "bold"
          })
        });
      });

      series.data.setAll(data);
      series.appear();
    });

    legend.data.setAll(chart.series.values);
    chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [employeeData, drilldownTab]);

  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };



  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-8 mt-2"
    >
      {/* Breadcrumb Navigation & Back Button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2 text-xs font-bold text-slate-500 uppercase tracking-widest bg-slate-50 px-4 py-2 rounded-lg w-fit">
          <span className="cursor-pointer hover:text-red-600 transition-colors" onClick={onBack}>Home</span>
          <FiChevronRight />
          <span className="text-slate-800">{role.role ? role.role.trim() : ''}</span>
          <FiChevronRight />
          <span className="text-red-600">Performance</span>
        </div>
        <button 
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 bg-slate-50 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition-all border border-slate-200"
        >
          <FiArrowLeft /> Back to Overview
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4 border-b border-slate-100 pb-6">
        <div>
          <h2 className="text-2xl font-black text-slate-800 tracking-tight">{role.role ? role.role.trim() : ''} Analytics</h2>
          <p className="text-xs text-slate-500 font-semibold mt-1">Detailed candidate analysis</p>
        </div>
      </div>

      <motion.div
        key="employees"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
            {/* Table Header Controls */}
            <div className="flex flex-col md:flex-row items-center justify-end mb-6 gap-4">
              <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 w-full md:w-64 shadow-sm">
                <FiSearch className="text-red-500" />
                <input 
                  type="text" 
                  placeholder="Search Name or ID..." 
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="bg-transparent border-none outline-none text-xs font-bold text-slate-600 focus:ring-0 w-full"
                />
              </div>

              <select 
                value={currentTestFilter}
                onChange={e => setCurrentTestFilter(e.target.value)}
                className="bg-white border border-slate-200 rounded-full px-5 py-2 text-xs font-bold text-slate-700 outline-none flex-1 md:flex-none cursor-pointer shadow-sm"
              >
                <option value="All">All Tests</option>
                {availableTests.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              
              <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-full px-5 py-2 shadow-sm">
                <span className="text-[10px] font-black text-red-500 uppercase tracking-widest">Chart</span>
                <div 
                  className={`w-9 h-5 rounded-full flex items-center cursor-pointer p-1 transition-colors ${showCurrentCharts ? 'bg-red-500' : 'bg-slate-200'}`}
                  onClick={() => setShowCurrentCharts(!showCurrentCharts)}
                >
                  <motion.div 
                    layout
                    className="bg-white w-3 h-3 rounded-full shadow-sm"
                    animate={{ x: showCurrentCharts ? 16 : 0 }}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                </div>
              </div>
            </div>

            <AnimatePresence>
              {showCurrentCharts && (
                <motion.div
                  initial={{ opacity: 0, height: 0, scale: 0.95 }}
                  animate={{ opacity: 1, height: 'auto', scale: 1 }}
                  exit={{ opacity: 0, height: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden mb-8"
                >
                  <div className="bg-white border border-slate-100 rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] relative">
                    <div className="absolute top-8 left-8 flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center text-red-500">
                        <FiTarget className="text-sm" />
                      </div>
                      <div>
                        <h3 className="text-[11px] font-black text-slate-700 uppercase tracking-widest">Performance Metrics</h3>
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">Role-Based Analytics View</p>
                      </div>
                    </div>
                    
                    <div className="w-full flex flex-col lg:flex-row items-center justify-between pt-16 md:pt-0">
                      {/* Left: Donut Chart of Employees */}
                      <div className="w-full lg:w-1/2 flex flex-col items-center">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                            Employee Performance
                          </div>
                          {currentTestFilter !== 'All' && availableGroups.length > 0 && (
                            <select 
                              value={currentGroupFilter}
                              onChange={e => setCurrentGroupFilter(e.target.value)}
                              className="bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5 text-[9px] font-bold text-slate-700 outline-none cursor-pointer shadow-sm max-w-[120px]"
                            >
                              <option value="All">All Groups</option>
                              {availableGroups.map(g => <option key={g} value={g}>{g}</option>)}
                            </select>
                          )}
                        </div>
                        <div className="relative w-full h-[350px]">
                          {employeeChartData.length > 0 ? (
                            <>
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  <Pie
                                    data={employeeChartData}
                                    innerRadius={70}
                                    outerRadius={95}
                                    paddingAngle={2}
                                    dataKey="value"
                                    stroke="none"
                                    onClick={(data) => setSelectedChartCandidate(data.payload.candidate)}
                                    className="cursor-pointer"
                                    label={renderCustomLabel}
                                    labelLine={{ stroke: '#cbd5e1', strokeWidth: 1 }}
                                  >
                                    {employeeChartData.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                  </Pie>
                                  <Tooltip 
                                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: '12px', fontWeight: 'bold' }} 
                                    itemStyle={{ color: '#1e293b' }}
                                    formatter={(value) => [`${value}%`, 'Score']}
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Total</span>
                                <span className="text-4xl font-black text-slate-800">{filteredCandidates.length}</span>
                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-[0.2em] mt-1">Candidates</span>
                              </div>
                            </>
                          ) : (
                            <div className="flex items-center justify-center h-full text-slate-400 font-bold text-sm bg-slate-50 rounded-full">
                              No data
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Right: Drill-down Analysis */}
                      <div className="w-full lg:w-1/2 border-t lg:border-t-0 lg:border-l border-slate-100 pt-8 lg:pt-0 lg:pl-8 flex flex-col min-h-[420px] h-full">
                        {selectedChartCandidate ? (
                          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="flex flex-col h-full w-full">
                            {/* Profile Header */}
                            <div className="flex items-start justify-between mb-4">
                              <div>
                                <h4 className="text-lg font-black text-slate-800">{selectedChartCandidate.name}</h4>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{selectedChartCandidate.emp_id}</p>
                              </div>
                              <div className="flex flex-col items-end">
                                <div className="text-2xl font-black text-red-500">{selectedChartCandidate.overall_pct || 0}%</div>
                                <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Overall Score</div>
                              </div>
                            </div>

                            {/* Key Metrics Row */}
                            <div className="grid grid-cols-2 gap-3 mb-6">
                              <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
                                 <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Performance Status</div>
                                 <div className="text-xs font-bold" style={{ color: selectedChartCandidate.status === 'High' ? '#10b981' : selectedChartCandidate.status === 'Medium' ? '#f59e0b' : '#ef4444' }}>
                                   {selectedChartCandidate.status} Performer
                                 </div>
                              </div>
                              <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 flex flex-col justify-center">
                                 <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1">Variance to Role Avg</div>
                                 <div className="text-xs font-bold flex items-center gap-1">
                                   {(() => {
                                      const avg = Object.values(groupAverages).length > 0 ? Object.values(groupAverages).reduce((a, b) => a + b, 0) / Object.values(groupAverages).length : 0;
                                      const diff = (selectedChartCandidate.overall_pct || 0) - avg;
                                      if (diff > 0) return <><span className="text-emerald-500">+{diff.toFixed(1)}%</span> <span className="text-slate-400 text-[10px]">Above Avg</span></>;
                                      if (diff < 0) return <><span className="text-red-500">{diff.toFixed(1)}%</span> <span className="text-slate-400 text-[10px]">Below Avg</span></>;
                                      return <span className="text-slate-400">Matches Average</span>;
                                   })()}
                                 </div>
                              </div>
                            </div>

                            {/* Test Progression Chart & Insights */}
                            <div className="flex-1 flex flex-col gap-3">
                              {/* Automated Insights Box */}
                              {employeeComparisonData.length > 0 && (
                                <div className="bg-blue-50 rounded-xl p-3 border border-blue-100 flex items-start gap-3 shadow-sm">
                                  <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                                    <FiTrendingUp className="text-blue-600 text-xs" />
                                  </div>
                                  <div className="text-[11px] text-blue-900 leading-relaxed">
                                    {(() => {
                                      if (employeeComparisonData.length === 1) {
                                        const test = employeeComparisonData[0];
                                        return (
                                          <>
                                            <strong className="font-black tracking-wide uppercase text-[9px] text-blue-700">MD Insight:</strong> Candidate has completed <strong>1 test</strong>. 
                                            Achieved a score of <strong>{test.candidateScore}%</strong> in {test.fullTestName}. 
                                            {test.candidateScore >= test.passMark ? <span className="text-emerald-600 font-bold"> (Passed)</span> : <span className="text-red-600 font-bold"> (Failed)</span>}
                                          </>
                                        );
                                      }

                                      const initial = employeeComparisonData[0];
                                      const latest = employeeComparisonData[employeeComparisonData.length - 1];
                                      const improvement = latest.candidateScore - initial.candidateScore;
                                      const avgScore = Math.round(employeeComparisonData.reduce((acc, curr) => acc + curr.candidateScore, 0) / employeeComparisonData.length);
                                      const isPassLatest = latest.candidateScore >= latest.passMark;

                                      return (
                                        <>
                                          <strong className="font-black tracking-wide uppercase text-[9px] text-blue-700">MD Insight:</strong> 
                                          Candidate averages <strong>{avgScore}%</strong> across {employeeComparisonData.length} attempts. 
                                          {improvement > 0 ? (
                                            <span className="text-emerald-600 font-bold"> Showed a solid +{improvement}% improvement</span>
                                          ) : improvement < 0 ? (
                                            <span className="text-red-500 font-bold"> Performance declined by {Math.abs(improvement)}%</span>
                                          ) : (
                                            <span className="text-slate-500 font-bold"> Performance has remained completely flat</span>
                                          )} from their first ({initial.testName}) to latest test ({latest.testName}). 
                                          {isPassLatest ? <span className="text-emerald-600 font-bold"> Currently meeting passing standards.</span> : <span className="text-red-500 font-bold"> Currently falling below passing standards.</span>}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                              )}

                              {/* Tab Navigation for Charts */}
                              <div className="flex flex-col md:flex-row md:items-center justify-between gap-2 mt-1 mb-2">
                                <div className="flex items-center gap-2">
                                  <button 
                                    onClick={() => setDrilldownTab('progression')}
                                    className={`px-4 py-1.5 rounded-full text-[10px] font-bold tracking-wider uppercase transition-colors ${drilldownTab === 'progression' ? 'bg-[#c62828] text-white shadow-md' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                                  >
                                    Assessment History
                                  </button>
                                  <button 
                                    onClick={() => setDrilldownTab('skills')}
                                    className={`px-4 py-1.5 rounded-full text-[10px] font-bold tracking-wider uppercase transition-colors ${drilldownTab === 'skills' ? 'bg-slate-800 text-white shadow-md' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                                  >
                                    Skill Breakdown
                                  </button>
                                </div>
                                
                                {/* Filter to compare specific tests */}
                                {candidateTests.length > 1 && (
                                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-xl">
                                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Compare:</span>
                                    {candidateTests.map(tName => {
                                      const isChecked = selectedCompareTests.length === 0 || selectedCompareTests.includes(tName);
                                      return (
                                        <label key={tName} className="flex items-center gap-1.5 cursor-pointer group">
                                          <input 
                                            type="checkbox" 
                                            className="w-3 h-3 text-[#c62828] bg-white rounded border-slate-300 focus:ring-[#c62828] cursor-pointer"
                                            checked={isChecked}
                                            onChange={() => {
                                              let next = [...selectedCompareTests];
                                              if (next.length === 0) {
                                                next = candidateTests.filter(t => t !== tName);
                                              } else {
                                                if (next.includes(tName)) next = next.filter(t => t !== tName);
                                                else next.push(tName);
                                              }
                                              
                                              if (next.length === candidateTests.length || next.length === 0) {
                                                setSelectedCompareTests([]);
                                              } else {
                                                setSelectedCompareTests(next);
                                              }
                                            }}
                                          />
                                          <span className="text-[10px] font-bold text-slate-600 group-hover:text-slate-800 transition-colors truncate max-w-[100px]" title={tName}>
                                            {tName}
                                          </span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>

                              <div className="flex flex-col gap-3 min-h-[250px]">
                                {drilldownTab === 'progression' && (
                                  <div className="flex-1 relative bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex flex-col min-h-[280px]">
                                    <div className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 text-center">
                                      <span>Assessment Progression</span>
                                    </div>
                                    
                                    {employeeData.flat.length > 0 ? (
                                      <div className="w-full h-full flex justify-center pb-4">
                                        <div id="amchart-progression" style={{ width: '100%', height: '220px' }}></div>
                                      </div>
                                    ) : (
                                      <div className="flex items-center justify-center h-full text-xs text-slate-400 font-bold">No test history.</div>
                                    )}
                                  </div>
                                )}

                                {drilldownTab === 'skills' && (
                                  <div className="flex-1 relative bg-white border border-slate-100 rounded-2xl p-4 shadow-sm flex flex-col min-h-[250px]">
                                    <div className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mb-2 text-center">
                                      Root Cause: Skill Footprint
                                    </div>
                                    <div className="absolute top-4 right-4 flex flex-col gap-1 z-10">
                                      <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                        <span className="text-[9px] font-bold text-slate-500 uppercase">Employee</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                                        <span className="text-[9px] font-bold text-slate-500 uppercase">Role Avg</span>
                                      </div>
                                    </div>
                                    {comparisonData.length > 0 ? (
                                      <div style={{ width: '100%', height: '250px' }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                          <RadarChart cx="50%" cy="50%" outerRadius="65%" data={comparisonData}>
                                            <PolarGrid stroke="#e2e8f0" />
                                            <PolarAngleAxis dataKey="skill" tick={{ fill: '#475569', fontSize: 9, fontWeight: 800 }} />
                                            <Radar name="Role Average" dataKey="groupAverage" stroke="#94a3b8" fill="#cbd5e1" fillOpacity={0.4} />
                                            <Radar name={selectedChartCandidate.name} dataKey="employeeScore" stroke="#ef4444" fill="#ef4444" fillOpacity={0.4} />
                                            <Tooltip 
                                              contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: '11px', fontWeight: 'bold' }}
                                              itemStyle={{ color: '#1e293b' }}
                                            />
                                          </RadarChart>
                                        </ResponsiveContainer>
                                      </div>
                                    ) : (
                                      <div className="flex items-center justify-center h-full text-xs text-slate-400 font-bold">No skill data.</div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Automated Action Plan Engine */}
                              {(() => {
                                const recommendationEngine = () => {
                                    if (employeeComparisonData.length === 0) return null;
                                    
                                    const latest = employeeComparisonData[employeeComparisonData.length - 1];
                                    const initial = employeeComparisonData[0];
                                    const improvement = latest.candidateScore - initial.candidateScore;
                                    const isPassing = latest.candidateScore >= latest.passMark;
                                    const attempts = employeeComparisonData.length;

                                    const sortedSkills = [...comparisonData].sort((a, b) => a.employeeScore - b.employeeScore);
                                    const weakestSkill = sortedSkills.length > 0 ? sortedSkills[0].fullSkill : 'General Knowledge';

                                    if (!isPassing && attempts >= 3) {
                                       return {
                                          icon: <FiAlertCircle className="text-red-600 text-xl mt-0.5" />,
                                          bg: 'bg-red-50 border-red-200',
                                          title: 'ðŸš¨ Immediate Intervention Required',
                                          action: `Candidate has failed ${attempts} times. Assign intensive remedial training focused specifically on ${weakestSkill}.`,
                                          btnColor: 'bg-red-600 hover:bg-red-700 text-white',
                                          btnText: 'Assign Remedial Plan'
                                       };
                                    } else if (!isPassing && improvement < 0) {
                                       return {
                                          icon: <FiActivity className="text-orange-600 text-xl mt-0.5" />,
                                          bg: 'bg-orange-50 border-orange-200',
                                          title: 'âš ï¸ Performance Regressing',
                                          action: `Candidate score dropped by ${Math.abs(improvement)}%. Recommend a 1-on-1 review before next attempt. Focus area: ${weakestSkill}.`,
                                          btnColor: 'bg-orange-600 hover:bg-orange-700 text-white',
                                          btnText: 'Schedule 1-on-1 Review'
                                       };
                                    } else if (!isPassing) {
                                       return {
                                          icon: <FiBookOpen className="text-blue-600 text-xl mt-0.5" />,
                                          bg: 'bg-blue-50 border-blue-200',
                                          title: 'ðŸ“š Targeted Training Suggested',
                                          action: `Candidate is failing. Assign targeted module for ${weakestSkill}.`,
                                          btnColor: 'bg-blue-600 hover:bg-blue-700 text-white',
                                          btnText: `Assign ${weakestSkill} Module`
                                       };
                                    } else {
                                       return {
                                          icon: <FiCheckCircle className="text-emerald-600 text-xl mt-0.5" />,
                                          bg: 'bg-emerald-50 border-emerald-200',
                                          title: 'ðŸŒŸ Ready for Deployment',
                                          action: 'Candidate is meeting passing standards. No immediate training required.',
                                          btnColor: 'bg-emerald-600 hover:bg-emerald-700 text-white',
                                          btnText: 'Approve Profile'
                                       };
                                    }
                                };
                                const rec = recommendationEngine();
                                if (!rec) return null;

                                return (
                                  <div className={`mt-1 rounded-xl p-4 border ${rec.bg} flex flex-col md:flex-row items-start md:items-center gap-4 shadow-sm`}>
                                      {rec.icon}
                                      <div className="flex-1">
                                         <div className="text-[11px] font-black text-slate-800 uppercase tracking-wide mb-1">{rec.title}</div>
                                         <div className="text-xs text-slate-700 font-medium">{rec.action}</div>
                                      </div>
                                      <button className={`${rec.btnColor} text-[10px] font-bold uppercase tracking-wider px-5 py-2.5 rounded-lg shadow-sm transition-colors mt-3 md:mt-0 whitespace-nowrap`}>
                                         {rec.btnText}
                                      </button>
                                  </div>
                                )
                              })()}
                            </div>
                          </motion.div>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-slate-400 font-bold text-sm bg-slate-50 rounded-3xl border border-slate-100 p-6 text-center shadow-inner">
                            <FiTarget className="text-4xl text-slate-300 mb-3" />
                            Select an employee from the donut chart<br/>to drill down into their specific<br/>skill and performance variance.
                          </div>
                        )}

                        {drilldownTab === 'pace' && (
                          <div className="h-full w-full fade-in flex flex-col relative">
                             {/* Quadrant Background Crosshairs */}
                             <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-30 px-[10%] pb-[10%]">
                                <div className="w-full h-px bg-slate-400 absolute"></div>
                                <div className="h-full w-px bg-slate-400 absolute"></div>
                             </div>
                             
                             <div className="absolute top-0 right-[15%] text-[9px] font-black uppercase text-emerald-500 opacity-50">Mastery (Fast & High)</div>
                             <div className="absolute top-0 left-[15%] text-[9px] font-black uppercase text-blue-500 opacity-50">Cautious (Slow & High)</div>
                             <div className="absolute bottom-[15%] right-[15%] text-[9px] font-black uppercase text-red-500 opacity-50">Guessing (Fast & Low)</div>
                             <div className="absolute bottom-[15%] left-[15%] text-[9px] font-black uppercase text-amber-500 opacity-50">Struggling (Slow & Low)</div>

                             <ResponsiveContainer width="100%" height="100%">
                               <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 0 }}>
                                 <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                 <XAxis 
                                    type="number" 
                                    dataKey="time" 
                                    name="Time Taken (mins)" 
                                    tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 700 }}
                                    axisLine={false}
                                    tickLine={false}
                                    reversed={true} // Fast (low time) on the right
                                    domain={['dataMin - 5', 'dataMax + 5']}
                                 />
                                 <YAxis 
                                    type="number" 
                                    dataKey="score" 
                                    name="Score (%)" 
                                    tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 700 }}
                                    axisLine={false}
                                    tickLine={false}
                                    domain={[0, 100]}
                                 />
                                 <Tooltip content={<PaceTooltip />} cursor={{ strokeDasharray: '3 3' }} />
                                 <Scatter data={paceData} shape="circle">
                                    {paceData.map((entry, index) => (
                                       <Cell key={`cell-${index}`} fill={entry.isCurrent ? '#c62828' : '#cbd5e1'} />
                                    ))}
                                 </Scatter>
                               </ScatterChart>
                             </ResponsiveContainer>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Employee Table */}
            <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm bg-white">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase font-black text-slate-500 tracking-wider">
                    <th className="py-4 px-4 text-center w-12">#</th>
                    <th className="py-4 px-4">
                      <div 
                        className="flex items-center gap-1 cursor-pointer w-fit hover:text-slate-800 transition-colors" 
                        onClick={() => handleSort('name')}
                      >
                        Employee Details
                        {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? <FiChevronUp /> : <FiChevronDown />)}
                      </div>
                    </th>
                    <th className="py-4 px-4 text-center">
                      <div 
                        className="flex items-center justify-center gap-1 cursor-pointer w-fit mx-auto hover:text-slate-800 transition-colors" 
                        onClick={() => handleSort('score')}
                      >
                        Score
                        {sortConfig.key === 'score' && (sortConfig.direction === 'asc' ? <FiChevronUp /> : <FiChevronDown />)}
                      </div>
                    </th>
                    <th className="py-4 px-4 text-center">
                      <div 
                        className="flex items-center justify-center gap-1 cursor-pointer w-fit mx-auto hover:text-slate-800 transition-colors" 
                        onClick={() => handleSort('pedagogyScore')}
                      >
                        Pedagogy Score
                        {sortConfig.key === 'pedagogyScore' && (sortConfig.direction === 'asc' ? <FiChevronUp /> : <FiChevronDown />)}
                      </div>
                    </th>
                    <th className="py-4 px-4 text-center">
                      <div 
                        className="flex items-center justify-center gap-1 cursor-pointer w-fit mx-auto hover:text-slate-800 transition-colors" 
                        onClick={() => handleSort('skillScore')}
                      >
                        Skill Score
                        {sortConfig.key === 'skillScore' && (sortConfig.direction === 'asc' ? <FiChevronUp /> : <FiChevronDown />)}
                      </div>
                    </th>
                    <th className="py-4 px-4 text-center">Primary Weakness</th>
                    <th className="py-4 px-4 text-center">Consistency Risk</th>
                    <th className="py-4 px-4 text-center">Status</th>
                    <th className="py-4 px-4 text-center w-20">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentCandidates.length > 0 ? currentCandidates.map((cand, idx) => (
                    <React.Fragment key={cand.user_id || idx}>
                    <tr 
                      onClick={() => {
                        setSelectedChartCandidate(cand);
                        setShowCurrentCharts(true);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={`border-b transition-colors text-xs font-bold text-slate-600 group cursor-pointer ${
                        selectedChartCandidate?.user_id === cand.user_id ? 'bg-red-50/50 border-red-100' : 'border-slate-100 hover:bg-slate-50/50'
                      }`}
                    >
                      <td className="py-4 px-4 text-center text-slate-400">
                        {((currentPage - 1) * itemsPerPage) + idx + 1}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex flex-col">
                          <span className="text-slate-800 font-black">{cand.name}</span>
                          <span className="text-[10px] text-slate-400">{cand.emp_id}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="flex flex-col items-center">
                          <span className="text-slate-800 font-black text-sm">{cand.overall_pct}%</span>
                          <span className="text-[9px] text-slate-400 uppercase">{cand.mcq_score}/{cand.total_marks} Marks</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-lg text-[10px] font-black">{cand.pedagogyScore}%</span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="bg-slate-100 text-slate-600 px-3 py-1 rounded-lg text-[10px] font-black">{cand.skillScore}%</span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        {cand.mindsetWeakness ? (
                          <span className="bg-red-50 text-red-600 border border-red-100 px-2 py-1 rounded text-[9px] font-black uppercase tracking-wider whitespace-nowrap">
                            âš ï¸ {cand.mindsetWeakness}
                          </span>
                        ) : (
                          <span className="text-slate-300 text-[10px] font-bold italic">None</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className={`px-2 py-1 rounded text-[9px] font-black uppercase tracking-wider whitespace-nowrap ${
                          cand.varianceBadge === 'emerald' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' :
                          cand.varianceBadge === 'amber' ? 'bg-amber-50 text-amber-600 border border-amber-100' :
                          cand.varianceBadge === 'red' ? 'bg-red-50 text-red-600 border border-red-100' :
                          'bg-slate-50 text-slate-600 border border-slate-100'
                        }`}>
                          {cand.consistencyProfile}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                          cand.status === 'High' ? 'bg-emerald-50 text-emerald-600' :
                          cand.status === 'Medium' ? 'bg-blue-50 text-blue-600' :
                          'bg-rose-50 text-rose-600'
                        }`}>
                          {cand.status}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewCandidate && onViewCandidate(cand.user_id);
                          }}
                          className="bg-slate-50 hover:bg-red-50 text-slate-400 hover:text-red-600 border border-slate-200 hover:border-red-200 p-2 rounded-lg transition-all shadow-sm mx-auto flex items-center justify-center cursor-pointer group-hover:bg-red-50 group-hover:text-red-600 group-hover:border-red-200"
                          title="View Employee Detail Analytics"
                        >
                          <FiUser />
                        </button>
                      </td>
                    </tr>
                    </React.Fragment>
                  )) : (
                    <tr>
                      <td colSpan="7" className="py-12 text-center text-xs font-bold text-slate-400">
                        No employees found matching the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredCandidates.length)} of {filteredCandidates.length}
                </span>
                <div className="flex items-center gap-1">
                  <button 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    className="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-50 rounded-lg disabled:opacity-30 transition-all cursor-pointer"
                  >
                    <FiChevronLeft />
                  </button>
                  {(() => {
                    let start = Math.max(1, currentPage - 1);
                    let end = Math.min(totalPages, currentPage + 1);
                    if (currentPage === 1) {
                      end = Math.min(totalPages, 3);
                    } else if (currentPage === totalPages) {
                      start = Math.max(1, totalPages - 2);
                    }
                    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
                  })().map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs transition-all cursor-pointer ${
                        currentPage === pageNum
                          ? 'bg-red-600 text-white shadow-sm'
                          : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                      }`}
                    >
                      {pageNum}
                    </button>
                  ))}
                  <button 
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    className="w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-50 rounded-lg disabled:opacity-30 transition-all cursor-pointer"
                  >
                    <FiChevronRight />
                  </button>
                </div>
              </div>
            )}
      </motion.div>
    </motion.div>
  );
}

/* --- Component from RoleTestHeatmap.jsx --- */
function RoleTestHeatmap({ candidatesData, onRoleClick }) {
  const chartRef = useRef(null);

  // 1. Process data for Heatmap and Executive Summary
  const { heatData, roles, tests, insights } = useMemo(() => {
    if (!candidatesData || candidatesData.length === 0) return { heatData: [], roles: [], tests: [], insights: null };

    const map = {};
    let lowestScore = 100;
    let worstRoleTest = null;
    let totalAssessed = 0;
    let highRiskCount = 0;

    candidatesData.forEach(c => {
      const r = c.role || 'Unassigned';
      const t = c.assessment_name || 'Unknown';
      const key = `${r}_${t}`;
      
      if (!map[key]) map[key] = { role: r, test: t, sum: 0, count: 0 };
      
      const pct = c.overall_pct || 0;
      map[key].sum += pct;
      map[key].count += 1;
      totalAssessed += 1;

      if (pct < (c.pass_percentage || 50)) {
        highRiskCount += 1;
      }
    });

    const processedHeatData = Object.values(map).map(item => {
      const score = Math.round(item.sum / item.count);
      if (score < lowestScore) {
        lowestScore = score;
        worstRoleTest = { role: item.role, test: item.test, score };
      }
      return {
        role: item.role,
        test: item.test,
        score: score
      };
    });

    const uniqueRoles = Array.from(new Set(processedHeatData.map(d => d.role)));
    const uniqueTests = Array.from(new Set(processedHeatData.map(d => d.test)));

    return { 
      heatData: processedHeatData, 
      roles: uniqueRoles, 
      tests: uniqueTests,
      insights: {
        worst: worstRoleTest,
        highRiskPct: Math.round((highRiskCount / totalAssessed) * 100) || 0
      }
    };
  }, [candidatesData]);

  // 2. Build AmCharts Heatmap
  useLayoutEffect(() => {
    if (heatData.length === 0) return;

    const root = am5.Root.new("heatmap-container");
    chartRef.current = root;

    // Remove logo
    if (root._logo) root._logo.dispose();

    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(am5xy.XYChart.new(root, {
      panX: false,
      panY: false,
      wheelX: "none",
      wheelY: "none",
      layout: root.verticalLayout,
      paddingLeft: 0,
      paddingRight: 0
    }));

    // X Axis (Tests)
    const xAxis = chart.xAxes.push(am5xy.CategoryAxis.new(root, {
      categoryField: "test",
      renderer: am5xy.AxisRendererX.new(root, {
        minGridDistance: 30,
        opposite: true // Put labels on top
      })
    }));
    xAxis.get("renderer").labels.template.setAll({
      fontSize: 10,
      fontWeight: "bold",
      fill: am5.color(0x64748b),
      paddingBottom: 10
    });
    xAxis.data.setAll(tests.map(t => ({ test: t })));

    // Y Axis (Roles)
    const yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(root, {
      categoryField: "role",
      renderer: am5xy.AxisRendererY.new(root, {
        inversed: true,
        minGridDistance: 20
      })
    }));
    yAxis.get("renderer").labels.template.setAll({
      fontSize: 10,
      fontWeight: "800",
      fill: am5.color(0x475569),
      paddingRight: 15
    });
    yAxis.data.setAll(roles.map(r => ({ role: r })));

    // Series
    const series = chart.series.push(am5xy.ColumnSeries.new(root, {
      calculateAggregates: true,
      stroke: am5.color(0xffffff),
      clustered: false,
      xAxis: xAxis,
      yAxis: yAxis,
      categoryXField: "test",
      categoryYField: "role",
      valueField: "score"
    }));

    series.columns.template.setAll({
      tooltipText: "[bold]{categoryY}[/]\n{categoryX}: {value}% Avg",
      tooltipY: am5.percent(50),
      tooltipX: am5.percent(50),
      strokeOpacity: 1,
      strokeWidth: 3,
      cornerRadiusTL: 8,
      cornerRadiusTR: 8,
      cornerRadiusBL: 8,
      cornerRadiusBR: 8,
      width: am5.percent(95),
      height: am5.percent(95),
      cursorOverStyle: "pointer"
    });

    // Custom hover/click behavior to integrate with Drill-down
    series.columns.template.events.on("click", (ev) => {
      const data = ev.target.dataItem.dataContext;
      if (onRoleClick) onRoleClick(data.role, data.test);
    });

    // Custom tooltip styling
    const tooltip = am5.Tooltip.new(root, {
      autoTextColor: false,
      pointerOrientation: "up"
    });
    tooltip.label.setAll({
      fill: am5.color(0xffffff),
      fontSize: 11,
      fontWeight: "bold"
    });
    tooltip.get("background").setAll({
      fill: am5.color(0x1e293b),
      fillOpacity: 0.9,
      cornerRadius: 8
    });
    series.set("tooltip", tooltip);

    // Heat Rules (Color Mapping: Red to Green)
    series.set("heatRules", [{
      target: series.columns.template,
      min: am5.color(0xef4444), // True Red (Danger)
      max: am5.color(0x10b981), // Emerald (Pass)
      dataField: "value",
      key: "fill",
      customFunction: (sprite, min, max, value) => {
        // We can force hard colors based on thresholds if we want, or let amcharts interpolate
        if (value < 50) sprite.set("fill", am5.color(0xef4444)); // Red
        else if (value < 75) sprite.set("fill", am5.color(0xf59e0b)); // Amber
        else sprite.set("fill", am5.color(0x10b981)); // Green
      }
    }]);

    // Add labels inside the heatmap boxes
    series.bullets.push(() => {
      return am5.Bullet.new(root, {
        sprite: am5.Label.new(root, {
          text: "{value}%",
          fill: am5.color(0xffffff),
          centerY: am5.percent(50),
          centerX: am5.percent(50),
          populateText: true,
          fontSize: 12,
          fontWeight: "black"
        })
      });
    });

    series.data.setAll(heatData);

    // Appear animation
    chart.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, [heatData, roles, tests, onRoleClick]);

  if (heatData.length === 0) return null;

  return (
    <div className="flex flex-col gap-6 mb-8 w-full select-none">
      
      {/* 1. Executive AI Summary Banner (ZERO CLICKS) */}
      {insights && (
        <div className="flex flex-col lg:flex-row gap-4 w-full">
          {insights.worst && (
            <div className="flex-1 bg-red-50 border border-red-100 rounded-2xl p-4 flex items-center gap-4 shadow-sm">
              <div className="w-12 h-12 bg-white rounded-xl shadow-sm text-red-600 flex items-center justify-center shrink-0">
                <FiTrendingDown className="text-xl" />
              </div>
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest text-red-500 mb-0.5">Critical Action Required</div>
                <div className="text-sm font-bold text-slate-800">
                  The <span className="text-red-600 font-black">"{insights.worst.role}"</span> role is critically lagging in the <span className="font-black">"{insights.worst.test}"</span> (Avg: {insights.worst.score}%).
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 bg-amber-50 border border-amber-100 rounded-2xl p-4 flex items-center gap-4 shadow-sm">
            <div className="w-12 h-12 bg-white rounded-xl shadow-sm text-amber-500 flex items-center justify-center shrink-0">
              <FiAlertCircle className="text-xl" />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-amber-500 mb-0.5">Company Readiness</div>
              <div className="text-sm font-bold text-slate-800">
                <span className="text-amber-600 font-black">{insights.highRiskPct}%</span> of all assessed candidates are currently falling below passing standards.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 2. Macro Heatmap */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-black text-slate-700 uppercase tracking-widest flex items-center gap-2">
              <FiTarget className="text-red-600" /> Executive Macro View: Role vs Test
            </h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">Zero-Click Diagnostics. Click any box to drill down.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-red-500"></div><span className="text-[9px] font-bold text-slate-500 uppercase">Critical (&lt;50%)</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-amber-500"></div><span className="text-[9px] font-bold text-slate-500 uppercase">Warning (50-74%)</span></div>
            <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded bg-emerald-500"></div><span className="text-[9px] font-bold text-slate-500 uppercase">Optimal (&gt;75%)</span></div>
          </div>
        </div>
        
        {/* Heatmap Container */}
        <div className="w-full relative" style={{ height: Math.max(300, roles.length * 60) }}>
          <div id="heatmap-container" style={{ width: "100%", height: "100%" }}></div>
        </div>
      </div>
    </div>
  );
}

/* --- Component from GroupsInlineSearchField.jsx --- */
/**
 * Groups panel â€” flex row: [icon] [input flex-1] [count slot] [arrow]
 */
const GroupsInlineSearchField = ({
  value,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  disabled = false,
  dropdownOpen = false,
  onToggleDropdown,
  selectionCount = 0,
  selectionSummary = null,
  toggleAriaLabel = 'Toggle list',
}) => {
  const [focused, setFocused] = useState(false);
  const hasCount = selectionCount > 0;
  const showSummary = Boolean(selectionSummary) && !String(value || '').trim() && !focused;
  const showCount = hasCount && !showSummary;

  return (
    <div className="assess-mod-groups-inline-search">
      <div
        className={`assess-mod-groups-inline-search-field${
          focused ? ' is-focused' : ''
        }${disabled ? ' is-disabled' : ''}${showCount ? ' has-count' : ''}`}
      >
        <span className="assess-mod-groups-inline-search-icon-wrap" aria-hidden>
          <i className="bi bi-search assess-mod-groups-inline-search-icon" />
        </span>

        <div className="assess-mod-groups-inline-search-input-wrap">
          {showSummary && (
            <div className="assess-mod-groups-inline-search-summary" aria-hidden>
              {selectionSummary}
            </div>
          )}
          <input
            type="text"
            disabled={disabled}
            className={`assess-mod-groups-inline-search-input${
              showSummary ? ' assess-mod-groups-inline-search-input--summary' : ''
            }`}
            placeholder={showSummary ? '' : placeholder}
            value={value}
            onFocus={(e) => {
              setFocused(true);
              onFocus?.(e);
            }}
            onBlur={(e) => {
              setFocused(false);
              onBlur?.(e);
            }}
            onChange={onChange}
          />
        </div>

        {showCount && (
          <div
            className="assess-mod-groups-inline-search-count-slot"
            title={`${selectionCount} selected`}
            aria-label={`${selectionCount} selected`}
          >
            <span className="assess-mod-groups-inline-search-count assess-mod-groups-inline-search-count--full">
              {selectionCount} selected
            </span>
            <span className="assess-mod-groups-inline-search-count assess-mod-groups-inline-search-count--short" aria-hidden>
              {selectionCount} sel.
            </span>
          </div>
        )}

        <div className="assess-mod-groups-inline-search-arrow-slot">
          <button
            type="button"
            disabled={disabled}
            className="assess-mod-groups-inline-search-toggle"
            aria-label={toggleAriaLabel}
            onClick={(e) => {
              e.stopPropagation();
              onToggleDropdown?.();
            }}
          >
            <FiChevronDown
              className={`assess-mod-groups-inline-search-chevron${
                dropdownOpen ? ' is-open' : ''
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

/* --- Component from AssessmentPopup.jsx --- */
/**
 * Premium Assessment Popup Component
 * Replaces SweetAlert2 with a unified design system.
 */
const AssessmentPopup = ({ 
  isOpen, 
  title, 
  message, 
  onConfirm, 
  onCancel, 
  confirmText = "Confirm", 
  cancelText = "Cancel",
  type = "warning", // warning, success, info
  showCancel = true,
  showInput = false,
  inputValue = "",
  onInputChange = () => {},
  inputPlaceholder = "Enter text...",
  children
}) => {
  if (!isOpen) return null;

  return (
    <div className="assessment-popup-overlay" onClick={onCancel}>
      <div className="assessment-popup-container" onClick={e => e.stopPropagation()}>
        <div className={`assessment-popup-icon ${type}`}>
          {type === 'warning' && <FiAlertCircle />}
          {type === 'success' && <FiCheckCircle />}
          {type === 'info' && <FiInfo />}
        </div>
        <h3 className="assessment-popup-title">{title}</h3>
        <p className="assessment-popup-message">{message}</p>

        {showInput && (
          <input 
            type="text"
            className="w-full bg-[#f8fafc] border border-slate-100 rounded-2xl px-5 py-3 text-[12px] font-black text-[#1e293b] focus:ring-4 focus:ring-red-50 outline-none transition-all placeholder:text-slate-300 shadow-inner mb-8"
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            placeholder={inputPlaceholder}
            autoFocus
          />
        )}

        {children}

        <div className="assessment-popup-actions">
          {showCancel && (
            <button 
              className="btn-assessment btn-assessment-secondary"
              onClick={onCancel}
            >
              {cancelText}
            </button>
          )}
          <button 
            className={`btn-assessment ${type === 'warning' ? 'btn-assessment-danger' : 'btn-assessment-primary'}`}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;




